import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getActiveTenantOverride } from '../contexts/TenantSwitchContext';

// Azure AD (MSAL) token getter — set from main.tsx after MsalProvider mounts.
// Returns a JWT access token from MSAL; axios attaches it as
// `Authorization: Bearer <jwt>` on every request.
let getTokenFn: ((opts?: { forceRefresh?: boolean }) => Promise<string | null>) | null = null;

export function setTokenGetter(fn: (opts?: { forceRefresh?: boolean }) => Promise<string | null>): void {
  getTokenFn = fn;
}

// In production, VITE_API_URL points to deployed backend (e.g. Railway).
// In development, Vite proxy forwards /api → localhost:3001
const configuredApiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '');
const API_BASE = configuredApiUrl
  ? configuredApiUrl.endsWith('/api/v1')
    ? configuredApiUrl
    : `${configuredApiUrl}/api/v1`
  : '/api/v1';

// For public token pages that call with bare axios/fetch (no auth header).
// Same resolution as API_BASE — exported style kept separate for clarity.
export const PUBLIC_API_BASE = API_BASE;

/**
 * The backend's ORIGIN, with no path.
 *
 * Almost everything talks to /api/v1, but a few backend routes are mounted at
 * the root. The one that matters is the embeddable job widget at /embed/jobs,
 * because its URL is copied out of the app and pasted into a customer's
 * website.
 *
 * That snippet used to be built by appending to VITE_API_URL, which already
 * ends in /api/v1 — producing ".../api/v1/embed/jobs", a 404. Pasted into Wix
 * or WordPress it rendered as an empty grey box, and because the request never
 * matched a route, nothing showed up in our logs to explain why.
 *
 * Derived from API_BASE so it is right whether VITE_API_URL was configured with
 * the /api/v1 suffix or without it, and falls back to the current origin in dev
 * where API_BASE is the relative "/api/v1".
 */
export const API_ORIGIN: string = (() => {
  try {
    return new URL(API_BASE, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
})();

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// AI / long-running endpoints need more than the 30s default — Claude
// generation (exam questions, candidate matching, drafts, parsing, etc.) can
// take a minute or more. Match these by URL and give them 3 minutes so the
// browser doesn't abort with "Request timed out" before the server responds.
const LONG_OP_PATTERN = /(\/ai[-/]|\/matching\/|parse-resume|resume\/review|job-suggestions|suggest-schedule|huddle\/extract|eod\/draft|auto-generate|ai-generate|ai-draft|ai-rewrite|ai-parse|ai-next-question)/i;
const LONG_OP_TIMEOUT = 180_000;

// Request interceptor — attach Azure AD access token
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Bump the timeout for AI/long-running calls (never lowers an explicit one).
  if (config.url && LONG_OP_PATTERN.test(config.url)) {
    config.timeout = Math.max(config.timeout ?? 0, LONG_OP_TIMEOUT);
  }
  if (getTokenFn) {
    try {
      const token = await getTokenFn();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Token fetch failed — proceed without auth header
    }
  }
  // Platform-operator tenant impersonation (platform/tenant control-plane split, Wave 2).
  // If an operator has "entered" a tenant, forward its uuid so the backend scopes
  // the request into that tenant. When no switch is active this is null and NO
  // header is sent → requests stay byte-identical to today for every FNS user.
  const tenantOverride = getActiveTenantOverride();
  if (tenantOverride) {
    config.headers['X-Tenant-Id'] = tenantOverride;
  }
  return config;
});

// Response interceptor — handle auth errors
// Global 429 cooldown. When the backend's auth-limiter snaps shut, we'd
// otherwise see a wall of "Too many auth requests" toasts as every
// in-flight component fires its own request and each gets rejected. By
// recording the cooldown window and short-circuiting subsequent
// requests during it, we collapse the cascade to a single error the
// user can see and act on.
let cooldownUntil = 0;
const CooldownError = Object.assign(new Error('Too many auth requests — backing off briefly.'), {
  isRateLimitCooldown: true,
});

api.interceptors.request.use((config) => {
  if (Date.now() < cooldownUntil) {
    // Reject without making the network call. Each caller's .catch
    // sees the same shaped error, and ToastHost dedups identical
    // messages so at most one toast is visible.
    return Promise.reject(CooldownError);
  }
  return config;
});

// Track recent 401s. A single 401 might be a transient race (token
// mid-refresh, clock skew, a stale request from a closed tab). Two 401s
// inside a short window almost certainly means the session is genuinely
// gone — only then do we ask the user to re-auth.
let lastUnauthorizedAt = 0;
let sessionExpiredBannerShown = false;

function showSessionExpiredBanner(): void {
  // Never show this on public, token-authed pages (signing, candidate
  // portal/upload, careers, etc.). Those visitors are candidates/clients with
  // no Microsoft session to expire — a stray background 401 must not nag them
  // with a "sign in" banner on a page they're meant to use without logging in.
  if (/^\/(sign|upload|portal|docs|verify-cert|rate|careers|client-view)(\/|$)/.test(window.location.pathname)) return;
  if (sessionExpiredBannerShown) return;
  sessionExpiredBannerShown = true;
  // Mount a vanilla banner so it survives React unmount cycles. CSS-only,
  // sits above app content. Click reloads the page, which triggers the
  // MSAL flow cleanly instead of dumping the user on a half-broken
  // /sign-in route.
  const div = document.createElement('div');
  div.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:99999;background:#fef3c7;border-bottom:1px solid #fde68a;color:#92400e;padding:12px 20px;font-family:system-ui,sans-serif;font-size:14px;display:flex;align-items:center;justify-content:space-between;gap:12px';
  div.innerHTML = `
    <div><strong>Your session may have expired.</strong> Reload the page to sign in again.</div>
    <button id="boaz-reauth-btn" style="background:#92400e;color:#fff;border:0;border-radius:6px;padding:6px 14px;font-weight:600;cursor:pointer">Sign in again</button>
  `;
  document.body.appendChild(div);
  const btn = document.getElementById('boaz-reauth-btn');
  // Clear MSAL's cache before bouncing to /sign-in. Navigating alone used to
  // land the user back on a cached account whose refresh token was already
  // gone — the exact state that produced the banner — so the sign-in screen
  // retried the same dead token and hung on "Checking your sign-in…".
  // clearCache() is local only: Microsoft's own SSO session survives, so the
  // sign-in that follows is normally silent rather than a fresh password.
  btn?.addEventListener('click', () => {
    void (async () => {
      try {
        const { msalInstance } = await import('./msal');
        await msalInstance.clearCache();
      } catch { /* clearing is best-effort — still send them to sign-in */ }
      window.location.replace('/sign-in');
    })();
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // A 401 usually just means the cached idToken aged out. Before
      // bothering the user, force MSAL to mint a fresh token and retry the
      // request ONCE. Most "session expired" banners were really just a
      // stale token that a silent refresh fixes instantly.
      const cfg = error.config as (InternalAxiosRequestConfig & { _retried401?: boolean }) | undefined;
      if (cfg && !cfg._retried401 && getTokenFn) {
        cfg._retried401 = true;
        try {
          const fresh = await getTokenFn({ forceRefresh: true });
          if (fresh) {
            cfg.headers = cfg.headers ?? {};
            (cfg.headers as Record<string, string>).Authorization = `Bearer ${fresh}`;
            return api(cfg);
          }
        } catch { /* fall through to the transient/banner logic */ }
      }
      // Refresh didn't help → genuine expiry. Treat one 401 as transient;
      // only show the banner on a second 401 in quick succession.
      const now = Date.now();
      if (now - lastUnauthorizedAt < 10_000) {
        showSessionExpiredBanner();
      }
      lastUnauthorizedAt = now;
    }
    if (error.response?.status === 429) {
      // Lock out further requests for 30s. The backend window is 60s
      // but we don't need to wait the whole thing — half the window
      // is enough for the limiter to start refilling.
      cooldownUntil = Date.now() + 30_000;
    }
    return Promise.reject(error);
  }
);

// ─── Staff ───────────────────────────────────────────────────────────────────
export interface Staff {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  role?: 'RN' | 'LPN' | 'LVN' | 'CNA' | 'RT' | 'NP' | 'PA' | 'Other';
  specialty?: string;
  status: 'active' | 'available' | 'onboarding' | 'inactive' | 'terminated';
  facility_id?: string;
  facility_name?: string;
  notes?: string;
  // Employment contract. contract_type 'indefinite' = "until they get fired or
  // leave early" (no end date); 'fixed_term' = ends on contract_end_date.
  employment_start_date?: string | null;
  contract_type?: 'fixed_term' | 'indefinite' | null;
  contract_end_date?: string | null;
  created_at: string;
  updated_at: string;
}

export const staffApi = {
  list: (params?: { status?: string; role?: string; search?: string }) =>
    api.get<{ staff: Staff[]; total: number }>('/staff', { params }),
  get: (id: string) => api.get<Staff & { credentials: Credential[]; placements: Placement[] }>(`/staff/${id}`),
  create: (data: Partial<Staff>) => api.post<Staff>('/staff', data),
  update: (id: string, data: Partial<Staff>) => api.put<Staff>(`/staff/${id}`, data),
  delete: (id: string) => api.delete(`/staff/${id}`),
};

// ─── Placements ───────────────────────────────────────────────────────────────
export interface Placement {
  id: string;
  staff_id?: string;
  facility_id: string;
  role: string;
  start_date?: string;
  end_date?: string;
  status: 'active' | 'pending' | 'unfilled' | 'completed' | 'cancelled';
  contract_status: 'not_sent' | 'pending_esign' | 'signed' | 'expired';
  // Holds the built-in eSign document UUID for placements sent via /esign.
  // Column name is historical (the system used to integrate with Foxit)
  // and kept for backwards compatibility with existing rows.
  foxit_envelope_id?: string;
  hourly_rate?: number;
  notes?: string;
  // [track-A rates] financials — what the client is billed, what staff are
  // paid, contracted hours, W-2/1099 classification, and the client link.
  bill_rate?: number | null;
  pay_rate?: number | null;
  hours_per_week?: number | null;
  employment_type?: 'w2' | '1099' | null;
  client_id?: string | null;
  first_name?: string;
  last_name?: string;
  facility_name?: string;
  created_at: string;
  updated_at: string;
}

export const placementsApi = {
  list: (params?: { status?: string; facility_id?: string; staff_id?: string }) =>
    api.get<{ placements: Placement[] }>('/placements', { params }),
  get: (id: string) => api.get<Placement>(`/placements/${id}`),
  create: (data: Partial<Placement>) => api.post<Placement>('/placements', data),
  update: (id: string, data: Partial<Placement>) => api.put<Placement>(`/placements/${id}`, data),
  sendContract: (id: string) => api.post<{
    success: boolean;
    esign_document_id: string;
    prepare_url: string;
    smsSent: boolean;
  }>(`/placements/${id}/send-contract`),
  approve: (id: string) => api.post(`/placements/${id}/approve`),
  shiftFit: (id: string) =>
    api.post<{
      placement: { id: string; role: string; facility_name: string | null; start_date: string | null; end_date: string | null };
      total_eligible: number;
      ranked: Array<{
        staff_id: string; full_name: string;
        role: string | null; specialty: string | null; status: string;
        phone: string | null; email: string | null;
        score: number; rationale: string;
      }>;
    }>(`/placements/${id}/shift-fit-ranking`),
};

// ─── Credentials ─────────────────────────────────────────────────────────────
export interface Credential {
  id: string;
  staff_id: string;
  type: string;
  issuer?: string;
  issue_date?: string;
  expiry_date?: string;
  status: 'valid' | 'expiring' | 'expiring_soon' | 'expired' | 'pending' | 'missing';
  document_url?: string;
  first_name?: string;
  last_name?: string;
  created_at: string;
}

export const credentialsApi = {
  list: (params?: { staffId?: string; status?: string }) =>
    api.get<{ credentials: Credential[] }>('/credentials', { params }),
  expiring: () => api.get<{ expiringSoon: Credential[]; alreadyExpired: Credential[] }>('/credentials/expiring'),
  add: (data: Partial<Credential>) => api.post<Credential>('/credentials', data),
  update: (id: string, data: Partial<Credential>) => api.put<Credential>(`/credentials/${id}`, data),
};

// ─── Facilities ───────────────────────────────────────────────────────────────
export interface Facility {
  id: string;
  name: string;
  type?: string;
  address?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  contract_status: 'active' | 'renewing' | 'expired' | 'pending';
  requires_jail_application?: boolean;
  special_requirements?: Record<string, unknown>;
  notes?: string;
  active_placements?: number;
  created_at: string;
}

export const facilitiesApi = {
  list: (params?: { contract_status?: string; search?: string }) =>
    api.get<{ facilities: Facility[] }>('/clients', { params }),
  get: (id: string) => api.get<Facility>(`/clients/${id}`),
  create: (data: Partial<Facility>) => api.post<Facility>('/clients', data),
  update: (id: string, data: Partial<Facility>) => api.put<Facility>(`/clients/${id}`, data),
  delete: (id: string) => api.delete(`/clients/${id}`),
  duplicate: (id: string, body?: { name?: string }) =>
    api.post<{ facility: Facility }>(`/clients/${id}/duplicate`, body ?? {}),
};

// ─── AI ───────────────────────────────────────────────────────────────────────
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiApi = {
  chat: (messages: ChatMessage[], userContext?: string) =>
    api.post<{ response: string; model: string }>('/ai/chat', { messages, userContext }),
  // Phase 5.3d — multipart chat with file attachment. Sends the file +
  // messages (JSON-stringified) + optional userContext to the backend,
  // which extracts text or passes vision content to Claude.
  chatWithFile: (messages: ChatMessage[], file: File, userContext?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('messages', JSON.stringify(messages));
    if (userContext) fd.append('userContext', userContext);
    return api.post<{ response: string; attached: { filename: string; mime: string; size: number } }>(
      '/ai/chat-with-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  // Phase 5.3c — search for an entity referenced by name in an AI response
  // (so a [[link:candidate:Noah]] click can show disambiguation if there
  // are multiple Noahs).
  resolveEntity: (type: 'candidate' | 'staff' | 'job' | 'facility' | 'policy', q: string) =>
    api.get<{ type: string; matches: Array<{ id: string; [k: string]: unknown }> }>(
      '/ai/resolve-entity', { params: { type, q } }
    ),
  // Phase 6.6 — context-aware action suggestions for a workflow page.
  // Pass a short subject + structured context; response is a bulleted
  // list using the same [[link:...]] / [[action:...]] tag grammar that
  // the TaggedText component renders.
  suggestActions: (data: { subject: string; context: Record<string, unknown> }) =>
    api.post<{ suggestions: string }>('/ai/suggest-actions', data),
  analyzeDocument: (documentText: string, documentType: string, staffId?: string) =>
    api.post('/ai/analyze-document', { documentText, documentType, staffId }),
  categorizeEmail: (subject: string, body: string, from: string) =>
    api.post('/ai/categorize-email', { subject, body, from }),
};

// ─── Emails ───────────────────────────────────────────────────────────────────
export interface EmailLog {
  id: string;
  outlook_message_id?: string;
  from_address?: string;
  from_name?: string;
  subject?: string;
  received_at?: string;
  ai_category?: 'urgent' | 'important' | 'low' | 'spam';
  ai_summary?: string;
  action_required: boolean;
  actioned: boolean;
  created_at: string;
}

export const emailsApi = {
  list: (params?: { category?: string; actioned?: boolean }) =>
    api.get<{ emails: EmailLog[]; total: number }>('/emails', { params }),
  // Scan can take a while: it fans out an AI categorization call per
  // new email. The default 30s axios timeout was tripping for batches
  // of 25 even after backend parallelization, so we give this call its
  // own ceiling.
  scan: (userId?: string, top?: number) =>
    api.post('/emails/scan', { userId, top }, { timeout: 120000 }),
  action: (id: string) => api.post(`/emails/${id}/action`),
  stats: () => api.get('/emails/stats'),
};

// ─── SMS Approvals ────────────────────────────────────────────────────────────
export interface SMSApproval {
  id: string;
  type: string;
  subject: string;
  message: string;
  recipient_phone: string;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'escalated';
  reference_id?: string;
  reference_type?: string;
  approved_at?: string;
  approved_by?: string;
  created_at: string;
}

export interface AutomationApprovalItem {
  id: string;
  automation_key: string;
  automation_label: string;
  template_key: string | null;
  template_label: string;
  candidate_id: string | null;
  summary: string | null;
  status: 'pending' | 'approved' | 'sent' | 'rejected' | 'failed';
  reason: string | null;
  created_at: string;
  decided_by: string | null;
  decided_at: string | null;
  to: string | null;
  subject: string | null;
  body: string | null;
}

export interface TemplateApprovalItem {
  key: string;
  automation_key: string;
  label: string;
  kind: 'builtin' | 'sequence_step' | 'message_template';
  subject: string;
  body: string;
  approved: boolean;
  approved_by: string | null;
  approved_at: string | null;
  cleared_at: string | null;
}

export const automationApprovalsApi = {
  list: (params?: { status?: string }) =>
    api.get<{ items: AutomationApprovalItem[] }>('/automation-approvals', { params }),
  approve: (id: string) => api.post(`/automation-approvals/${id}/approve`),
  reject: (id: string, reason?: string) => api.post(`/automation-approvals/${id}/reject`, { reason }),
  edit: (id: string, data: { subject: string; body: string }) =>
    api.patch(`/automation-approvals/${id}`, data),
  templates: () => api.get<{ templates: TemplateApprovalItem[] }>('/automation-approvals/templates'),
  approveTemplate: (templateKey: string) =>
    api.post(`/automation-approvals/templates/approve`, { templateKey }),
};

export const smsApi = {
  list: (params?: { status?: string }) =>
    api.get<{ approvals: SMSApproval[] }>('/sms', { params }),
  send: (data: {
    type: string;
    subject: string;
    message: string;
    recipient_phone: string;
    reference_id?: string;
    reference_type?: string;
    details?: string;
  }) => api.post<SMSApproval>('/sms', data),
  approve: (id: string) => api.post(`/sms/${id}/approve`),
  deny: (id: string) => api.post(`/sms/${id}/deny`),
};

// ─── Incidents ────────────────────────────────────────────────────────────────
export interface Incident {
  id: string;
  staff_id?: string;
  facility_id?: string;
  type: string;
  description: string;
  date: string;
  status: 'open' | 'under_review' | 'resolved' | 'closed';
  workers_comp_claim: boolean;
  first_name?: string;
  last_name?: string;
  facility_name?: string;
  created_at: string;
  updated_at: string;
}

export const incidentsApi = {
  list: (params?: { status?: string; staff_id?: string; facility_id?: string }) =>
    api.get<{ incidents: Incident[] }>('/incidents', { params }),
  get: (id: string) => api.get<Incident>(`/incidents/${id}`),
  create: (data: Partial<Incident>) => api.post<Incident>('/incidents', data),
  update: (id: string, data: Partial<Incident>) => api.put<Incident>(`/incidents/${id}`, data),
  close: (id: string) => api.delete(`/incidents/${id}`),
  // Phase 4.1 — AI-assisted incident report creation.
  // aiNextQuestion: given context + answers so far, return the next question (or { done }).
  // aiDraft: given the full answer set, return a narrative for the description textarea.
  aiNextQuestion: (data: {
    type: string;
    staff_name?: string | null;
    facility_name?: string | null;
    answers: { question: string; answer: string }[];
  }) => api.post<{ done: boolean; question?: string }>('/incidents/ai-next-question', data),
  aiDraft: (data: {
    type: string;
    staff_name?: string | null;
    facility_name?: string | null;
    date?: string | null;
    answers: { question: string; answer: string }[];
  }) => api.post<{ description: string }>('/incidents/ai-draft', data),
};

// ─── Phase 4 — Business Development ───────────────────────────────────────
// Bids + Leads + Contacts + Follow-ups. Backend mounted at /api/v1/bd/*.
export interface BDBid {
  id: string;
  title: string;
  client_name?: string | null;
  facility_id?: string | null;
  facility_name?: string | null;
  status: 'draft' | 'in_progress' | 'submitted' | 'won' | 'lost';
  due_date?: string | null;
  estimated_value?: number | null;
  assigned_to?: string | null;
  notes?: string | null;
  checklist_total?: number;
  checklist_completed?: number;
  created_at: string;
  updated_at: string;
}
export interface BDBidChecklistItem {
  id: string;
  bid_id: string;
  label: string;
  required: boolean;
  completed: boolean;
  completed_at?: string | null;
  completed_by?: string | null;
  order_index: number;
}
export interface BDBidStats {
  open_count: number;
  open_value: number;
  won_count: number;
  lost_count: number;
  win_rate: number | null;
  due_this_week: number;
}
export interface BDLead {
  id: string;
  company: string;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  status: 'prospect' | 'qualified' | 'proposal' | 'negotiating' | 'closed' | 'lost';
  source: 'cold_call' | 'referral' | 'website' | 'linkedin' | 'event';
  last_contact?: string | null;
  next_follow_up?: string | null;
  notes?: string | null;
}
export interface BDContact {
  id: string;
  name: string;
  title?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  last_contact?: string | null;
  notes?: string | null;
}
export interface BDFollowup {
  id: string;
  company_contact: string;
  follow_up_date: string;
  type: 'call' | 'email' | 'meeting';
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'done';
  notes?: string | null;
}

export const bdApi = {
  // Bids
  listBids: (params?: { status?: string; assigned_to?: string }) =>
    api.get<{ bids: BDBid[] }>('/bd/bids', { params }),
  getBid: (id: string) =>
    api.get<{ bid: BDBid; checklist: BDBidChecklistItem[] }>(`/bd/bids/${id}`),
  createBid: (data: Partial<BDBid> & { checklist?: { label: string; required?: boolean }[] }) =>
    api.post<{ bid: BDBid; checklist: BDBidChecklistItem[] }>('/bd/bids', data),
  updateBid: (id: string, data: Partial<BDBid>) =>
    api.put<BDBid>(`/bd/bids/${id}`, data),
  deleteBid: (id: string) => api.delete(`/bd/bids/${id}`),
  addChecklistItem: (bidId: string, data: { label: string; required?: boolean }) =>
    api.post<BDBidChecklistItem>(`/bd/bids/${bidId}/checklist`, data),
  updateChecklistItem: (bidId: string, itemId: string, data: Partial<BDBidChecklistItem>) =>
    api.put<BDBidChecklistItem>(`/bd/bids/${bidId}/checklist/${itemId}`, data),
  deleteChecklistItem: (bidId: string, itemId: string) =>
    api.delete(`/bd/bids/${bidId}/checklist/${itemId}`),
  bidStats: () => api.get<BDBidStats>('/bd/bids-stats'),
  aiDraftBid: (data: { context: string; client_name?: string | null }) =>
    api.post<{ title: string; notes: string; checklist: { label: string; required: boolean }[] }>('/bd/bids/ai-draft', data),

  // Leads
  listLeads: () => api.get<{ leads: BDLead[] }>('/bd/leads'),
  createLead: (data: Partial<BDLead>) => api.post<BDLead>('/bd/leads', data),
  updateLead: (id: string, data: Partial<BDLead>) => api.put<BDLead>(`/bd/leads/${id}`, data),
  deleteLead: (id: string) => api.delete(`/bd/leads/${id}`),

  // Contacts
  listContacts: () => api.get<{ contacts: BDContact[] }>('/bd/contacts'),
  createContact: (data: Partial<BDContact>) => api.post<BDContact>('/bd/contacts', data),
  updateContact: (id: string, data: Partial<BDContact>) => api.put<BDContact>(`/bd/contacts/${id}`, data),
  deleteContact: (id: string) => api.delete(`/bd/contacts/${id}`),

  // Follow-ups
  listFollowups: () => api.get<{ followups: BDFollowup[] }>('/bd/followups'),
  createFollowup: (data: Partial<BDFollowup>) => api.post<BDFollowup>('/bd/followups', data),
  updateFollowup: (id: string, data: Partial<BDFollowup>) => api.put<BDFollowup>(`/bd/followups/${id}`, data),
  deleteFollowup: (id: string) => api.delete(`/bd/followups/${id}`),

  // Phase 4.4 — Contracts + versioning
  listContracts: (params?: { status?: string; facility_id?: string }) =>
    api.get<{ contracts: BDContract[] }>('/bd/contracts', { params }),
  getContract: (id: string) =>
    api.get<{ contract: BDContract; versions: BDContractVersion[] }>(`/bd/contracts/${id}`),
  createContract: (data: Partial<BDContract>) => api.post<BDContract>('/bd/contracts', data),
  updateContract: (id: string, data: Partial<BDContract>) => api.put<BDContract>(`/bd/contracts/${id}`, data),
  deleteContract: (id: string) => api.delete(`/bd/contracts/${id}`),
  uploadContractVersion: (id: string, file: File, changesSummary?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (changesSummary) fd.append('changes_summary', changesSummary);
    return api.post<BDContractVersion>(`/bd/contracts/${id}/versions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  contractsAlerts: () => api.get<{ alerts: BDContractAlert[] }>('/bd/contracts-alerts'),

  // Phase 4.4 — RFPs
  listRfps: (params?: { status?: string }) =>
    api.get<{ rfps: BDRfp[] }>('/bd/rfps', { params }),
  getRfp: (id: string) => api.get<BDRfp>(`/bd/rfps/${id}`),
  uploadRfp: (file: File, title?: string, clientName?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    if (clientName) fd.append('client_name', clientName);
    return api.post<BDRfp>('/bd/rfps', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  updateRfp: (id: string, data: Partial<BDRfp>) => api.put<BDRfp>(`/bd/rfps/${id}`, data),
  deleteRfp: (id: string) => api.delete(`/bd/rfps/${id}`),
  draftBidFromRfp: (id: string) =>
    api.post<{ bid: BDBid; rfp_id: string }>(`/bd/rfps/${id}/draft-bid`),
  analyzeRfpGap: (id: string) =>
    api.post<{ gap_analysis: BDRfpGapAnalysis }>(`/bd/rfps/${id}/gap-analysis`),

  // Phase 4.4 — Revenue forecast
  forecast: () => api.get<BDForecast>('/bd/forecast'),
};

// ─── Phase 4.4 — additional BD types (contracts, RFPs, forecast) ──────
export interface BDContract {
  id: string;
  title: string;
  client_name?: string | null;
  facility_id?: string | null;
  facility_name?: string | null;
  bid_id?: string | null;
  current_version: number;
  effective_date?: string | null;
  expiration_date?: string | null;
  total_value?: number | null;
  status: 'draft' | 'active' | 'expired' | 'terminated';
  terms_summary?: string | null;
  notes?: string | null;
  version_count?: number;
  expiring_soon?: boolean;
  created_at: string;
  updated_at: string;
}
export interface BDContractVersion {
  id: string;
  contract_id: string;
  version: number;
  file_path?: string | null;
  file_name?: string | null;
  changes_summary?: string | null;
  uploaded_by?: string | null;
  created_at: string;
}
export interface BDContractAlert {
  id: string;
  title: string;
  client_name: string | null;
  expiration_date: string | null;
  status: string;
  alert_level: 'expired' | 'expiring_soon' | 'ok';
}
export interface BDRfp {
  id: string;
  title?: string | null;
  client_name?: string | null;
  file_path?: string | null;
  file_name?: string | null;
  parsed_text?: string | null;
  parsed_summary?: string | null;
  due_date?: string | null;
  bid_id?: string | null;
  status: 'new' | 'reviewed' | 'drafted' | 'declined' | 'expired';
  received_at: string;
  notes?: string | null;
  gap_analysis?: BDRfpGapAnalysis | null;
  gap_analyzed_at?: string | null;
}
// Phase 3 (AI layer) — RFP-vs-capability-statement gap analysis
export interface BDRfpGapAnalysis {
  requirements: Array<{ requirement: string; status: 'met' | 'partial' | 'gap' | 'unknown'; evidence: string }>;
  summary: string;
  win_themes: string[];
  risks: string[];
  capability_file?: string;
}
export interface BDForecastMonth {
  month: string;
  weighted_value: number;
  gross_value: number;
  bid_count: number;
}
export interface BDForecastBid {
  id: string;
  title: string;
  status: 'draft' | 'in_progress' | 'submitted';
  due_date: string | null;
  gross: number;
  weighted: number;
  probability: number;
}
export interface BDForecast {
  baseline_win_rate: number;
  history: { won: number; lost: number; decided_total: number };
  probabilities: { draft: number; in_progress: number; submitted: number };
  total_gross_open: number;
  total_weighted_projection: number;
  by_month: BDForecastMonth[];
  by_bid: BDForecastBid[];
}

// ─── Phase 4.4 — Workforce Scheduling + PTO ─────────────────────────────
export interface WorkShift {
  id: string;
  staff_id: string;
  first_name?: string;
  last_name?: string;
  staff_role?: string;
  facility_id?: string | null;
  facility_name?: string | null;
  role?: string | null;
  start_time: string;
  end_time: string;
  hourly_rate?: number | null;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string | null;
  created_at: string;
  updated_at: string;
}
export interface ShiftCoverageDay {
  day: string;
  total: number;
  confirmed: number;
  no_show: number;
}

export const schedulingApi = {
  listShifts: (params?: { staff_id?: string; facility_id?: string; status?: string; from?: string; to?: string }) =>
    api.get<{ shifts: WorkShift[] }>('/scheduling/shifts', { params }),
  getShift: (id: string) => api.get<WorkShift>(`/scheduling/shifts/${id}`),
  createShift: (data: Partial<WorkShift>) => api.post<WorkShift>('/scheduling/shifts', data),
  updateShift: (id: string, data: Partial<WorkShift>) => api.put<WorkShift>(`/scheduling/shifts/${id}`, data),
  deleteShift: (id: string) => api.delete(`/scheduling/shifts/${id}`),
  coverage: (params: { from: string; to: string; facility_id?: string }) =>
    api.get<{ coverage: ShiftCoverageDay[] }>('/scheduling/coverage', { params }),
};

export interface PtoRequest {
  id: string;
  staff_id: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  type: 'vacation' | 'sick' | 'personal' | 'unpaid';
  start_date: string;
  end_date: string;
  hours: number;
  reason?: string | null;
  status: 'pending' | 'approved' | 'denied' | 'cancelled';
  approved_by?: string | null;
  approved_at?: string | null;
  denial_reason?: string | null;
  created_at: string;
}
export interface PtoBalance {
  id?: string;
  staff_id: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  vacation_hours: number;
  sick_hours: number;
  personal_hours: number;
  updated_at?: string;
}

// ─── Phase 5.2 — Action Plan tasks ────────────────────────────────────
export interface PlanTaskGroup {
  id: string;
  name: string;
  color?: string | null;
  created_at: string;
}
export interface PlanSubtask {
  id: string;
  task_id: string;
  title: string;
  done: boolean;
  done_at?: string | null;
  done_by?: string | null;
  order_index: number;
  created_at: string;
}
export interface PlanReminder {
  id: string;
  task_id: string;
  remind_at: string;
  message?: string | null;
  dismissed: boolean;
  dismissed_at?: string | null;
  task_title?: string;
  priority?: 'High' | 'Medium' | 'Low';
  due_date?: string | null;
}
export interface PlanTask {
  id: string;
  title: string;
  category?: string | null;
  priority: 'High' | 'Medium' | 'Low';
  due_date?: string | null;
  notes?: string | null;
  done: boolean;
  done_at?: string | null;
  group_id?: string | null;
  group_name?: string | null;
  group_color?: string | null;
  assigned_to?: string | null;
  subtask_total?: number;
  subtask_done?: number;
  reminder_soon?: boolean;
  created_at: string;
  updated_at: string;
}
export interface PlanAIDraftResult {
  title: string;
  category: string;
  priority: 'High' | 'Medium' | 'Low';
  due_date: string | null;
  notes: string;
  subtasks: string[];
  suggested_reminder_days: number | null;
}

export const planTasksApi = {
  listTasks: (params?: { done?: boolean; group_id?: string; priority?: string }) =>
    api.get<{ tasks: PlanTask[] }>('/plan-tasks', { params }),
  getTask: (id: string) =>
    api.get<{ task: PlanTask; subtasks: PlanSubtask[]; reminders: PlanReminder[] }>(`/plan-tasks/${id}`),
  createTask: (data: Partial<PlanTask>) => api.post<PlanTask>('/plan-tasks', data),
  updateTask: (id: string, data: Partial<PlanTask>) => api.put<PlanTask>(`/plan-tasks/${id}`, data),
  deleteTask: (id: string) => api.delete(`/plan-tasks/${id}`),

  listGroups: () => api.get<{ groups: PlanTaskGroup[] }>('/plan-tasks/groups'),
  createGroup: (data: { name: string; color?: string }) => api.post<PlanTaskGroup>('/plan-tasks/groups', data),
  deleteGroup: (id: string) => api.delete(`/plan-tasks/groups/${id}`),

  addSubtask: (taskId: string, data: { title: string; done?: boolean }) =>
    api.post<PlanSubtask>(`/plan-tasks/${taskId}/subtasks`, data),
  updateSubtask: (taskId: string, sid: string, data: Partial<PlanSubtask>) =>
    api.put<PlanSubtask>(`/plan-tasks/${taskId}/subtasks/${sid}`, data),
  deleteSubtask: (taskId: string, sid: string) =>
    api.delete(`/plan-tasks/${taskId}/subtasks/${sid}`),

  addReminder: (taskId: string, data: { remind_at: string; message?: string }) =>
    api.post<PlanReminder>(`/plan-tasks/${taskId}/reminders`, data),
  dismissReminder: (taskId: string, rid: string) =>
    api.put<PlanReminder>(`/plan-tasks/${taskId}/reminders/${rid}/dismiss`),
  deleteReminder: (taskId: string, rid: string) =>
    api.delete(`/plan-tasks/${taskId}/reminders/${rid}`),
  upcomingReminders: () =>
    api.get<{ reminders: PlanReminder[] }>('/plan-tasks/upcoming-reminders'),

  aiNextQuestion: (data: { goal: string; answers: { question: string; answer: string }[] }) =>
    api.post<{ done: boolean; question?: string }>('/plan-tasks/ai-next-question', data),
  aiDraft: (data: { goal: string; answers: { question: string; answer: string }[] }) =>
    api.post<PlanAIDraftResult>('/plan-tasks/ai-draft', data),
};

// ─── Phase 6.5 — Client Portal ────────────────────────────────────────
export interface ClientPortalToken {
  id: string;
  token: string;
  facility_id?: string | null;
  client_id?: string | null;
  facility_name?: string | null;
  client_name?: string | null;
  display_label?: string | null;
  expires_at?: string | null;
  revoked: boolean;
  last_accessed_at?: string | null;
  access_count: number;
  created_at: string;
}
// [phase4 track-C] A client's recorded decision on a submission.
export type ClientPortalActionKind = 'approve' | 'decline' | 'request_interview';

export interface ClientPortalView {
  label: string;
  scope: 'facility' | 'client';
  generated_at: string;
  // [phase4 track-C] per-link interaction toggles + contact.
  can_approve?: boolean;
  can_request?: boolean;
  contact_name?: string | null;
  facilities: Array<{ id: string; name: string; city?: string; state?: string }>;
  active_staff: Array<{
    placement_id: string;
    status: string;
    start_date?: string | null;
    end_date?: string | null;
    first_name: string;
    last_name: string;
    role?: string;
    facility_name?: string;
  }>;
  upcoming_submissions: Array<{
    id: string;
    status: string;
    submitted_at?: string | null;
    created_at: string;
    first_name: string;
    last_name: string;
    candidate_role?: string;
    job_title?: string;
    facility_name?: string;
    // [phase4 track-C] actions the client has already taken on this packet.
    actions?: ClientPortalActionKind[];
  }>;
  open_jobs: Array<{
    id: string;
    title: string;
    status: string;
    created_at: string;
    facility_name?: string;
  }>;
  // [phase4 track-C] deep-links into the other tokenized flows (NOT merged).
  pending_approvals?: Array<{
    id: string;
    status: string;
    facility_name?: string | null;
    period_start?: string | null;
    period_end?: string | null;
    link: string;
  }>;
  open_shifts?: Array<{
    id: string;
    status: string;
    role?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    facility_name?: string | null;
    offered_at?: string | null;
    expires_at?: string | null;
  }>;
}

// [phase4 track-C] Client-flavored submission packet (pay/margin hidden).
export interface ClientPortalPacket {
  id: string;
  candidate_name: string;
  candidate_role?: string | null;
  years_experience?: number | null;
  location?: string | null;
  job_title: string;
  job_code?: string | null;
  facility?: string | null;
  submitted_at: string;
  ai_score?: number | null;
  ai_fit_label?: string | null;
  ai_summary?: string | null;
  recruiter_summary?: string | null;
  skill_ratings: Array<{ skill: string; rating: number; notes?: string }>;
  certifications: string[];
  specialties: string[];
  education?: string | null;
  items_to_discuss: Array<{ category: string; gap: string; severity: string }>;
  bill_rate?: number | null;
  recruiter_name?: string | null;
}

export const clientPortalApi = {
  listTokens: (params?: { facility_id?: string; client_id?: string }) =>
    api.get<{ tokens: ClientPortalToken[] }>('/client-portal/admin-tokens', { params }),
  createToken: (data: { facility_id?: string; client_id?: string; display_label?: string; expires_at?: string; can_approve?: boolean; can_request?: boolean; contact_name?: string; contact_email?: string }) =>
    api.post<ClientPortalToken>('/client-portal/admin-tokens', data),
  revokeToken: (id: string) => api.delete(`/client-portal/admin-tokens/${id}`),
  // Public — no auth. The apiClient still attaches Clerk bearer if a user
  // happens to be logged in, but the backend doesn't require it for /view/.
  view: (token: string) => api.get<ClientPortalView>(`/client-portal/view/${token}`),
};

// [phase4 track-C] Interactive client-portal actions. These are genuinely
// public (the URL token is the credential), so they go through a bare axios
// client — no bearer, no auth interceptors — exactly like ClientPortalView's
// data load. `token` is the client_view_tokens.token from the URL.
const publicPortal = axios.create({ baseURL: PUBLIC_API_BASE, headers: { 'Content-Type': 'application/json' }, timeout: 30000 });

export const clientPortalActionsApi = {
  packet: (token: string, submissionId: string) =>
    publicPortal.get<ClientPortalPacket>(`/client-portal/view/${token}/submissions/${submissionId}/packet`),
  decide: (token: string, submissionId: string, action: ClientPortalActionKind, note?: string) =>
    publicPortal.post<{ ok: boolean; action: ClientPortalActionKind; submission_stage: string | null; candidate_emailed: boolean; recruiter_notified: boolean }>(
      `/client-portal/view/${token}/submissions/${submissionId}/decision`, { action, note }),
  requestStaff: (token: string, payload: { facility_id?: string | null; profession?: string; specialty?: string; quantity?: number; shift?: string; needed_by?: string; notes?: string }) =>
    publicPortal.post<{ ok: boolean; id: string }>(`/client-portal/view/${token}/staff-request`, payload),
};

export const ptoApi = {
  listRequests: (params?: { staff_id?: string; status?: string }) =>
    api.get<{ requests: PtoRequest[] }>('/pto/requests', { params }),
  createRequest: (data: Partial<PtoRequest>) => api.post<PtoRequest>('/pto/requests', data),
  updateRequest: (id: string, data: Partial<PtoRequest>) => api.put<PtoRequest>(`/pto/requests/${id}`, data),
  approveRequest: (id: string) => api.put<PtoRequest>(`/pto/requests/${id}/approve`),
  denyRequest: (id: string, reason?: string) => api.put<PtoRequest>(`/pto/requests/${id}/deny`, { reason }),
  cancelRequest: (id: string) => api.put<PtoRequest>(`/pto/requests/${id}/cancel`),
  deleteRequest: (id: string) => api.delete(`/pto/requests/${id}`),
  listBalances: () => api.get<{ balances: PtoBalance[] }>('/pto/balances'),
  getBalance: (staffId: string) => api.get<PtoBalance>(`/pto/balances/${staffId}`),
  updateBalance: (staffId: string, data: Partial<PtoBalance>) => api.put<PtoBalance>(`/pto/balances/${staffId}`, data),
};

// ─── Onboarding ───────────────────────────────────────────────────────────────
export interface OnboardingItem {
  id: string;
  staff_id: string;
  item_name: string;
  category?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'missing';
  due_date?: string;
  completed_at?: string;
  notes?: string;
  created_at: string;
}

export const onboardingApi = {
  list: (params?: { staffId?: string; status?: string }) =>
    api.get<{ items: OnboardingItem[] }>('/onboarding', { params }),
  summary: () => api.get('/onboarding/summary'),
  create: (data: Partial<OnboardingItem>) => api.post<OnboardingItem>('/onboarding', data),
  update: (id: string, data: Partial<OnboardingItem>) =>
    api.put<OnboardingItem>(`/onboarding/${id}`, data),
};

// New Hires — everyone who reached the Offer stage or beyond, with the
// tracker step they're currently on (Offer → Onboarding → Placed → Working).
export interface HireStep { key: string; label: string }
export interface NewHire {
  id: string;
  name: string;
  email: string | null;
  role: string | null;
  stage: string;
  step_index: number;
  step_key: string;
  step_label: string;
  docs_total: number;
  docs_done: number;
  recruiter_name: string | null;
  staff_id: string | null;
  staff_status: string | null;
  stage_changed_at: string | null;
  placement_id: string | null;
  placement_status: string | null;
  placement_start: string | null;
  facility_name: string | null;
}
export const newHiresApi = {
  list: () =>
    api.get<{
      hires: NewHire[];
      summary: { key: string; label: string; count: number }[];
      steps: HireStep[];
    }>('/new-hires'),
};

// ─── Documents ────────────────────────────────────────────────────────────────
export interface Document {
  id: string;
  staff_id?: string;
  name: string;
  type?: string;
  status: 'pending' | 'checking' | 'passed' | 'issues_found' | 'rejected';
  ai_review_result?: {
    passed_checks: string[];
    issues: Array<{ severity: 'error' | 'warning'; message: string; field?: string }>;
    questions: Array<{ question: string; context: string }>;
    overall_status: string;
    summary: string;
  };
  created_at: string;
  has_file?: boolean;
  file_url?: string | null;
}

export const documentsApi = {
  list: (params?: { staff_id?: string; facility_id?: string; status?: string }) =>
    api.get<{ documents: Document[] }>('/documents', { params }),
  get: (id: string) => api.get<Document>(`/documents/${id}`),
  download: (id: string) => api.get<Blob>(`/documents/${id}/file`, { responseType: 'blob' }),
  upload: (formData: FormData) =>
    api.post<{ document: Document; qaQuestions: number }>('/documents/upload', formData, {
      // Reading a photo/scan with vision + running the compliance analysis can
      // take well over the 30s default — give it the long-op window so the
      // browser doesn't abort with "Failed to check document".
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180_000,
    }),
  pendingQA: () => api.get('/documents/qa/pending'),
  answerQA: (id: string, answer: string, answer_scope: string) =>
    api.post(`/documents/qa/${id}/answer`, { answer, answer_scope }),
};

// ─── Insurance ────────────────────────────────────────────────────────────────
export interface InsurancePolicy {
  id: string;
  type: 'workers_comp' | 'professional_liability' | 'epli' | 'general_liability' | 'other';
  provider?: string;
  policy_number?: string;
  annual_premium?: number;
  coverage_limit?: string;
  status: 'quote_needed' | 'quote_received' | 'applied' | 'active' | 'expired';
  renewal_date?: string;
  notes?: string;
  created_at: string;
}

export const insuranceApi = {
  list: () => api.get<{ policies: InsurancePolicy[] }>('/insurance'),
  get: (id: string) => api.get<InsurancePolicy>(`/insurance/${id}`),
  create: (data: Partial<InsurancePolicy>) => api.post<InsurancePolicy>('/insurance', data),
  update: (id: string, data: Partial<InsurancePolicy>) =>
    api.put<InsurancePolicy>(`/insurance/${id}`, data),
  delete: (id: string) => api.delete(`/insurance/${id}`),
};

// ─── Learning ─────────────────────────────────────────────────────────────────
export interface AIRule {
  id: string;
  rule_text: string;
  scope?: string;
  source: 'document_qa' | 'three_strike' | 'manual' | 'setup_wizard';
  correction_count: number;
  is_active: boolean;
  facility_name?: string;
  created_at: string;
  corrections?: Array<{
    id: string;
    correction_text: string;
    is_exception: boolean;
    exception_details?: string;
    created_at: string;
  }>;
}

export const learningApi = {
  corrections: () => api.get<{ rules: AIRule[] }>('/learning/corrections'),
  strike: (id: string, correction_text: string, is_exception = false, exception_details?: string) =>
    api.post(`/learning/corrections/${id}/strike`, { correction_text, is_exception, exception_details }),
  defend: (id: string) => api.post(`/learning/corrections/${id}/defend`),
  addManual: (data: { content: string; source?: string; tags?: string[]; facility_id?: string }) =>
    api.post('/learning/manual', data),
  rules: () => api.get<{ rules: AIRule[] }>('/learning/rules'),
};

// ─── Timekeeping ──────────────────────────────────────────────────────────────
export interface Timesheet {
  id: string;
  staff_id: string;
  facility_id: string;
  placement_id?: string;
  week_start: string;
  hours_worked?: number;
  status: 'pending' | 'verified' | 'disputed' | 'approved';
  first_name?: string;
  last_name?: string;
  facility_name?: string;
  created_at: string;
}

export const timekeepingApi = {
  list: (params?: { status?: string; staff_id?: string; facility_id?: string }) =>
    api.get<{ timesheets: Timesheet[] }>('/timekeeping', { params }),
  submit: (data: Partial<Timesheet>) => api.post<Timesheet>('/timekeeping', data),
  verify: (id: string, status?: string, notes?: string) =>
    api.post(`/timekeeping/${id}/verify`, { status, notes }),
};

// ─── Attendance ─────────────────────────────────────────────────────────────
export interface AttendanceRecord {
  id: string;
  date: string;
  staff_name: string;
  staff_role: string | null;
  clock_in: string | null;
  clock_out: string | null;
  break_minutes: number;
  total_hours: number;
  notes: string | null;
  status: 'present' | 'late' | 'absent' | 'half_day' | 'pto' | 'sick';
  created_at?: string;
}
export type AttendanceInput = Omit<AttendanceRecord, 'id' | 'created_at'>;
export const attendanceApi = {
  list: (params?: { date?: string; status?: string }) =>
    api.get<{ records: AttendanceRecord[] }>('/attendance', { params }),
  create: (data: AttendanceInput) => api.post<{ record: AttendanceRecord }>('/attendance', data),
  remove: (id: string) => api.delete<{ deleted: boolean }>(`/attendance/${id}`),
  import: (records: AttendanceInput[]) => api.post<{ imported: number }>('/attendance/import', { records }),
};

// ─── Funding & Banking settings ─────────────────────────────────────────────
export const fundingApi = {
  get: () => api.get<{ settings: Record<string, Record<string, unknown>> }>('/funding'),
  put: (key: string, value: Record<string, unknown>) =>
    api.put<{ setting: unknown }>(`/funding/${key}`, { value }),
};

// ─── Checklists ───────────────────────────────────────────────────────────────
export const checklistsApi = {
  templates: (params?: { facility_id?: string }) =>
    api.get('/checklists/templates', { params }),
  getTemplate: (id: string) => api.get(`/checklists/templates/${id}`),
  createTemplate: (data: unknown) => api.post('/checklists/templates', data),
  updateTemplate: (id: string, data: unknown) => api.put(`/checklists/templates/${id}`, data),
};

// ─── eSign ────────────────────────────────────────────────────────────────────
// One signing role on a template — see backend services/templateRoles.ts.
// Role.key is stable, label is the display name, order is used for
// sequential signing.
export interface DetectedField {
  page: number;
  type: 'signature' | 'initials' | 'date' | 'text' | 'checkbox' | 'radio' | 'dropdown' | 'file-upload';
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  role_hint: 'signer' | 'admin' | null;
  /** Template role key, or a document signer's id. */
  role_key?: string | null;
  confidence: number;
  required?: boolean;
  context_text?: string;
  options?: string[];
  group?: string;
  group_mandatory?: boolean;
  validation?: { preset: string; pattern?: string; message?: string };
}

export interface ESignTemplateRole {
  key: string;
  label: string;
  order: number;
}

export interface ESignTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  content?: string;
  /** How often this template has been used (drives default Quick Send order). */
  use_count?: number;
  /** Whether the current user has pinned this template to the top of Quick Send. */
  pinned?: boolean;
  /** When this user pinned it. Newest pins sort first. */
  pinned_at?: string | null;
  fields: Array<{
    id: string;
    label: string;
    type: string;
    required: boolean;
    default_value?: string;
    options?: string[];
    placeholder?: string;
    /** Role this field is bound to (stage 2 visual builder uses this). */
    role_key?: string;
  }>;
  // Roles + signing order live on the template now (PR scope: roles
  // rework). Defaults to [HR, Candidate] for system templates that
  // don't have an explicit overlay.
  roles?: ESignTemplateRole[];
  signing_order?: 'parallel' | 'sequential';
  /** Server-relative path to the uploaded PDF, if attached. */
  file_path?: string | null;
  is_system: boolean;
  is_active: boolean;
  created_at: string | null;
}

export interface ESignDocument {
  id: string;
  template_id: string;
  title: string;
  field_values: Record<string, string>;
  status: 'draft' | 'sent' | 'partially_signed' | 'completed' | 'voided' | 'expired';
  created_by: string;
  created_at: string;
  completed_at?: string;
  expires_at?: string;
  voided_at?: string;
  void_reason?: string;
  signers: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    status: 'pending' | 'viewed' | 'signed' | 'declined';
    signed_at?: string;
    token: string;
  }>;
}

export const esignApi = {
  // Templates
  listTemplates: () => api.get<{ templates: ESignTemplate[] }>('/esign/templates'),
  listTemplatePins: () => api.get<{ pins: Array<{ template_id: string; pinned_at: string }> }>('/esign/templates/pins'),
  pinTemplate: (id: string) =>
    api.put<{ ok: boolean; pinned: boolean; pinned_at?: string }>(`/esign/templates/${id}/pin`, {}),
  unpinTemplate: (id: string) =>
    api.delete<{ ok: boolean; pinned: boolean }>(`/esign/templates/${id}/pin`),
  getTemplate: (id: string) => api.get<{ template: ESignTemplate }>(`/esign/templates/${id}`),
  createTemplate: (data: Partial<ESignTemplate>) => api.post<{ template: ESignTemplate }>('/esign/templates', data),
  updateTemplate: (id: string, data: Partial<ESignTemplate>) => api.put<{ template: ESignTemplate }>(`/esign/templates/${id}`, data),
  deleteTemplate: (id: string) => api.delete(`/esign/templates/${id}`),
  duplicateTemplate: (id: string) => api.post<{ template: ESignTemplate }>(`/esign/templates/${id}/duplicate`),
  // Measures the PDF (lines, boxes, text) and asks the model to label
  // the blanks. Nothing is written until the editor accepts a selection.
  // existing is the canvas right now, so a second scan skips those boxes.
  // roles are the template roles or the document's signers.
  detectTemplateFields: (id: string, body?: { existing?: Array<{ page: number; x: number; y: number; width: number; height: number }>; roles?: Array<{ key: string; label: string }> }) =>
    api.post<{ fields: DetectedField[]; warnings: string[] }>(`/esign/templates/${id}/detect-fields`, body ?? {}, { timeout: 240_000 }),
  detectDocumentFields: (id: string, body?: { existing?: Array<{ page: number; x: number; y: number; width: number; height: number }>; roles?: Array<{ key: string; label: string }> }) =>
    api.post<{ fields: DetectedField[]; warnings: string[] }>(`/esign/documents/${id}/detect-fields`, body ?? {}, { timeout: 240_000 }),
  // PR scope: template-roles rework. Attaches a PDF to a custom
  // template so document-from-template flows always start from the
  // same canvas. System templates are code-defined and reject this.
  uploadTemplateFile: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ template: ESignTemplate }>(
      `/esign/templates/${id}/upload-file`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  // Streams the attached PDF as raw bytes — used by the template
  // visual builder (Stage 2) to feed pdf.js for field placement.
  getTemplateFile: (id: string) =>
    api.get<ArrayBuffer>(`/esign/templates/${id}/file`, { responseType: 'arraybuffer' }),

  // Documents
  listDocuments: (params?: { status?: string; staff_id?: string; search?: string }) =>
    api.get<{ documents: ESignDocument[] }>('/esign/documents', { params }),
  getDocument: (id: string) => api.get<{ document: any; fields?: any[]; auditLog?: any[] }>(`/esign/documents/${id}`),
  // Admin preview of the stamped result (signatures/fields drawn on a copy of
  // the real PDF; unfilled fields shown as labeled placeholder boxes).
  previewDocument: (id: string) => api.get<Blob>(`/esign/documents/${id}/preview`, { responseType: 'blob' }),
  createDocument: (data: any) => api.post<{ document: ESignDocument; signers: any[] }>('/esign/documents', data),
  sendDocument: (data: {
    template_id: string; title: string; field_values: Record<string, string>;
    signers: Array<{ name: string; email?: string; role?: string; order_index?: number; auth_method?: string }>;
    staff_id?: string; expires_days?: number; signing_order?: string; message?: string;
  }) => api.post<{ document: ESignDocument; signers: Array<{ name: string; email: string; signing_url: string }> }>('/esign/documents', data),
  sendDocument2: (id: string) => api.post<{
    success?: boolean;
    document?: any;
    signers: any[];
    emails_sent?: number;
    emails_failed?: number;
    message?: string;
  }>(`/esign/documents/${id}/send`),
  uploadDocument: (formData: FormData) => api.post<{ document: any }>('/esign/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateDocument: (id: string, data: any) => api.put(`/esign/documents/${id}`, data),
  voidDocument: (id: string, reason?: string) => api.post(`/esign/documents/${id}/void`, { reason }),
  // Clone an existing doc (its placed fields + the original PDF
  // bytes) into a brand-new custom template. Lets recruiters turn
  // an ad-hoc upload into a reusable template after the fact.
  saveDocumentAsTemplate: (id: string, data?: { name?: string; category?: string; description?: string }) =>
    api.post<{ template: ESignTemplate }>(`/esign/documents/${id}/save-as-template`, data ?? {}),
  remind: (id: string) => api.post<{
    signers: Array<{ name: string; email: string; signing_url: string }>;
    pendingSigners?: Array<{ name: string; email: string; signing_url: string }>;
    emails_sent?: number;
    emails_failed?: number;
    message?: string;
  }>(`/esign/documents/${id}/remind-all`),
  // Read-only. Copy / Get Links must not call remind-all, which emails signers.
  signingLinks: (id: string) => api.get<{ signers: Array<{ id: string; name: string; email: string; signing_url: string; status: string }> }>(`/esign/documents/${id}/signing-links`),
  downloadSigned: (id: string) => api.get(`/esign/documents/${id}/download`, { responseType: 'blob' }),
  downloadFieldFile: (docId: string, fieldId: string) =>
    api.get<Blob>(`/esign/documents/${docId}/fields/${fieldId}/file`, { responseType: 'blob' }),
  getAudit: (id: string) => api.get<{ auditLog: any[] }>(`/esign/documents/${id}/audit`),
  getFields: (id: string) => api.get<{ fields: any[] }>(`/esign/documents/${id}/fields`),
  saveFields: (id: string, fields: any[]) => api.post<{ fields: any[] }>(`/esign/documents/${id}/fields`, { fields }),
  addSigner: (id: string, data: any) => api.post(`/esign/documents/${id}/signers`, data),
  updateSigner: (id: string, sid: string, data: any) => api.put(`/esign/documents/${id}/signers/${sid}`, data),
  deleteSigner: (id: string, sid: string) => api.delete(`/esign/documents/${id}/signers/${sid}`),
  stats: () => api.get<{ stats: { awaiting: string; completed: string; voided: string; drafts: string; declined: string; total: string; custom_templates: string } }>('/esign/stats'),
  analytics: () => api.get<{ overview: any; daily: any[]; topTemplates: any[]; slowestDocuments: any[] }>('/esign/analytics'),

  // Online forms
  listForms: () => api.get<{ forms: any[] }>('/esign/forms'),
  createForm: (data: any) => api.post<{ form: any }>('/esign/forms', data),
  getForm: (id: string) => api.get<{ form: any }>(`/esign/forms/${id}`),
  updateForm: (id: string, data: any) => api.put(`/esign/forms/${id}`, data),
  getFormSubmissions: (id: string) => api.get(`/esign/forms/${id}/submissions`),

  // Public signing (no auth — plain axios).
  //
  // These MUST use the absolute backend URL (VITE_API_URL), same as
  // getSigningPdf. A relative `/api/v1/...` on the static production host is
  // answered by the SPA fallback with index.html and a 200 — axios resolves
  // "successfully" with HTML, so the signer page got no fields, no signer
  // name, and no document title while the PDF (absolute URL) still rendered.
  getSigningPage: (token: string) => axios.get(`${PUBLIC_API_BASE}/esign/sign/${token}`),
  getSigningPdf: (token: string) =>
    fetch(`${PUBLIC_API_BASE}/esign/sign/${encodeURIComponent(token)}/pdf`),
  submitConsent: (token: string) => axios.post(`${PUBLIC_API_BASE}/esign/sign/${token}/consent`),
  saveFieldValue: (token: string, fieldId: string, value: any) => axios.post(`${PUBLIC_API_BASE}/esign/sign/${token}/field/${fieldId}`, { value }),
  submitSignature: (token: string, data: { signature_data: string; signature_type: string; typed_name?: string; field_values?: Record<string, any> }) =>
    axios.post(`${PUBLIC_API_BASE}/esign/sign/${token}/sign`, data),
  declineDocument: (token: string, reason: string) => axios.post(`${PUBLIC_API_BASE}/esign/sign/${token}/decline`, { reason }),
  // ESIGN withdrawal / paper path — stops electronic signing for this signer
  // and notifies the sender to arrange paper documents.
  requestPaperCopy: (token: string, reason?: string) => axios.post(`${PUBLIC_API_BASE}/esign/sign/${token}/paper-request`, { reason }),
  // Durable signer self-service download of the executed copy + certificate.
  completedPdfUrl: (token: string) => `${PUBLIC_API_BASE}/esign/sign/${encodeURIComponent(token)}/completed-pdf`,
  // Admin: tamper-evidence check against the completion-time SHA-256.
  verifyDocument: (id: string) => axios.get(`/esign/documents/${id}/verify`),
};

// AI Advisor reply → file. Generates a PDF / DOCX / XLSX from any agent
// message and returns a blob_id the caller can stream from the existing
// /ai-advisors/attachments/:blobId/download endpoint.
export const aiAdvisorsExportApi = {
  exportMessage: (messageId: string, format: 'pdf' | 'docx' | 'xlsx') =>
    api.post<{ blob_id: string; filename: string; mime: string; size: number }>(
      `/ai-advisors/messages/${messageId}/export?format=${format}`,
    ),
};

// ─── Candidates ──────────────────────────────────────────────────────────────
export interface Candidate {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  linkedin_url?: string | null;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  role?: 'RN' | 'LPN' | 'LVN' | 'CNA' | 'RT' | 'NP' | 'PA' | 'Other';
  specialties?: string[];
  skills?: string[];
  certifications?: string[];
  licenses?: string[];
  years_experience?: number;
  education?: string;
  resume_url?: string;
  parsed_resume?: ParsedResume;
  stage: 'application' | 'interview' | 'credentialing' | 'onboarding' | 'placed' | 'rejected' | 'withdrawn';
  status: 'active' | 'inactive' | 'placed' | 'rejected' | 'withdrawn';
  assigned_recruiter_id?: string;
  recruiter_name?: string;
  target_facility_id?: string;
  target_facility_name?: string;
  desired_pay_rate?: number;
  offered_pay_rate?: number;
  availability_start?: string;
  availability_type?: 'full_time' | 'part_time' | 'per_diem' | 'contract';
  available_shifts?: string[];
  recruiter_notes?: string;
  hr_notes?: string;
  source?: string;
  // Recruiter → HR workflow
  recruiter_screening_status?: 'not_started' | 'in_progress' | 'complete';
  recruiter_screening?: Record<string, string> | null;
  recruiter_screening_completed_at?: string | null;
  hr_interview_status?: 'not_started' | 'pending' | 'in_progress' | 'complete';
  hr_interview?: Record<string, string> | null;
  hr_interview_completed_at?: string | null;
  hr_decision?: 'approved' | 'rejected' | null;
  hr_decision_reason?: string | null;
  hr_decision_at?: string | null;
  resume_filename?: string | null;
  resume_uploaded_at?: string | null;
  has_resume?: boolean;
  has_submission?: boolean;
  checklist_manual?: Record<string, boolean> | null;
  checklist_checks?: Record<string, { checked?: boolean; by_user_id?: string | null; by_name?: string | null; at?: string | null }> | null;
  // Candidate tracker signals. Present on the candidate profile and when
  // the list is requested with checklist=1. Application status is the
  // eSign envelope, not has_submission.
  application_esign_status?: string | null;
  ai_checked?: boolean;
  human_reviewer_name?: string | null;
  corrections_open?: boolean;
  background_check_status?: string | null;
  fingerprints_on_file?: boolean;
  onboarding_docs_complete?: boolean;
  shiftwise_uploaded?: boolean;
  prestart_lab?: boolean;
  prestart_hsh?: boolean;
  requires_jail_application?: boolean;
  jail_application_source?: 'job' | 'facility' | null;
  tracker_view_stage?: string | null;
  tracker_view_mapped?: boolean;
  days_since_update?: number;
  missing_docs_count?: number;
  stage_history?: StageHistory[];
  documents?: CandidateDocument[];
  onboarding_forms?: OnboardingForm[];
  // Referral tracking (from FNS tracker import + the candidate form)
  referred_by_user_id?: string | null;
  referred_by_user_name?: string | null;
  referred_by_candidate_id?: string | null;
  referred_by_candidate_name?: string | null;
  referred_by_other?: string | null;
  referral_commission_paid_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ParsedResume {
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  role: string | null;
  specialties: string[];
  skills: string[];
  certifications: string[];
  licenses: string[];
  education: Array<{ degree: string; institution: string; year: string | null }>;
  work_history: Array<{ title: string; employer: string; start_date: string | null; end_date: string | null; description: string | null }>;
  years_experience: number | null;
  summary: string | null;
}

// AI reading of a resume — mirrors backend services/resumeReview.ts.
export interface ResumeFit {
  job_id: string;
  job_title: string;
  score: number;        // 0–100
  verdict: string;      // "Strong fit" | "Possible fit" | "Weak fit"
  strengths: string[];
  missing: string[];
}
export interface ResumeReview {
  summary: string;
  highlights: string[];
  years_experience: number | null;
  red_flags: string[];
  gaps: string[];
  interview_questions: string[];
  fit: ResumeFit | null;
  ai_configured: boolean;   // false → no API key on server; show a notice
  resume_filename: string | null;
  generated_at: string;
}

export interface StageHistory {
  id: string;
  candidate_id: string;
  from_stage: string | null;
  to_stage: string;
  moved_by_name?: string;
  notes?: string;
  created_at: string;
}

export interface CandidateDocument {
  id: string;
  candidate_id: string;
  document_type: string;
  label: string;
  status: 'missing' | 'pending' | 'received' | 'approved' | 'rejected' | 'expired';
  file_url?: string;
  expiry_date?: string;
  notes?: string;
  required: boolean;
  uploaded_at?: string;
  approved_at?: string;
  created_at: string;
  updated_at: string;
  /** True when a file blob is attached (so the UI can offer Download). */
  has_file?: boolean;
}

export interface OnboardingForm {
  id: string;
  candidate_id: string;
  form_type: 'w4' | 'i9' | 'direct_deposit' | 'emergency_contact' | 'hipaa' | 'handbook' | 'other';
  status: 'not_sent' | 'sent' | 'opened' | 'completed' | 'expired';
  sent_at?: string;
  completed_at?: string;
  reminder_count: number;
  created_at: string;
}

export interface Reminder {
  id: string;
  type: 'email' | 'sms' | 'both';
  trigger_type: 'missing_document' | 'incomplete_onboarding' | 'pending_application' | 'credential_expiry' | 'manual';
  candidate_id?: string;
  candidate_name?: string;
  recipient_email?: string;
  recipient_phone?: string;
  recipient_name?: string;
  subject: string;
  message: string;
  status: 'scheduled' | 'sent' | 'completed' | 'overdue' | 'failed' | 'cancelled';
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
}

// One row from candidate_upload_links. The `token` is the secret
// piece that gets pasted into the public URL — treat it like a
// password.
export interface CandidateUploadLink {
  id: string;
  token: string;
  label: string | null;
  created_by: string | null;
  expires_at: string | null;     // ISO timestamp or null = no expiry
  max_uses: number | null;       // null = unlimited
  used_count: number;
  revoked_at: string | null;
  last_used_at: string | null;
  created_at: string;
}

// Public landing page metadata returned by GET /uploads/:token.
// Reveals only the candidate's first name so the URL doesn't leak
// PII to anyone who finds it.
export interface CandidateUploadLinkPublic {
  first_name: string;
  label: string | null;
  expires_at: string | null;
  max_uses: number | null;
  uses_remaining: number | null;
}

// One row of the candidate's required-document checklist, returned
// by GET /uploads/:token/checklist. Each row corresponds to a
// candidate_documents record the recruiter set up; the candidate
// uploads against a specific row by passing its `id` back as
// `document_id` on POST /uploads/:token.
export interface CandidateUploadChecklistItem {
  id: string;
  document_type: string;
  label: string;
  status: 'missing' | 'pending' | 'received' | 'approved' | 'rejected' | 'expired';
  required: boolean;
  expiry_date: string | null;
  notes: string | null;
  uploaded_at: string | null;
}

export interface CallConsent {
  call_consent_status: 'unknown' | 'granted' | 'revoked';
  call_consent_at: string | null;
  call_consent_source: string | null;
  call_consent_phone: string | null;
}

export type CallOutcome =
  | 'connected' | 'voicemail' | 'no_answer' | 'callback'
  | 'not_interested' | 'on_contract' | 'wrong_number' | 'do_not_call';

export interface CandidateCall {
  id: string;
  candidate_id: string;
  logged_by: string | null;
  logged_by_name: string | null;
  logged_by_display?: string;
  outcome: CallOutcome;
  notes: string | null;
  follow_up_at: string | null;
  created_at: string;
}

export interface CandidateNote {
  id: string;
  candidate_id: string;
  author_id: string | null;
  author_name: string | null;
  author_display?: string;
  body: string;
  pinned: boolean;
  created_at: string;
  edited_at: string | null;
}

export type BulkImportMode = 'single' | 'match' | 'none';
export interface BulkImportItem {
  file: string;
  status: 'created' | 'updated' | 'duplicate' | 'failed';
  candidateId?: string;
  name?: string;
  jobId?: string | null;
  jobTitle?: string | null;
  reason?: string;
  filled?: string[];
}
export interface BulkImportResponse {
  summary: { total: number; created: number; updated: number; duplicates: number; failed: number; assigned: number };
  items: BulkImportItem[];
}

// Result of the natural-language candidate search: structured filters the
// Candidates page applies to the normal list.
export interface SmartCandidateFilter {
  role: string | null;
  stage: string | null;
  status: string | null;
  shift: string | null;
  unassigned_only: boolean;
  advanced: Array<{ field: string; op: string; value: string }>;
  summary: string;
}

export const candidatesApi = {
  list: (params?: {
    stage?: string; status?: string; search?: string;
    // Phase 1.1D — role + shift filters
    role?: string; shift?: string;
    // Web-applicant intake — source='website'
    source?: string;
    // Candidates submitted to a specific job (per-job checklist)
    job_id?: string;
    // Advanced "Edit Filters" — JSON-encoded array of {field, op, value}
    filters?: string;
    // "Unassigned only" — candidates with no recruiter assigned.
    unassigned?: string;
    // Exclude toggles for sourcing fresh leads.
    not_contacted?: string;
    not_in_recruitment?: string;
    // Opt-in pagination (the list page). Omit for the full filtered set.
    limit?: number; offset?: number;
    // Attach eSign, background, ShiftWise, and jail-application signals.
    checklist?: '1';
  }) =>
    api.get<{ candidates: Candidate[]; total?: number }>('/candidates', { params }),
  // Natural-language search → structured filters the client then applies.
  smartFilter: (prompt: string) =>
    api.post<{ filter: SmartCandidateFilter }>('/candidates/smart-filter', { prompt }),
  get: (id: string) => api.get<Candidate>(`/candidates/${id}`),
  // `force=true` bypasses the server-side duplicate-candidate guard
  // (a 409 with `duplicate_candidate` is returned otherwise). Used by
  // the create form's "Create anyway" override after the user has seen
  // the matching candidates and confirmed the new record is intentional.
  create: (data: Partial<Candidate>, opts?: { force?: boolean }) =>
    api.post<Candidate>('/candidates', data, opts?.force ? { params: { force: 1 } } : undefined),
  update: (id: string, data: Partial<Candidate>) => api.put<Candidate>(`/candidates/${id}`, data),
  delete: (id: string) => api.delete(`/candidates/${id}`),
  permanentDelete: (id: string) => api.delete(`/candidates/${id}/permanent`),
  // Every id matching the given filters, for "select all N matching".
  ids: (params?: Record<string, unknown>) =>
    api.get<{ ids: string[]; capped: boolean; limit: number }>('/candidates/ids', { params }),
  // One request for the whole selection, instead of one request per candidate.
  bulk: (ids: string[], action: 'archive' | 'permanent') =>
    api.post<{
      action: 'archive' | 'permanent';
      requested: number;
      succeeded: number;
      blocked: number;
      missing: number;
      blocked_ids?: string[];
    }>('/candidates/bulk', { ids, action }),
  moveStage: (id: string, stage: string, notes?: string) =>
    api.post(`/candidates/${id}/move-stage`, { stage, notes }),
  // ─── Call log ──────────────────────────────────────────────────
  calls: (id: string) => api.get<{ calls: CandidateCall[] }>(`/candidates/${id}/calls`),
  logCall: (id: string, body: { outcome: CallOutcome; notes?: string; follow_up_at?: string }) =>
    api.post<{ call: CandidateCall }>(`/candidates/${id}/calls`, body),
  notes: (id: string) => api.get<{ notes: CandidateNote[] }>(`/candidates/${id}/notes`),
  addNote: (id: string, body: { body: string; pinned?: boolean }) =>
    api.post<{ note: CandidateNote }>(`/candidates/${id}/notes`, body),
  updateNote: (id: string, noteId: string, body: { body?: string; pinned?: boolean }) =>
    api.patch<{ note: CandidateNote }>(`/candidates/${id}/notes/${noteId}`, body),
  deleteNote: (id: string, noteId: string) =>
    api.delete<{ ok: true }>(`/candidates/${id}/notes/${noteId}`),
  parseResume: (id: string, file: File) => {
    const form = new FormData();
    form.append('resume', file);
    return api.post<{ success: boolean; parsed: ParsedResume }>(`/candidates/${id}/parse-resume`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // ─── Mass resume import ────────────────────────────────────────
  // Upload many resume files (or a .zip of them) at once. `mode` decides job
  // assignment: 'single' → all to `job_id`; 'match' → each to its best-fit open
  // job; 'none' → pool only. Long timeout: the server AI-parses every file.
  /** Repair candidates whose parsed resume never seeded structured records. */
  backfillRecords: () =>
    api.post<{ scanned: number; repaired: number; education_added: number; work_added: number }>(
      '/candidates/backfill-records', {}, { timeout: 300_000 }),
  bulkImport: (
    files: File[],
    opts: { mode: BulkImportMode; job_id?: string | null },
  ) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    form.append('mode', opts.mode);
    if (opts.mode === 'single' && opts.job_id) form.append('job_id', opts.job_id);
    return api.post<BulkImportResponse>('/candidates/bulk-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 1_200_000, // up to 20 min — a few hundred resumes are AI-parsed server-side
    });
  },
  // ─── Recruiter → HR workflow ───────────────────────────────────
  // Store the ORIGINAL resume file so it stays attached + downloadable.
  uploadResume: (id: string, file: File, opts?: { parse?: boolean }) => {
    const form = new FormData();
    form.append('resume', file);
    return api.post<{ success: boolean; filename: string; parsed: ParsedResume | null }>(
      `/candidates/${id}/resume`, form,
      { headers: { 'Content-Type': 'multipart/form-data' }, params: opts?.parse ? { parse: 1 } : undefined },
    );
  },
  downloadResume: (id: string) => api.get<Blob>(`/candidates/${id}/resume`, { responseType: 'blob' }),
  // Resume in a browser-renderable form for the in-app viewer: PDF/image pass
  // through, DOCX/TXT come back as HTML. Fetched as a blob so the bearer token
  // rides along; the caller object-URLs it into a (sandboxed) iframe.
  viewResume: (id: string) => api.get<Blob>(`/candidates/${id}/resume/view`, { responseType: 'blob' }),
  // AI reading of the resume. Omit job_id for the general review (cached
  // server-side); pass a job_id to add a fit assessment (always fresh).
  reviewResume: (id: string, opts?: { job_id?: string; refresh?: boolean }) =>
    api.post<{ review: ResumeReview; cached: boolean }>(`/candidates/${id}/resume/review`, {
      job_id: opts?.job_id, refresh: opts?.refresh,
    }),
  /** Write a resume from the ATS profile, for candidates who don't have one. */
  draftResume: (id: string) =>
    api.post<{ filename: string; markdown: string }>(`/candidates/${id}/draft-resume`),
  saveScreening: (id: string, answers: Record<string, string>, complete?: boolean) =>
    api.post<{ success: boolean; candidate: Candidate }>(`/candidates/${id}/recruiter-screening`, { answers, complete }),
  workforceStatus: () => api.get<{
    total: number;
    categories: { submitted_pending: number; cleared_bg_pending_start: number; pending_bg: number; start_scheduled: number; actively_working: number; withdrew_declined: number; in_pipeline: number };
    shifts: { day: number; night: number; both: number; unspecified: number };
    pending_starts: Array<{ id: string; name: string; role: string | null; shift: string; start_date: string | null; status: string }>;
  }>('/candidates/workforce-status'),
  interviewQuestions: (id: string, jobId?: string) =>
    api.get<{ job: { id: string; title: string | null; client: string | null } | null;
      baseline: Array<{ id: string; question: string; category: string }>;
      tailored: Array<{ id: string; question: string; category: string }>;
      client_set: {
        playbook_id: string; slug: string; title: string; client_name: string | null;
        sections: Array<{ section: string; items: Array<{ number: number; question: string; hint?: string; critical?: boolean }> }>;
      } | null }>(
      `/candidates/${id}/interview-questions${jobId ? `?job_id=${encodeURIComponent(jobId)}` : ''}`),
  hrInterview: (id: string, body: { action: 'start' | 'save' | 'complete'; answers?: Record<string, string>; decision?: 'approved' | 'rejected'; reason?: string }) =>
    api.post<{ success: boolean; candidate: Candidate }>(`/candidates/${id}/hr-interview`, body),
  hrInterviewQueue: () => api.get<{ candidates: Array<Candidate & { submission_id: string; job_id: string; job_title: string; job_city?: string | null; job_state?: string | null }> }>('/candidates/hr-interview-queue'),
  addToHrQueue: (id: string, jobId?: string) =>
    api.post<{ success: boolean; submission_id: string }>(`/candidates/${id}/hr-queue`, jobId ? { job_id: jobId } : {}),
  toggleChecklist: (id: string, key: string, value: boolean) =>
    api.patch<{ success: boolean; checklist_manual: Record<string, boolean>; checklist_checks?: Candidate['checklist_checks'] }>(`/candidates/${id}/checklist`, { key, value }),

  getDocuments: (id: string) => api.get<{ documents: CandidateDocument[] }>(`/candidates/${id}/documents`),
  // `force=true` bypasses the server-side duplicate-document-type guard
  // (a 409 with `duplicate_document_type` is returned otherwise). Used
  // by the document add flow's "Add anyway" override.
  addDocument: (id: string, data: Partial<CandidateDocument>, opts?: { force?: boolean }) =>
    api.post<CandidateDocument>(`/candidates/${id}/documents`, data, opts?.force ? { params: { force: 1 } } : undefined),
  updateDocument: (id: string, docId: string, data: Partial<CandidateDocument>) =>
    api.put<CandidateDocument>(`/candidates/${id}/documents/${docId}`, data),
  deleteDocument: (id: string, docId: string) =>
    api.delete<{ success: boolean }>(`/candidates/${id}/documents/${docId}`),
  // Download the file attached to a candidate document (resume, credential, etc.).
  downloadDocumentFile: (id: string, docId: string) =>
    api.get<Blob>(`/candidates/${id}/documents/${docId}/file`, { responseType: 'blob' }),
  uploadDocumentFile: (id: string, docId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; document_id: string; filename: string; size: number; has_file: boolean }>(
      `/candidates/${id}/documents/${docId}/file`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  // ─── Tokenised candidate upload links ──────────────────────────
  // Recruiter generates a public URL the candidate can use without
  // logging in. Uploaded files attach to the candidate's documents
  // automatically. See backend services/candidateUploadLinks.ts.
  listUploadLinks: (id: string) =>
    api.get<{ links: CandidateUploadLink[] }>(`/candidates/${id}/upload-links`),
  createUploadLink: (id: string, data: { label?: string; expires_days?: number; max_uses?: number }) =>
    api.post<{ link: CandidateUploadLink }>(`/candidates/${id}/upload-links`, data),
  revokeUploadLink: (linkId: string) =>
    api.delete<{ success: boolean }>(`/candidates/upload-links/${linkId}`),
  // Phase 1.3B — upload a file and run AI credential review on an existing
  // candidate_documents row. The backend stores the review JSON in notes,
  // updates expiry_date if readable, and auto-advances status when
  // confidence is high.
  reviewDocument: (candidateId: string, docId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{
      success: boolean;
      status: 'approved' | 'pending' | 'rejected';
      review: {
        type_match: boolean;
        expired: boolean | null;
        expiry_date: string | null;
        complete: boolean;
        issues: string[];
        confidence: 'high' | 'medium' | 'low';
        summary: string;
        clarification_needed: string | null;
        recommended_status: 'approved' | 'pending' | 'rejected';
      };
    }>(`/candidates/${candidateId}/documents/${docId}/review`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getStageHistory: (id: string) => api.get<{ history: StageHistory[] }>(`/candidates/${id}/stage-history`),
  getOnboardingForms: (id: string) => api.get<{ forms: OnboardingForm[] }>(`/candidates/${id}/onboarding-forms`),
  sendOnboardingForm: (id: string, form_type: string) =>
    api.post<OnboardingForm>(`/candidates/${id}/onboarding-forms`, { form_type }),
  stats: () => api.get<{ total: number; by_stage: Record<string, number>; recent_7_days: number }>('/candidates/stats/overview'),
  // ATS Phase 3
  matchingJobs: (id: string) => api.get<{
    jobs: Array<{
      id: string; job_code?: string; title: string;
      profession?: string; specialty?: string;
      city?: string; state?: string;
      priority: 'low' | 'normal' | 'high' | 'urgent';
      client_name?: string; facility_name?: string;
      match_score: number;
      already_submitted: boolean;
    }>;
  }>(`/candidates/${id}/matching-jobs`),
  duplicates: (params: { email?: string; phone?: string; name?: string; exclude_id?: string }) =>
    api.get<{
      candidates: Array<{ id: string; first_name: string; last_name: string; email?: string; phone?: string; role?: string; stage: string; status: string; created_at: string }>;
      match_count: number;
    }>('/candidates/duplicates', { params }),
  // QA Phase 4 — candidate → staff conversion
  convertToStaff: (id: string) =>
    api.post<{ staff_id: string; created: boolean }>(`/candidates/${id}/convert-to-staff`),
  // ATS Phase 5 — AI outreach
  aiSmsOutreach: (id: string, job_id?: string) =>
    api.post<{ message: string }>(`/candidates/${id}/ai/sms-outreach`, { job_id }),
  aiRecruiterSummary: (id: string, job_id?: string) =>
    api.post<{ summary: string }>(`/candidates/${id}/ai/recruiter-summary`, { job_id }),
  aiClientSummary: (id: string, job_id?: string) =>
    api.post<{ summary: string }>(`/candidates/${id}/ai/client-summary`, { job_id }),
  // AI phone caller — call-consent (TCPA gate)
  recordCallConsent: (id: string, body: { action: 'granted' | 'revoked'; source?: string; phone?: string; note?: string }) =>
    api.post<{ consent: CallConsent }>(`/candidates/${id}/call-consent`, body),
  consentLog: (id: string) =>
    api.get<{ log: Array<{ id: string; action: string; source: string | null; phone: string | null; created_at: string }> }>(`/candidates/${id}/consent-log`),
  callableCandidates: () =>
    api.get<{ candidates: unknown[]; count: number }>(`/candidates/call-consent/callable`),
  // ATS Phase 2 — multi-channel outreach drafts + log
  aiOutreach: (id: string, job_id?: string) =>
    api.post<{ drafts: OutreachDrafts; logged: OutreachLogEntry[] }>(`/candidates/${id}/ai/outreach`, { job_id }),
  getOutreachLog: (id: string) =>
    api.get<{ outreach: OutreachLogEntry[] }>(`/candidates/${id}/outreach-log`),
  updateOutreachLog: (id: string, logId: string, status: 'draft' | 'sent' | 'replied' | 'bounced') =>
    api.patch<{ outreach: OutreachLogEntry }>(`/candidates/${id}/outreach-log/${logId}`, { status }),
};

// ── AI resume search + qualification analysis ───────────────────────────────
export interface ResumeSearchPlan {
  names: string[]; role: string | null; specialties: string[];
  state: string | null; city: string | null; min_years: number | null;
  keywords: string[]; summary: string;
}
export interface ResumeSearchHit {
  id: string; first_name: string; last_name: string; role: string | null;
  city: string | null; state: string | null; years_experience: number | null;
  specialties: string[]; resume_file_id: string; resume_filename: string;
}
export type QualifyVerdict = 'qualified' | 'not_qualified' | 'maybe' | 'unknown';
export interface QualifyResult {
  candidate_id: string; name: string; verdict: QualifyVerdict; summary: string;
  met: string[]; missing: string[]; years_experience: number | null;
  ai_configured: boolean; resume_filename: string | null;
}

export const aiResumeApi = {
  /** Plain-English request → candidates with a resume on file (resumes only). */
  search: (prompt: string) =>
    api.post<{ plan: ResumeSearchPlan; results: ResumeSearchHit[]; note?: string }>(
      '/ai-resume/search', { prompt }),
  /** Scan selected candidates' resumes against a position's requirements. */
  analyze: (candidate_ids: string[], position?: string) =>
    api.post<{ position: string; results: QualifyResult[] }>(
      '/ai-resume/analyze', { candidate_ids, position }),
};

export interface OutreachDrafts {
  linkedin_inmail: { subject: string; body: string };
  email: { subject: string; body: string };
  sms: { body: string };
}
export interface OutreachLogEntry {
  id: string;
  candidate_id: string;
  job_id: string | null;
  channel: 'linkedin' | 'email' | 'sms';
  subject: string | null;
  body: string;
  status: 'draft' | 'sent' | 'replied' | 'bounced';
  created_at: string;
}
// ─── OIG / LEIE exclusion screening ────────────────────────────────────────
export interface OigStatus {
  loaded: boolean;
  row_count: number;
  last_imported_at: string | null;
  stale: boolean;
  last_run: { id: string; status: string; row_count: number; error: string | null; created_at: string } | null;
}
export interface OigMatch {
  id: string;
  lastname: string | null;
  firstname: string | null;
  midname: string | null;
  general: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
  excltype: string | null;
  excldate: string | null;
}
export interface OigScreen {
  status: 'clear' | 'review' | 'no_data';
  matches: OigMatch[];
  lastNameMatchCount: number;
  candidate?: { first_name: string; last_name: string };
}
export const oigApi = {
  status: () => api.get<OigStatus>('/oig/status'),
  refresh: () => api.post<{ ok: boolean; rowCount: number }>('/oig/refresh'),
  checkCandidate: (id: string) => api.get<OigScreen>(`/oig/candidates/${id}/check`),
};

// Public, token-gated candidate upload API. Called from /upload/:token —
// the token IS the auth, so these requests are sent without a bearer.
// We use the same axios instance so the base URL + interceptors apply,
// but don't pass any auth-required helpers here.
export const candidateUploadPublicApi = {
  // Fetch the landing-page metadata. 404 means the token is bad,
  // expired, revoked, or exhausted — the page renders the error.
  info: (token: string) =>
    api.get<CandidateUploadLinkPublic>(`/uploads/${token}`),
  // Fetch the required-document checklist for the candidate behind
  // this token. Drives the per-item rows on the public upload page.
  checklist: (token: string) =>
    api.get<{ items: CandidateUploadChecklistItem[] }>(`/uploads/${token}/checklist`),
  upload: (
    token: string,
    file: File,
    opts?: {
      document_type?: string;
      label?: string;
      // When supplied, the upload attaches to an existing checklist
      // row instead of creating a new candidate_documents record.
      document_id?: string;
      // Optional expiration. Empty / undefined / 'N/A' all mean
      // "candidate doesn't know" — backend stores NULL.
      expiry_date?: string;
    },
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (opts?.document_type) form.append('document_type', opts.document_type);
    if (opts?.label)         form.append('label', opts.label);
    if (opts?.document_id)   form.append('document_id', opts.document_id);
    if (opts?.expiry_date)   form.append('expiry_date', opts.expiry_date);
    return api.post<{ ok: true; document_id: string; filename: string; size: number }>(
      `/uploads/${token}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
};

// Candidate-kanban types — Phase 1.4 Pipeline rewrite with drag-drop.
export interface PipelineCandidateCard {
  id: string;
  first_name: string;
  last_name: string;
  role: string | null;
  stage: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  available_shifts: string[] | null;
  desired_pay_rate: number | null;
  specialties: string[] | null;
  years_experience: number | null;
  recruiter_name: string | null;
  days_in_stage: number;
  is_stale: boolean;
  submitted_job_ids: string[];
  missing_docs_count: number;
  updated_at: string;
}

export interface PipelineStageColumn {
  key: string;
  label: string;
  color: string;
  sort_order: number;
  is_terminal: boolean;
  stale_after_days: number | null;
  items: PipelineCandidateCard[];
  count: number;
}

// Simple user list for assignee dropdowns etc.
// `id` is the Azure oid (used in PATCH /users/:id); `db_id` is the
// users table primary key (the value stored in foreign keys like
// candidates.assigned_recruiter_id).
export interface OrgUser {
  id: string;
  db_id: string;
  clerk_user_id?: string;
  email: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  name?: string | null;
  role: string;
}
export const usersApi = {
  // Full user list with email + timestamps. Admin-only — backend requires
  // admin.users.manage. Use this only in admin screens (Team Access etc.).
  list: () => api.get<{ users: OrgUser[] }>('/users'),
  // Minimal team directory — id + name + role only. Available to any
  // signed-in user so "assign to" dropdowns work on HR / Recruiter /
  // Compliance pages without needing admin perms.
  directory: () => api.get<{ users: OrgUser[] }>('/users/directory'),
  // Add a teammate: creates their account with the chosen role and emails
  // them a sign-in link. `email_sent` is honest — the person has access
  // even when mail fails, so the UI should say "added, but email didn't go".
  invite: (body: { email: string; name?: string; role: string }) =>
    api.post<{
      success: boolean;
      user: { id: string; db_id: string; email: string; role: string; name: string | null };
      created: boolean;
      email_sent: boolean;
      email_error: string | null;
    }>('/users/invite', body),
};

// Phase 1.1B + 1.1C — direct SMS send (no approval flow) for recruiter
// outbound texting. Approval-flow endpoints on smsApi are unchanged.
export const textingApi = {
  sendDirect: (data: {
    recipient_phone: string;
    message: string;
    reference_id?: string | null;
    reference_type?: string | null;
  }) => api.post<{ success: boolean; messageId: string; status: string }>('/sms/send-direct', data),
};

export interface TrackerBoardCard {
  id: string;
  first_name?: string;
  last_name?: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  stage: string | null;
  stored_stage: string;
  view_stage: string | null;
  view_mapped: boolean;
  days_in_stage?: number | null;
  is_stale?: boolean;
  recruiter_name?: string | null;
  requires_jail_application?: boolean;
}

export interface TrackerBoard {
  mode: 'fns' | 'fallback';
  writes: number;
  total: number;
  stages: Array<{
    key: string;
    label: string;
    color: string | null;
    sort_order: number;
    stale_after_days: number | null;
    count: number;
    items: TrackerBoardCard[];
  }>;
  other: { key: string; label: string; count: number; items: TrackerBoardCard[] };
  lists: {
    bg_pending: { key: string; label: string; count: number; items: TrackerBoardCard[] };
    waiting_on_client: { key: string; label: string; count: number; items: TrackerBoardCard[] };
  };
}

export interface TrackerMappingReport {
  writes: number;
  mode: 'fns' | 'fallback';
  note?: string;
  total: number;
  mapped: number;
  native: number;
  unmapped: number;
  by_stored_stage: Record<string, { count: number; view_stage: string | null; reason: string }>;
  proposals: Array<{ candidate_id: string; name: string; stored_stage: string; view_stage: string | null; mapped: boolean; reason: string }>;
}

export const pipelineApi = {
  overview: () => api.get<{ stages: Record<string, Candidate[]>; total: number }>('/pipeline/overview'),
  candidatesKanban: () =>
    api.get<{ stages: PipelineStageColumn[]; total: number }>('/pipeline/candidates-kanban'),
  metrics: () => api.get('/pipeline/metrics'),
  tracker: () => api.get<TrackerBoard>('/pipeline/tracker'),
  trackerMappingReport: () => api.get<TrackerMappingReport>('/pipeline/tracker/mapping-report'),
};

export const remindersApi = {
  list: (params?: { status?: string; candidate_id?: string; type?: string }) =>
    api.get<{ reminders: Reminder[] }>('/reminders', { params }),
  create: (data: Partial<Reminder>) => api.post<Reminder>('/reminders', data),
  update: (id: string, data: Partial<Reminder>) => api.put<Reminder>(`/reminders/${id}`, data),
  // Phase 1.6B+C — AI drafts a subject+message given an optional candidate
  // and topic. If candidate_id is provided the backend pulls their missing
  // docs / stale stage info into the prompt so the message is specific.
  aiDraft: (data: { candidate_id?: string | null; topic?: string; type?: 'email' | 'sms' | 'both' }) =>
    api.post<{ subject: string; message: string }>('/reminders/ai-draft', data),
  cancel: (id: string) => api.delete(`/reminders/${id}`),
  send: (id: string) => api.post(`/reminders/${id}/send`),
  autoGenerate: () => api.post<{ success: boolean; generated: number }>('/reminders/auto-generate'),
};

// ─── Compliance ───────────────────────────────────────────────────────────────

export interface CompCategory {
  id: string;
  level: number;
  name: string;
  parent_id: string | null;
  sort_order: number;
}

export interface CompPolicy {
  id: string;
  title: string;
  content: string;
  version: string;
  expiration_days: number | null;
  require_signature: boolean;
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CompDocument {
  id: string;
  title: string;
  description: string | null;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  expiration_days: number | null;
  require_read_ack: boolean;
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CompetencyRecord {
  id: string;
  user_clerk_id: string;
  staff_id: string | null;
  candidate_id: string | null;
  item_type: 'policy' | 'document' | 'exam' | 'checklist' | 'bundle';
  item_id: string;
  title: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'expired' | 'failed' | 'signed' | 'read';
  assigned_date: string;
  started_date: string | null;
  completed_date: string | null;
  due_date: string | null;
  expiration_date: string | null;
  score: number | null;
  ceus: number;
  attempts_used: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export const complianceApi = {
  // Categories
  getCategories: (level?: number) =>
    api.get<CompCategory[]>('/compliance/categories', { params: level ? { level } : {} }),
  createCategory: (data: { level: number; name: string; parent_id?: string; sort_order?: number }) =>
    api.post<CompCategory>('/compliance/categories', data),
  updateCategory: (id: string, data: { name?: string; sort_order?: number }) =>
    api.put<CompCategory>(`/compliance/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/compliance/categories/${id}`),

  // Policies
  getPolicies: (params?: { status?: string; cat1_id?: string }) =>
    api.get<{ policies: CompPolicy[] }>('/compliance/policies', { params }),
  getPolicy: (id: string) => api.get<{ policy: CompPolicy }>(`/compliance/policies/${id}`),
  createPolicy: (data: Partial<CompPolicy>) => api.post<{ policy: CompPolicy }>('/compliance/policies', data),
  updatePolicy: (id: string, data: Partial<CompPolicy>) => api.put<{ policy: CompPolicy }>(`/compliance/policies/${id}`, data),
  archivePolicy: (id: string) => api.delete(`/compliance/policies/${id}`),
  signPolicy: (id: string, typed_signature: string) =>
    api.post<{ success: boolean; signature_id: string; competency_record_id: string }>(`/compliance/policies/${id}/sign`, { typed_signature }),
  assignPolicy: (id: string, user_clerk_ids: string[], due_date?: string) =>
    api.post(`/compliance/policies/${id}/assign`, { user_clerk_ids, due_date }),

  // Documents
  getDocuments: (params?: { status?: string; cat1_id?: string }) =>
    api.get<{ documents: CompDocument[] }>('/compliance/documents', { params }),
  getDocument: (id: string) => api.get<{ document: CompDocument }>(`/compliance/documents/${id}`),
  createDocument: (data: Partial<CompDocument>) => api.post<{ document: CompDocument }>('/compliance/documents', data),
  updateDocument: (id: string, data: Partial<CompDocument>) => api.put<{ document: CompDocument }>(`/compliance/documents/${id}`, data),
  archiveDocument: (id: string) => api.delete(`/compliance/documents/${id}`),
  readDocument: (id: string) =>
    api.post<{ success: boolean }>(`/compliance/documents/${id}/read`),
  assignDocument: (id: string, user_clerk_ids: string[], due_date?: string) =>
    api.post(`/compliance/documents/${id}/assign`, { user_clerk_ids, due_date }),

  // Competency Records
  getRecords: (params?: { mine?: boolean; item_type?: string; status?: string }) =>
    api.get<{ records: CompetencyRecord[] }>('/compliance/competency-records', { params }),
  getUserRecords: (userId: string) =>
    api.get<{ records: CompetencyRecord[] }>(`/compliance/competency-records/user/${userId}`),
  createRecord: (data: Partial<CompetencyRecord>) =>
    api.post<{ record: CompetencyRecord }>('/compliance/competency-records', data),
  updateRecord: (id: string, data: { status?: string; notes?: string; due_date?: string; score?: number }) =>
    api.patch<{ record: CompetencyRecord }>(`/compliance/competency-records/${id}`, data),
  addNote: (id: string, content: string) =>
    api.post(`/compliance/competency-records/${id}/notes`, { content }),
  getNotes: (id: string) =>
    api.get<{ notes: Array<{ id: string; author_clerk_id: string; content: string; created_at: string }> }>(`/compliance/competency-records/${id}/notes`),

  // Stats
  getStats: () =>
    api.get<{
      policies: { total: number; published: number; draft: number };
      documents: { total: number; published: number; draft: number };
      competency_records: Record<string, number>;
    }>('/compliance/stats'),
};

// ─── Compliance Phase 2 ──────────────────────────────────────────────────────

export interface CompExam {
  id: string;
  title: string;
  description: string | null;
  instructions: string | null;
  passing_score: number;
  max_attempts: number;
  expiration_type: 'one_time' | 'yearly' | 'bi_annual';
  time_limit_minutes: number | null;
  randomize_questions: boolean;
  question_count: number;
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  ceus: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CompExamQuestion {
  id: string;
  exam_id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false';
  sort_order: number;
  answers: CompExamAnswer[];
}

export interface CompExamAnswer {
  id: string;
  question_id: string;
  answer_text: string;
  is_correct: boolean;
  sort_order: number;
}

export interface CompChecklist {
  id: string;
  title: string;
  description: string | null;
  mode: 'skills' | 'questionnaire';
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CompBundle {
  id: string;
  title: string;
  description: string | null;
  sequential: boolean;
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  facility_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const complianceExamsApi = {
  list: (params?: { status?: string; cat1_id?: string }) =>
    api.get<{ exams: CompExam[] }>('/compliance/exams', { params }),
  stats: () => api.get<{ total: number; published: number; draft: number; total_attempts: number; passed_attempts: number; failed_attempts: number }>('/compliance/exams/stats'),
  get: (id: string) => api.get<{ exam: CompExam; questions: CompExamQuestion[] }>(`/compliance/exams/${id}`),
  create: (data: Partial<CompExam>) => api.post<{ exam: CompExam }>('/compliance/exams', data),
  update: (id: string, data: Partial<CompExam>) => api.put<{ exam: CompExam }>(`/compliance/exams/${id}`, data),
  archive: (id: string) => api.delete(`/compliance/exams/${id}`),
  addQuestion: (id: string, data: { question_text: string; question_type: string; sort_order?: number }) =>
    api.post<{ question: CompExamQuestion }>(`/compliance/exams/${id}/questions`, data),
  updateQuestion: (id: string, qid: string, data: Partial<CompExamQuestion>) =>
    api.put(`/compliance/exams/${id}/questions/${qid}`, data),
  deleteQuestion: (id: string, qid: string) => api.delete(`/compliance/exams/${id}/questions/${qid}`),
  addAnswer: (id: string, qid: string, data: { answer_text: string; is_correct: boolean; sort_order?: number }) =>
    api.post<{ answer: CompExamAnswer }>(`/compliance/exams/${id}/questions/${qid}/answers`, data),
  updateAnswer: (id: string, qid: string, aid: string, data: Partial<CompExamAnswer>) =>
    api.put(`/compliance/exams/${id}/questions/${qid}/answers/${aid}`, data),
  deleteAnswer: (id: string, qid: string, aid: string) =>
    api.delete(`/compliance/exams/${id}/questions/${qid}/answers/${aid}`),
  startAttempt: (id: string) =>
    api.post<{ attempt_id: string; attempt_number: number; attempts_remaining: number; exam: Partial<CompExam>; questions: any[] }>(`/compliance/exams/${id}/start`),
  submitAttempt: (id: string, data: { attempt_id: string; answers: Array<{ question_id: string; answer_id: string }> }) =>
    api.post<{ score: number; passed: boolean; attempt_number: number; attempts_used: number; attempts_remaining: number; passing_score: number; message: string }>(`/compliance/exams/${id}/submit`, data),
  getMyAttempts: (id: string) => api.get<{ attempts: any[] }>(`/compliance/exams/${id}/attempts`),
};

export const complianceChecklistsApi = {
  list: (params?: { status?: string }) =>
    api.get<{ checklists: CompChecklist[] }>('/compliance/checklists', { params }),
  stats: () => api.get<{ total: number; published: number; draft: number; total_submissions: number }>('/compliance/checklists/stats'),
  get: (id: string) => api.get<{ checklist: CompChecklist; sections: any[] }>(`/compliance/checklists/${id}`),
  create: (data: Partial<CompChecklist>) => api.post<{ checklist: CompChecklist }>('/compliance/checklists', data),
  update: (id: string, data: Partial<CompChecklist>) => api.put<{ checklist: CompChecklist }>(`/compliance/checklists/${id}`, data),
  archive: (id: string) => api.delete(`/compliance/checklists/${id}`),
  addSection: (id: string, data: { title: string; sort_order?: number }) =>
    api.post<{ section: any }>(`/compliance/checklists/${id}/sections`, data),
  updateSection: (id: string, sid: string, data: { title?: string; sort_order?: number }) =>
    api.put(`/compliance/checklists/${id}/sections/${sid}`, data),
  deleteSection: (id: string, sid: string) => api.delete(`/compliance/checklists/${id}/sections/${sid}`),
  addSkill: (id: string, sid: string, data: { skill_name: string; description?: string; exclude_from_score?: boolean; sort_order?: number }) =>
    api.post<{ skill: any }>(`/compliance/checklists/${id}/sections/${sid}/skills`, data),
  updateSkill: (id: string, sid: string, kid: string, data: any) =>
    api.put(`/compliance/checklists/${id}/sections/${sid}/skills/${kid}`, data),
  deleteSkill: (id: string, sid: string, kid: string) =>
    api.delete(`/compliance/checklists/${id}/sections/${sid}/skills/${kid}`),
  submit: (id: string, data: { ratings: Array<{ skill_id: string; rating: number; notes?: string }> }) =>
    api.post<{ submission_id: string; overall_score: number; message: string }>(`/compliance/checklists/${id}/submit`, data),
  getMySubmission: (id: string) => api.get<{ submission: any; ratings: any[] }>(`/compliance/checklists/${id}/my-submission`),
};

export const complianceBundlesApi = {
  list: (params?: { status?: string }) =>
    api.get<{ bundles: CompBundle[] }>('/compliance/bundles', { params }),
  stats: () => api.get<{ total: number; published: number; draft: number; total_assignments: number }>('/compliance/bundles/stats'),
  get: (id: string) => api.get<{ bundle: CompBundle; items: any[]; rules: any[] }>(`/compliance/bundles/${id}`),
  create: (data: Partial<CompBundle>) => api.post<{ bundle: CompBundle }>('/compliance/bundles', data),
  update: (id: string, data: Partial<CompBundle>) => api.put<{ bundle: CompBundle }>(`/compliance/bundles/${id}`, data),
  archive: (id: string) => api.delete(`/compliance/bundles/${id}`),
  addItem: (id: string, data: { item_type: string; item_id: string; item_title: string; sort_order?: number; required?: boolean }) =>
    api.post<{ item: any }>(`/compliance/bundles/${id}/items`, data),
  updateItem: (id: string, iid: string, data: { sort_order?: number; required?: boolean }) =>
    api.put(`/compliance/bundles/${id}/items/${iid}`, data),
  deleteItem: (id: string, iid: string) => api.delete(`/compliance/bundles/${id}/items/${iid}`),
  addRule: (id: string, data: { rule_type: string; role?: string; specialty?: string; onboarding_stage?: string; priority?: number }) =>
    api.post<{ rule: any }>(`/compliance/bundles/${id}/rules`, data),
  deleteRule: (id: string, rid: string) => api.delete(`/compliance/bundles/${id}/rules/${rid}`),
  assign: (id: string, data: { user_clerk_ids: string[]; due_date?: string }) =>
    api.post<{ created: number; skipped: number }>(`/compliance/bundles/${id}/assign`, data),
};

// ═══════════════════════════════════════════════════════════════════════════
// Phase 2 additions — doc types admin, policy AI, exam/checklist AI + bulk,
// courses, unified my-compliance rollup.
// ═══════════════════════════════════════════════════════════════════════════

// Phase 2.2 — admin-defined document types
export interface DocType {
  id: string;
  key: string;
  label: string;
  description: string | null;
  prompt_hints: string;
  issuing_bodies: string[];
  expires_months: number | null;
  category: string | null;
  required_fields: string[];
  applicable_roles: string[];
  active: boolean;
  created_at: string;
  updated_at: string;
}

export const docTypesApi = {
  list: (params?: { active?: 'true' | 'false' | 'all'; category?: string }) =>
    api.get<{ doc_types: DocType[] }>('/doc-types', { params }),
  get: (key: string) => api.get<DocType>(`/doc-types/${key}`),
  create: (data: Partial<DocType>) => api.post<DocType>('/doc-types', data),
  update: (id: string, data: Partial<DocType>) => api.put<DocType>(`/doc-types/${id}`, data),
  remove: (id: string) => api.delete(`/doc-types/${id}`),
};

// Phase 2.3 — policy AI
export interface ParsedPolicy {
  title?: string;
  content?: string;
  suggested_version?: string;
  suggested_expiration_days?: number;
  require_signature?: boolean;
  applicable_roles?: string[];
  category_guess?: string;
  summary?: string;
}

export const policyAiApi = {
  parse: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ parsed: ParsedPolicy; file: { name: string; size: number; mime: string } }>(
      '/compliance/policies/ai-parse', form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  rewrite: (data: { title?: string; content: string; instruction: string }) =>
    api.post<{ revised_content: string }>('/compliance/policies/ai-rewrite', data),
};

// Phase 2.4 — exams AI + bulk
export interface GeneratedExamQuestion {
  question_text: string;
  question_type: 'multiple_choice' | 'true_false';
  explanation?: string;
  answers: Array<{ answer_text: string; is_correct: boolean }>;
}

export const examsAiApi = {
  generate: (examId: string, data: {
    topic: string; count?: number; difficulty?: 'easy' | 'medium' | 'hard';
    question_types?: Array<'multiple_choice' | 'true_false'>;
  }) =>
    api.post<{ questions: GeneratedExamQuestion[] }>(`/compliance/exams/${examId}/ai-generate`, data),
  bulkImport: (examId: string, questions: GeneratedExamQuestion[]) =>
    api.post<{
      inserted_count: number;
      inserted: Array<{ id: string; question_text: string }>;
      skipped_count: number;
      skipped: string[];
    }>(`/compliance/exams/${examId}/bulk-import`, { questions }),
};

// Phase 2.5 — checklists AI + bulk
export interface GeneratedChecklistSection {
  title: string;
  skills: Array<{ skill_name: string; description?: string | null }>;
}

export const checklistsAiApi = {
  generate: (checklistId: string, data: {
    topic: string; role?: string; sections_count?: number; skills_per_section?: number;
  }) =>
    api.post<{ sections: GeneratedChecklistSection[] }>(`/compliance/checklists/${checklistId}/ai-generate`, data),
  bulkImport: (checklistId: string, sections: GeneratedChecklistSection[]) =>
    api.post<{
      sections_created: number;
      skills_created_total: number;
      created: Array<{ section_id: string; title: string; skills_created: number }>;
    }>(`/compliance/checklists/${checklistId}/bulk-import`, { sections }),
};

// Phase 2.6 — courses
export interface CompCourse {
  id: string;
  title: string;
  description: string | null;
  content_markdown: string | null;
  video_url: string | null;
  estimated_minutes: number | null;
  quiz_exam_id: string | null;
  quiz_title?: string | null;
  pass_threshold: number | null;
  require_attestation: boolean;
  status: 'draft' | 'published' | 'archived';
  cat1_id: string | null;
  cat2_id: string | null;
  cat3_id: string | null;
  applicable_roles: string[];
  completions_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CourseCompletion {
  id: string;
  course_id: string;
  user_clerk_id: string;
  started_at: string | null;
  completed_at: string | null;
  duration_seconds: number;
  attestation_signed: boolean;
  attestation_signed_at: string | null;
  attestation_signer_name: string | null;
  quiz_attempt_id: string | null;
  quiz_score: number | null;
  passed: boolean | null;
}

export const compCoursesApi = {
  list: (params?: { status?: string; cat1_id?: string }) =>
    api.get<{ courses: CompCourse[] }>('/compliance/courses', { params }),
  get: (id: string) => api.get<{ course: CompCourse & { quiz_pass_threshold?: number } }>(`/compliance/courses/${id}`),
  create: (data: Partial<CompCourse>) => api.post<{ course: CompCourse }>('/compliance/courses', data),
  update: (id: string, data: Partial<CompCourse>) => api.put<{ course: CompCourse }>(`/compliance/courses/${id}`, data),
  remove: (id: string) => api.delete(`/compliance/courses/${id}`),
  start: (id: string) => api.post<{ completion: CourseCompletion }>(`/compliance/courses/${id}/start`),
  complete: (id: string, data: {
    duration_seconds?: number;
    attestation_signed?: boolean;
    signer_name?: string;
    quiz_score?: number;
    quiz_attempt_id?: string;
  }) => api.post<{ completion: CourseCompletion }>(`/compliance/courses/${id}/complete`, data),
  myProgress: (id: string) =>
    api.get<{ completion: CourseCompletion | null }>(`/compliance/courses/${id}/my-progress`),
};

// Phase 2.1 + 2.7 — unified /my-all rollup
export interface MyComplianceRollup {
  user_clerk_id: string;
  summary: {
    total: number;
    completed: number;
    in_progress: number;
    overdue: number;
    not_started: number;
  };
  competency: Array<{
    id: string; item_type: string; item_id: string; title: string;
    status: string; assigned_date: string; due_date: string | null;
    expiration_date: string | null; completed_date: string | null;
    score: number | null; ceus: number | null;
  }>;
  courses: Array<{
    completion_id: string; course_id: string; title: string;
    description: string | null; estimated_minutes: number | null;
    require_attestation: boolean; course_status: string;
    started_at: string | null; completed_at: string | null;
    duration_seconds: number;
    attestation_signed: boolean;
    quiz_score: number | null; passed: boolean | null;
  }>;
}

export const myComplianceApi = {
  rollup: (user_clerk_id?: string) =>
    api.get<MyComplianceRollup>('/compliance/my-all', {
      params: user_clerk_id ? { user_clerk_id } : {},
    }),
};

// ─── Compliance Phase 3 ──────────────────────────────────────────────────────

export type JCSignal = 'green' | 'yellow' | 'red' | 'unknown';
export interface JCReadinessRow {
  staff_id: string;
  name: string;
  role: string | null;
  facility_name: string | null;
  readiness_score: number | null;
  categories: {
    license: JCSignal;
    certifications: JCSignal;
    background: JCSignal;
    health: JCSignal;
    competency: JCSignal;
    performance: JCSignal;
    hcss_audit: JCSignal;
  };
  overall: JCSignal;
}
export interface JCManualCheck {
  check_type: string;
  status: 'complete' | 'pending' | 'na';
  checked_on: string | null;
  valid_until: string | null;
  notes: string | null;
  updated_at?: string;
}

export const jcApi = {
  readiness: () =>
    api.get<{ staff: JCReadinessRow[]; summary: { total: number; ready: number; expiring: number; critical: number; unknown: number } }>(
      '/compliance/jc/readiness'),
  staffDetail: (staffId: string) =>
    api.get<{
      staff: { id: string; first_name: string; last_name: string; role: string | null; status: string };
      credentials: Array<{ type: string; issuer: string | null; status: string; issue_date: string | null; expiry_date: string | null; verified_at: string | null; document_url: string | null }>;
      manual_checks: JCManualCheck[];
      hcss_audit: { submitted_at: string; overall_score: number | null } | null;
      manual_check_catalog: string[];
    }>(`/compliance/jc/readiness/${staffId}`),
  saveCheck: (staffId: string, body: { check_type: string; status?: string; checked_on?: string | null; valid_until?: string | null; notes?: string | null }) =>
    api.put<{ check: JCManualCheck }>(`/compliance/jc/checks/${staffId}`, body),
  clearCheck: (staffId: string, checkType: string) =>
    api.delete(`/compliance/jc/checks/${staffId}/${checkType}`),
};

export interface CeoFinancialSummary {
  pipeline: { open_value: number; won_value: number; open_count: number };
  contracts: { active_value: number; active_count: number };
  open_reqs: { weekly_value: number; count: number };
  avg_job_margin_pct: number;
  actual: {
    weekly_revenue: number; monthly_revenue: number;
    weekly_margin: number; monthly_margin: number;
    margin_pct: number; billable_placements: number; total_active_placements: number;
  };
  goals: Record<string, number>;
  period_month: string;
}
export interface OperationalMetrics {
  open_jobs: number;
  active_candidates: number;
  submissions_last_7d: number;
  avg_time_to_fill_days: number | null;
  active_placements: number;
  terminated_last_30d: number;
  active_sequence_enrollments: number;
  compliance_pct: number | null;
  compliance_at_risk_count: number;
  compliance_total_active_staff: number;
}
export const financeApi = {
  ceoSummary: () => api.get<CeoFinancialSummary>('/finance/ceo-summary'),
  setGoal: (body: { metric: string; target_value: number; period_month?: string; notes?: string }) =>
    api.put<{ goal: unknown }>('/finance/goals', body),
  opsMetrics: () => api.get<OperationalMetrics>('/finance/ops-metrics'),
};

// ─── Morning huddle ────────────────────────────────────────────────
export interface HuddleActionItem {
  who: string;
  what: string;
  when?: string;
  notes?: string;
}
export interface HuddleExtracted {
  summary: string;
  decisions: string[];
  questions: string[];
  action_items: HuddleActionItem[];
}
export interface HuddleListItem {
  id: string;
  huddle_date: string;
  title: string | null;
  created_at: string;
  tasks_created: number;
  tasks_done: number;
}
export interface HuddleOpenCommitment {
  huddle_id: string;
  huddle_date: string;
  task_id: string;
  title: string;
  description: string | null;
  due_at: string | null;
  assignee_name: string | null;
}
export interface HuddleReviewCommitment {
  huddle_task_id: string;
  task_id: string;
  title: string;
  assignee_name: string | null;
  due_at: string | null;
  status: 'done' | 'open' | 'overdue' | 'cancelled';
  completed_at: string | null;
  completed_by_name: string | null;
  barrier_note: string | null;
}
export interface HuddleYesterdayReview {
  huddle_id: string;
  huddle_date: string;
  commitments: HuddleReviewCommitment[];
  done_count: number;
  open_count: number;
  overdue_count: number;
}
export interface HuddleEodReport {
  user_id: string;
  report_date: string;
  what_got_done: string | null;
  blockers: string | null;
  tomorrow_focus: string | null;
  author_name: string | null;
  author_email: string;
}
export interface HuddleBrief {
  yesterday: { id: string; huddle_date: string; extracted: HuddleExtracted | null; title: string | null } | null;
  yesterday_review: HuddleYesterdayReview | null;
  open_commitments: HuddleOpenCommitment[];
  eod_reports: { report_date: string | null; reports: HuddleEodReport[] };
}
// ─── Smart candidate matching ──────────────────────────────────────
export interface MatchGap { category: string; gap: string; severity: 'low' | 'medium' | 'high' }
export interface MatchResult {
  candidate_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  role: string | null;
  specialties: string[];
  years_experience: number | null;
  city: string | null;
  state: string | null;
  sql_score: number;
  // Match++ verified signals (computed from records, not by the AI)
  distance_miles?: number | null;
  license_eligibility?: 'direct' | 'compact' | 'none' | 'unknown';
  license_detail?: string;
  expired_credentials?: string[];
  ai_score: number | null;
  fit_label: 'excellent' | 'strong' | 'moderate' | 'weak' | 'poor' | null;
  summary: string | null;
  gaps: MatchGap[];
  scoring_failed: boolean;
}
export interface MatchResponse {
  job: { id: string; title: string; profession: string | null; specialty: string | null; city: string | null; state: string | null };
  matches: MatchResult[];
  candidates_evaluated: number;
  note?: string;
}
export const matchingApi = {
  forJob: (jobId: string, limit = 12) =>
    api.post<MatchResponse>(`/matching/job/${jobId}`, { limit }),
};

// ─── Match Alerts (A1.d + A1.f) ────────────────────────────────────
export interface MatchAlertSettings {
  enabled: boolean;
  mode: 'off' | 'review_first' | 'auto_send';
  min_score: number;
  max_alerts_per_candidate_per_week: number;
  max_alerts_per_job_per_day: number;
  /** Drip enrolled on auto-send. null = single alert email only. */
  followup_sequence_id: string | null;
  updated_at: string;
}
export interface MatchAlertQueueItem {
  id: string;
  candidate_id: string;
  job_id: string;
  ai_score: number | null;
  ai_summary: string | null;
  status: 'pending' | 'sent' | 'skipped';
  created_at: string;
  first_name: string;
  last_name: string;
  email: string | null;
  candidate_role: string | null;
  job_title: string;
  profession: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
}
export const matchAlertsApi = {
  getSettings: () => api.get<{ settings: MatchAlertSettings | null }>('/match-alerts/settings'),
  updateSettings: (body: Partial<MatchAlertSettings>) =>
    api.put<{ settings: MatchAlertSettings }>('/match-alerts/settings', body),
  queue: (status: 'pending' | 'sent' | 'skipped' = 'pending') =>
    api.get<{ items: MatchAlertQueueItem[] }>('/match-alerts/queue', { params: { status } }),
  approve: (id: string) => api.post<{ success: boolean }>(`/match-alerts/queue/${id}/approve`),
  skip: (id: string) => api.post<{ success: boolean }>(`/match-alerts/queue/${id}/skip`),
  run: () => api.post<{ tick: { picked: number; ok: number; failed: number } }>('/match-alerts/run'),
};

// ─── Email sequences (A4) ──────────────────────────────────────────
export interface EmailSequenceSummary {
  id: string; name: string; description: string | null;
  is_active: boolean; step_count: number; active_enrollments: number;
  channel: 'email' | 'sms';
  created_at: string; updated_at: string;
}
export interface StepCondition { field: 'stage' | 'status' | 'role' | 'state'; op: 'eq' | 'neq'; value: string; }
export interface SequenceStep {
  id: string; sequence_id: string; step_order: number;
  day_offset: number; subject: string | null; body: string;
  condition?: StepCondition | null;
}
export interface SequenceEnrollment {
  id: string; sequence_id: string; sequence_name: string;
  candidate_id: string; status: 'active' | 'completed' | 'cancelled';
  started_at: string; cancelled_at: string | null; completed_at: string | null;
  cancel_reason: string | null;
}
export interface CandidateTimelineEvent {
  id: string;
  kind:
    | 'email_sent' | 'email_failed' | 'email_pending'
    | 'sms_sent'   | 'sms_failed'
    | 'stage_change'
    | 'task_created' | 'task_completed'
    | 'sequence_started' | 'sequence_cancelled'
    | 'match_alert'
    | 'document_uploaded';
  occurred_at: string;
  headline: string;
  detail?: string;
  ref?: { type: string; id: string };
}
export const candidateTimelineApi = {
  get: (candidateId: string) =>
    api.get<{ events: CandidateTimelineEvent[] }>(`/candidates/${candidateId}/timeline`),
};

export const sequencesApi = {
  list: (includeInactive = false) =>
    api.get<{ sequences: EmailSequenceSummary[] }>('/sequences', { params: { include_inactive: includeInactive } }),
  get: (id: string) =>
    api.get<{ sequence: EmailSequenceSummary; steps: SequenceStep[] }>(`/sequences/${id}`),
  create: (body: { name: string; description?: string; channel?: 'email' | 'sms' }) =>
    api.post<{ sequence: EmailSequenceSummary }>('/sequences', body),
  update: (id: string, body: Partial<EmailSequenceSummary>) =>
    api.put<{ sequence: EmailSequenceSummary }>(`/sequences/${id}`, body),
  addStep: (id: string, body: { step_order: number; day_offset: number; subject: string; body: string; condition?: StepCondition | null }) =>
    api.post<{ step: SequenceStep }>(`/sequences/${id}/steps`, body),
  updateStep: (stepId: string, body: Partial<SequenceStep>) =>
    api.put<{ step: SequenceStep }>(`/sequences/steps/${stepId}`, body),
  deleteStep: (stepId: string) => api.delete(`/sequences/steps/${stepId}`),
  enroll: (sequenceId: string, candidateId: string) =>
    api.post<{ enrollment_id: string; steps_scheduled: number }>(`/sequences/${sequenceId}/enroll`, { candidate_id: candidateId }),
  enrollBulk: (sequenceId: string, candidateIds: string[]) =>
    api.post<{
      enrolled: number;
      failed: number;
      results: Array<{ candidate_id: string; ok: boolean; error?: string }>;
    }>(`/sequences/${sequenceId}/enroll-bulk`, { candidate_ids: candidateIds }),
  enrollments: (candidateId: string) =>
    api.get<{ enrollments: SequenceEnrollment[] }>('/sequences/enrollments/list', { params: { candidate_id: candidateId } }),
  cancel: (enrollmentId: string, reason?: string) =>
    api.post<{ success: boolean }>(`/sequences/enrollments/${enrollmentId}/cancel`, { reason }),
  // Event triggers
  triggerEvents: () => api.get<{ events: Array<{ key: string; label: string; fields: string[] }> }>('/sequences/triggers/events'),
  listTriggers: () => api.get<{ triggers: SequenceTrigger[] }>('/sequences/triggers'),
  createTrigger: (body: { sequence_id: string; event: string; condition?: Record<string, string> }) =>
    api.post<{ trigger: SequenceTrigger }>('/sequences/triggers', body),
  updateTrigger: (id: string, body: { enabled?: boolean; condition?: Record<string, string> }) =>
    api.put<{ trigger: SequenceTrigger }>(`/sequences/triggers/${id}`, body),
  deleteTrigger: (id: string) => api.delete(`/sequences/triggers/${id}`),
};

export interface SequenceTrigger {
  id: string; sequence_id: string; event: string; condition: Record<string, string>;
  enabled: boolean; sequence_name?: string; sequence_channel?: string; created_at: string;
}

// ─── Scheduler admin ────────────────────────────────────────────────
export type SchedulerJobStatus = 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
export interface SchedulerJob {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  run_at: string;
  status: SchedulerJobStatus;
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  last_run_at: string | null;
  related_type: string | null;
  related_id: string | null;
  created_at: string;
  updated_at: string;
}
export interface SchedulerStats {
  counts: Record<SchedulerJobStatus, number>;
  next_run_at: string | null;
  registered_kinds: string[];
}
export interface WorkflowEvent {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  source: string | null;
  related_type: string | null;
  related_id: string | null;
  created_at: string;
}
export interface AutomationSettings {
  global_outbound_enabled: boolean;
  settings: Record<string, boolean>;   // kind -> enabled (only explicitly-toggled kinds)
  recurring_kinds: string[];
  registered_kinds: string[];
  outbound_kinds: string[];
}
export const schedulerApi = {
  listJobs: (params?: { kind?: string; status?: SchedulerJobStatus; limit?: number }) =>
    api.get<{ jobs: SchedulerJob[]; registered_kinds: string[] }>('/scheduler/jobs', { params }),
  stats: () => api.get<SchedulerStats>('/scheduler/jobs/stats'),
  cancel: (id: string) => api.post<{ success: boolean }>(`/scheduler/jobs/${id}/cancel`),
  retry: (id: string) => api.post<{ success: boolean }>(`/scheduler/jobs/${id}/retry`),
  tick: () => api.post<{ picked: number; ok: number; failed: number; skipped: number }>('/scheduler/tick'),
  testEmail: (body: { to: string; subject: string; body: string }) =>
    api.post<{ job_id: string; message: string }>('/scheduler/jobs/test-email', body),
  listEvents: (params?: { type?: string; limit?: number }) =>
    api.get<{ events: WorkflowEvent[]; subscribed_types: string[] }>('/scheduler/events', { params }),
  fireEvent: (body: { type: string; payload?: Record<string, unknown>; related_type?: string; related_id?: string }) =>
    api.post<{ success: boolean }>('/scheduler/events/fire', body),
  kinds: () =>
    api.get<{ kinds: Array<{ kind: string; last_run_at: string | null; next_run_at: string | null; last_status: string | null }> }>('/scheduler/kinds'),
  runNow: (kind: string) =>
    api.post<{ success: boolean; job_id: string; note: string }>('/scheduler/run-now', { kind }),
  // T1 pause switches — per-automation + global "Pause all outbound".
  automations: () => api.get<AutomationSettings>('/scheduler/automations'),
  setAutomation: (kind: string, enabled: boolean) =>
    api.put<{ success: boolean }>(`/scheduler/automations/${encodeURIComponent(kind)}`, { enabled }),
};

// ─── In-app notification feed (the topbar bell) ─────────────────────────────
export interface AppNotification {
  id: string; category: string; title: string; body: string | null; link: string | null;
  read_at: string | null; created_at: string;
}
export const notificationsApi = {
  feed: () => api.get<{ notifications: AppNotification[]; unread: number }>('/notifications'),
  markRead: (id: string) => api.post<{ success: boolean }>(`/notifications/${id}/read`),
  markAllRead: () => api.post<{ success: boolean }>('/notifications/read-all'),
};

export const huddleApi = {
  brief:   () => api.get<HuddleBrief>('/huddle/brief'),
  extract: (transcript: string, context_note?: string) =>
    api.post<{ extracted: HuddleExtracted }>('/huddle/extract', { transcript, context_note }),
  save:    (body: { huddle_date?: string; title?: string; transcript: string; extracted: HuddleExtracted; approved_indices?: number[] }) =>
    api.post<{ huddle_id: string; tasks_created: number; tasks: { task_id: string; index: number; assigned_to: string | null }[] }>('/huddle', body),
  list:    () => api.get<{ huddles: HuddleListItem[] }>('/huddle'),
  get:     (id: string) =>
    api.get<{ huddle: { id: string; huddle_date: string; title: string | null; transcript: string; extracted: HuddleExtracted | null; created_by_name: string | null }; tasks: { action_item_index: number; id: string; title: string; status: string; due_at: string | null; assignee_name: string | null }[] }>(`/huddle/${id}`),
  setBarrier: (huddle_task_id: string, barrier_note: string | null) =>
    api.patch<{ huddle_task_id: string; task_id: string; barrier_note: string | null }>(`/huddle/commitments/${huddle_task_id}/barrier`, { barrier_note }),
};

// ─── Today's focus (personalized priority list) ───────────────────
export interface TodayTaskItem {
  id: string;
  title: string;
  task_type: string | null;
  due_at: string;
  candidate_id: string | null;
  job_id: string | null;
  is_private: boolean;
  candidate_name: string | null;
  job_title: string | null;
}
export interface TodayInterviewItem {
  id: string;
  interview_scheduled_at: string;
  candidate_id: string;
  candidate_name: string;
  job_id: string;
  job_title: string;
  client_name: string | null;
}
export interface TodayStalledItem {
  id: string;
  candidate_name: string;
  last_activity: string;
  days_stalled: number;
  stage: string | null;
}
export interface TodayFocus {
  overdue_tasks: TodayTaskItem[];
  due_today_tasks: TodayTaskItem[];
  interviews_today: TodayInterviewItem[];
  stalled_candidates: TodayStalledItem[];
  eod_filed: boolean;
  generated_at: string;
}
export const meApi = {
  today: () => api.get<TodayFocus>('/me/today'),
};

// ─── End-of-day report ─────────────────────────────────────────────
export interface EodReport {
  id: string;
  user_id: string;
  report_date: string;
  what_got_done: string | null;
  blockers: string | null;
  tomorrow_focus: string | null;
  created_at: string;
  updated_at: string;
}
// ─── Candidate credentials (A2.b) ───────────────────────────────────
export interface CandidateCredential {
  id: string;
  candidate_id: string;
  type: string;
  issuer: string | null;
  state_or_jurisdiction: string | null;
  identifier: string | null;
  issue_date: string | null;
  expiry_date: string | null;
  status: 'valid' | 'expiring_soon' | 'expired' | 'pending' | 'missing' | 'verified';
  document_url: string | null;
  notes: string | null;
  verified_at: string | null;
}

export const candidateCredentialsApi = {
  list: (candidateId: string) =>
    api.get<{ credentials: CandidateCredential[] }>(`/candidates/${candidateId}/credentials`),
  add: (candidateId: string, body: Partial<CandidateCredential>) =>
    api.post<{ credential: CandidateCredential }>(`/candidates/${candidateId}/credentials`, body),
  update: (candidateId: string, credId: string, body: Partial<CandidateCredential>) =>
    api.put<{ credential: CandidateCredential }>(`/candidates/${candidateId}/credentials/${credId}`, body),
  remove: (candidateId: string, credId: string) =>
    api.delete(`/candidates/${candidateId}/credentials/${credId}`),
  verify: (candidateId: string, credId: string) =>
    api.post<{ credential: CandidateCredential }>(`/candidates/${candidateId}/credentials/${credId}/verify`),
  expiring: (days = 30) =>
    api.get<{ rows: Array<{
      candidate_id: string; first_name: string; last_name: string;
      type: string; expiry_date: string; status: string; days_until: number;
    }>; window_days: number }>(`/candidate-credentials/expiring?days=${days}`),
};

// ─── Structured candidate sub-records (Education / Languages / Work Experience) ───
export interface CandidateEducation {
  id: string; candidate_id: string;
  degree: string | null; institution: string | null; field_of_study: string | null;
  start_year: string | null; end_year: string | null; gpa: string | null; notes: string | null;
  source: 'manual' | 'resume_import'; sort_order: number;
}
export interface CandidateLanguage {
  id: string; candidate_id: string;
  language: string; proficiency: 'basic' | 'conversational' | 'fluent' | 'native' | null;
  can_read: boolean; can_speak: boolean; can_write: boolean; notes: string | null;
  source: 'manual' | 'resume_import';
}
export interface CandidateWorkExperience {
  id: string; candidate_id: string;
  title: string | null; employer: string | null; location: string | null;
  start_date: string | null; end_date: string | null; is_current: boolean;
  pay_rate: string | null; tax_term: string | null; description: string | null;
  source: 'manual' | 'resume_import'; sort_order: number;
}

export const candidateRecordsApi = {
  all: (candidateId: string) =>
    api.get<{ education: CandidateEducation[]; languages: CandidateLanguage[]; work_experience: CandidateWorkExperience[] }>(
      `/candidates/${candidateId}/records`),
  addEducation: (candidateId: string, body: Partial<CandidateEducation>) =>
    api.post<CandidateEducation>(`/candidates/${candidateId}/records/education`, body),
  updateEducation: (candidateId: string, id: string, body: Partial<CandidateEducation>) =>
    api.put<CandidateEducation>(`/candidates/${candidateId}/records/education/${id}`, body),
  removeEducation: (candidateId: string, id: string) =>
    api.delete(`/candidates/${candidateId}/records/education/${id}`),
  addLanguage: (candidateId: string, body: Partial<CandidateLanguage>) =>
    api.post<CandidateLanguage>(`/candidates/${candidateId}/records/languages`, body),
  updateLanguage: (candidateId: string, id: string, body: Partial<CandidateLanguage>) =>
    api.put<CandidateLanguage>(`/candidates/${candidateId}/records/languages/${id}`, body),
  removeLanguage: (candidateId: string, id: string) =>
    api.delete(`/candidates/${candidateId}/records/languages/${id}`),
  addWork: (candidateId: string, body: Partial<CandidateWorkExperience>) =>
    api.post<CandidateWorkExperience>(`/candidates/${candidateId}/records/work-experience`, body),
  updateWork: (candidateId: string, id: string, body: Partial<CandidateWorkExperience>) =>
    api.put<CandidateWorkExperience>(`/candidates/${candidateId}/records/work-experience/${id}`, body),
  removeWork: (candidateId: string, id: string) =>
    api.delete(`/candidates/${candidateId}/records/work-experience/${id}`),
  importFromResume: (candidateId: string) =>
    api.post<{ success: boolean; education_added: number; work_experience_added: number }>(
      `/candidates/${candidateId}/records/import-from-resume`),
};

// ─── Interview scheduling + video eInterview ───────────────────────────────
export interface Interview {
  id: string; candidate_id: string; submission_id: string | null; job_id: string | null;
  round: 'phone_screen' | 'clinical' | 'manager' | 'panel' | 'final' | 'client' | 'other';
  mode: 'phone' | 'video' | 'in_person';
  scheduled_at: string | null; duration_minutes: number;
  interviewer_names: string | null; location: string | null; video_link: string | null;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show' | 'rescheduled';
  outcome: 'pending' | 'passed' | 'failed' | 'hold';
  notes: string | null; cancellation_reason: string | null;
  first_name?: string; last_name?: string; candidate_email?: string; candidate_phone?: string; job_title?: string | null;
}
export const interviewsApi = {
  list: (params?: { candidate_id?: string; submission_id?: string; job_id?: string; status?: string; upcoming?: boolean; from?: string; to?: string }) =>
    api.get<{ interviews: Interview[] }>('/interviews', { params }),
  schedule: (body: Partial<Interview> & { generate_video_link?: boolean }) =>
    api.post<{ interview: Interview; video_warning?: string }>('/interviews', body),
  update: (id: string, body: Partial<Interview>) =>
    api.put<{ interview: Interview }>(`/interviews/${id}`, body),
  cancel: (id: string, reason?: string) =>
    api.post(`/interviews/${id}/cancel`, { reason }),
  outcome: (id: string, outcome: Interview['outcome'], notes?: string) =>
    api.post(`/interviews/${id}/outcome`, { outcome, notes }),
  remove: (id: string) => api.delete(`/interviews/${id}`),
};

// ─── [phase4 track-B] Candidate interview self-scheduling ──────────────────
export interface InterviewSlotInput { start_at: string; end_at: string }
export interface InterviewBookingRequest {
  id: string; candidate_id: string; candidate_name: string | null;
  round: string; mode: string; status: 'sent' | 'viewed' | 'booked' | 'expired' | 'cancelled';
  token: string; booking_url: string; slot_count: number; open_slot_count: number;
  booked_interview_id: string | null; expires_at: string | null; created_at: string;
}
export interface CreateBookingRequestBody {
  candidate_id: string;
  submission_id?: string | null;
  job_id?: string | null;
  round?: Interview['round'];
  mode?: Interview['mode'];
  slots: InterviewSlotInput[];
  interviewer_user_ids?: string[];
  interviewer_names?: string | null;
  duration_minutes?: number;
  timezone?: string;
  ttl_days?: number;
}
export const interviewSchedulingApi = {
  // Recruiter-side (authenticated).
  create: (body: CreateBookingRequestBody) =>
    api.post<{ request_id: string; token: string; booking_url: string; slot_count: number; email_scheduled: boolean; sms_scheduled: boolean; expires_at: string | null }>(
      '/interview-scheduling', body),
  listRequests: (candidateId: string) =>
    api.get<{ requests: InterviewBookingRequest[] }>('/interview-scheduling', { params: { candidate_id: candidateId } }),
  cancel: (id: string) => api.post(`/interview-scheduling/${id}/cancel`),
  // Public token page (unauthenticated — token IS the credential).
  publicView: (token: string) =>
    axios.get(`${PUBLIC_API_BASE}/interview-scheduling/public/${token}`),
  publicBook: (token: string, slotId: string) =>
    axios.post(`${PUBLIC_API_BASE}/interview-scheduling/public/${token}/book`, { slot_id: slotId }),
};

// ─── Job-board distribution (Published Jobs) ───────────────────────────────
export interface JobBoard {
  key: string; label: string; feed_path: string; format: string;
  enabled: boolean; published_count: number;
}
export interface PublishedJobRow {
  id: string; job_code: string | null; title: string;
  city: string | null; state: string | null;
  published_boards: string[] | null; client_name: string | null;
}
export const jobBoardsApi = {
  list: () => api.get<{ boards: JobBoard[]; total_open: number }>('/job-boards'),
  toggle: (board: string, enabled: boolean) => api.put(`/job-boards/${board}`, { enabled }),
  published: () => api.get<{ jobs: PublishedJobRow[]; boards: Array<{ key: string; label: string }> }>('/job-boards/published'),
  setJobBoards: (jobId: string, boards: string[] | null) =>
    api.put<{ success: boolean; published_boards: string[] | null }>(`/job-boards/jobs/${jobId}`, { boards }),
};

// ─── External (vendor) integrations: BGV / Nursys / payroll / WhatsApp ──────
/** Where a provider's credentials came from for the current tenant. Values are never returned. */
export type CredentialSource = 'tenant' | 'platform_demo' | 'platform_env' | null;

export interface IntegrationProvider {
  key: string; label: string; docs_url: string; notes: string | null;
  /** Credential field NAMES. Values are write-only and never returned. */
  credential_fields: string[];
  /** Fields this tenant has stored a value for. */
  fields_set: string[];
  /** Fields this tenant still owes. Empty when env fallback is in use. */
  missing_credentials: string[];
  credential_source: CredentialSource;
  configured: boolean; enabled: boolean;
  status: 'not_configured' | 'configured' | 'connected' | 'error';
  last_status_message: string | null; last_tested_at: string | null;
}
export interface IntegrationCategory {
  key: string; label: string; description: string; providers: IntegrationProvider[];
}
// ─── Bring-your-own Anthropic key (per organization) ────────────────────────
export interface AiProviderKeyStatus {
  /** This organization has stored its own key. The value itself is never returned. */
  tenant_key_set: boolean;
  tenant_key_updated_at: string | null;
  /** The deployment has a platform key AI falls back to without a tenant key. */
  platform_key_available: boolean;
  /** Which key AI calls use right now. */
  active_source: 'tenant' | 'platform' | null;
  /** False when the deployment can't store secrets (TENANT_SECRETS_KEY unset). */
  secret_store_enabled: boolean;
  /** False unless the operator set TENANT_ANTHROPIC_KEYS_ENABLED=true. */
  tenant_keys_enabled: boolean;
}
export const aiProviderKeyApi = {
  get: () => api.get<AiProviderKeyStatus>('/ai-provider-key'),
  /** Verified against Anthropic server-side before it is stored. */
  save: (apiKey: string) => api.put<AiProviderKeyStatus>('/ai-provider-key', { api_key: apiKey }),
  clear: () => api.delete<AiProviderKeyStatus>('/ai-provider-key'),
};

export const externalIntegrationsApi = {
  list: () => api.get<{ categories: IntegrationCategory[] }>('/external-integrations'),
  save: (category: string, provider: string, body: { enabled?: boolean; config?: Record<string, unknown> }) =>
    api.put<{ success: boolean; status: string }>(`/external-integrations/${category}/${provider}`, body),
  test: (category: string, provider: string) =>
    api.post<{ status: string; message: string }>(`/external-integrations/${category}/${provider}/test`),
  /** Store this tenant's keys. An empty string clears that one field. The response never includes values. */
  saveCredentials: (category: string, provider: string, credentials: Record<string, string>) =>
    api.put<{
      success: boolean; fields_set: string[]; missing_credentials: string[];
      credential_source: CredentialSource; configured: boolean;
    }>(`/external-integrations/${category}/${provider}/credentials`, { credentials }),
  clearCredentials: (category: string, provider: string) =>
    api.delete<{
      success: boolean; missing_credentials: string[];
      credential_source: CredentialSource; configured: boolean;
    }>(`/external-integrations/${category}/${provider}/credentials`),
};

// ─── eBoarding: onboarding sessions / workflow ─────────────────────────────
export type OnboardingStatus = 'draft' | 'initiated' | 'responded' | 'verified' | 'negotiate' | 'joined' | 'rejected' | 'disqualified' | 'cancelled';
export interface OnboardingSession {
  id: string; candidate_id: string; status: OnboardingStatus;
  offer_status: 'none' | 'sent' | 'accepted' | 'rejected' | 'countered';
  offer_amount: string | null; offer_notes: string | null; owner_user_id: string | null;
  notes: string | null; initiated_at: string | null; joined_at: string | null;
  created_at: string; updated_at: string;
}
export interface OnboardingApprover {
  id: string; session_id: string; approver_user_id: string | null; approver_name: string | null;
  sort_order: number; decision: 'pending' | 'approved' | 'rejected'; comment: string | null; decided_at: string | null;
  user_first?: string | null; user_last?: string | null;
}
export interface OnboardingHistoryRow { id: string; from_status: string | null; to_status: string; note: string | null; created_at: string; }
export interface OnboardingSessionPayload { session: OnboardingSession | null; approvers?: OnboardingApprover[]; history?: OnboardingHistoryRow[]; }
export interface OnboardingBranding { id: number; portal_title: string | null; logo_url: string | null; primary_color: string | null; welcome_message: string | null; support_email: string | null; }
export interface OnboardingLifecycleEmail { event: string; enabled: boolean; subject: string; body: string; }

export const onboardingSessionsApi = {
  get: (candidateId: string) => api.get<OnboardingSessionPayload>(`/onboarding-sessions/candidate/${candidateId}`),
  create: (candidateId: string) => api.post<OnboardingSessionPayload>(`/onboarding-sessions/candidate/${candidateId}`),
  transition: (id: string, to: OnboardingStatus, note?: string) => api.post<OnboardingSessionPayload>(`/onboarding-sessions/${id}/transition`, { to, note }),
  offer: (id: string, body: { offer_status: OnboardingSession['offer_status']; offer_amount?: string | null; offer_notes?: string | null }) =>
    api.post<OnboardingSessionPayload>(`/onboarding-sessions/${id}/offer`, body),
  setApprovers: (id: string, approvers: Array<{ approver_name?: string | null; approver_user_id?: string | null }>) =>
    api.put<OnboardingSessionPayload>(`/onboarding-sessions/${id}/approvers`, { approvers }),
  decide: (approverId: string, decision: 'approved' | 'rejected', comment?: string) =>
    api.post<OnboardingSessionPayload>(`/onboarding-sessions/approvers/${approverId}/decision`, { decision, comment }),
  branding: () => api.get<{ branding: OnboardingBranding | null }>('/onboarding-sessions/branding'),
  saveBranding: (body: Partial<OnboardingBranding>) => api.put<{ branding: OnboardingBranding }>('/onboarding-sessions/branding', body),
  lifecycleEmails: () => api.get<{ emails: OnboardingLifecycleEmail[] }>('/onboarding-sessions/lifecycle-emails'),
  saveLifecycleEmail: (event: string, body: Partial<OnboardingLifecycleEmail>) =>
    api.put<{ email: OnboardingLifecycleEmail }>(`/onboarding-sessions/lifecycle-emails/${event}`, body),
};

// ─── Candidate document portal (→ OneDrive) ────────────────────────────────
export interface DocPortalState {
  first_name: string; email: string | null; phone: string | null;
  email_verified: boolean; phone_verified: boolean; verified: boolean;
  opted_out?: boolean;
  required_docs: Array<{ key: string; label: string; submitted: boolean; rejected_reason: string | null; promised_date?: string | null }>;
  submitted_docs: Array<{ doc_type: string; status: string; filename: string; review_note: string | null }>;
  esign_docs: Array<{ title: string; status: 'awaiting_signature' | 'signed'; signed_at: string | null; sign_token: string | null }>;
}
export interface DocPortalAdmin {
  account: null | {
    id: string; email: string | null; phone: string | null; email_verified: boolean; phone_verified: boolean;
    status: string; required_docs: Array<{ key: string; label: string }>; created_at: string;
    opted_out_at?: string | null; opted_out_reason?: string | null; reminder_count?: number; last_reminder_at?: string | null;
  };
  documents: Array<{ id: string; doc_type: string; doc_label: string; filename: string; sharepoint_url: string; status: string; storage: string; review_note: string | null; issue_date?: string | null; expiry_date?: string | null; uploaded_at: string; is_sensitive?: boolean; locked?: boolean }>;
  promises?: Array<{ doc_type: string; promised_date: string; note: string | null; chased_at: string | null; created_at: string }>;
  waivers?: Array<{ doc_type: string; note: string | null; created_at: string; waived_by_name: string | null }>;
}
export const docPortalApi = {
  // public (token)
  get: (token: string) => api.get<DocPortalState>(`/portal-docs/${token}`),
  identify: (token: string, body: { email?: string; phone?: string }) => api.post(`/portal-docs/${token}/identify`, body),
  sendCode: (token: string, channel: 'email' | 'phone') => api.post<{ success: boolean }>(`/portal-docs/${token}/send-code`, { channel }),
  verifyCode: (token: string, channel: 'email' | 'phone', code: string) =>
    api.post<{ verified: boolean; email_verified: boolean; phone_verified: boolean }>(`/portal-docs/${token}/verify-code`, { channel, code }),
  upload: (token: string, docType: string, docLabel: string, file: File, dates?: { issue_date?: string; expiry_date?: string }) => {
    const fd = new FormData(); fd.append('file', file); fd.append('doc_type', docType); fd.append('doc_label', docLabel);
    if (dates?.issue_date) fd.append('issue_date', dates.issue_date);
    if (dates?.expiry_date) fd.append('expiry_date', dates.expiry_date);
    return api.post<{ success: boolean; filename: string }>(`/portal-docs/${token}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  optOut: (token: string, reason?: string) => api.post<{ success: boolean }>(`/portal-docs/${token}/opt-out`, { reason }),
  promise: (token: string, body: { doc_type: string; promised_date: string; note?: string }) =>
    api.post<{ success: boolean }>(`/portal-docs/${token}/promise`, body),
  // admin
  invite: (body: { candidate_id: string; required_docs?: Array<{ key: string; label: string }>; send_email?: boolean; send_sms?: boolean }) =>
    api.post<{ account_id: string; link: string; delivery: Record<string, string> }>(`/candidate-doc-portal/invite`, body),
  adminStatus: (candidateId: string) => api.get<DocPortalAdmin>(`/candidate-doc-portal/candidate/${candidateId}`),
  reviewDoc: (submissionId: string, body: { status: 'verified' | 'rejected'; reason?: string; note?: string; issue_date?: string; expiry_date?: string }) =>
    api.post<{ success: boolean; status: string; candidate_notified: boolean }>(`/candidate-doc-portal/doc/${submissionId}/review`, body),
  // Inline preview — fetch the bytes with auth, then show as an object URL.
  previewDoc: (submissionId: string) =>
    api.get<Blob>(`/candidate-doc-portal/doc/${submissionId}/content`, { responseType: 'blob' }),
  exportZip: (candidateId: string) =>
    api.get<Blob>(`/candidate-doc-portal/candidate/${candidateId}/export.zip`, { responseType: 'blob' }),
  // HR "will get it later / N/A" — waive a required doc (stops the chase and
  // lets the portal complete without it); DELETE puts it back in scope.
  waive: (accountId: string, body: { doc_type: string; note?: string }) =>
    api.post<{ success: boolean }>(`/candidate-doc-portal/account/${accountId}/waive`, body),
  unwaive: (accountId: string, docType: string) =>
    api.delete<{ success: boolean }>(`/candidate-doc-portal/account/${accountId}/waive/${encodeURIComponent(docType)}`),
};

// ─── Inbound credential docs + ShiftWise outbox ─────────────────────────────
export interface InboundDocFile {
  id: string; email_id: string; filename: string; mime: string | null;
  size_bytes: number | null; status: 'held' | 'classified' | 'discarded';
  submission_id: string | null; classified_at: string | null;
}
export interface InboundDocEmail {
  id: string; subject: string | null; from_name: string | null; from_email: string | null;
  received_at: string | null; body_preview: string | null;
  candidate_id: string | null; match_status: 'matched' | 'unmatched' | 'ignored';
  attachments_total: number; candidate_first_name: string | null; candidate_last_name: string | null;
  files: InboundDocFile[];
}
export interface ShiftwiseBatch {
  id: string; candidate_id: string; person_key: string;
  files: Array<{ name: string; path: string; storage: string; url: string }>;
  failures: string[]; status: 'staged' | 'uploaded' | 'cancelled';
  staged_by: string | null; staged_at: string; uploaded_by: string | null; uploaded_at: string | null;
  first_name: string; last_name: string;
}
export const inboundDocsApi = {
  list: (show: 'open' | 'all' = 'open') => api.get<{ emails: InboundDocEmail[] }>(`/inbound-docs`, { params: { show } }),
  previewFile: (fileId: string) => api.get<Blob>(`/inbound-docs/files/${fileId}/content`, { responseType: 'blob' }),
  assign: (emailId: string, candidateId: string) => api.put<{ success: boolean }>(`/inbound-docs/emails/${emailId}/assign`, { candidate_id: candidateId }),
  ignore: (emailId: string) => api.put<{ success: boolean }>(`/inbound-docs/emails/${emailId}/ignore`),
  classify: (fileId: string, body: { candidate_id?: string; doc_type: string; doc_label: string; issue_date?: string; expiry_date?: string }) =>
    api.post<{ success: boolean; submission_id: string; filename: string }>(`/inbound-docs/files/${fileId}/classify`, body),
  discard: (fileId: string) => api.post<{ success: boolean }>(`/inbound-docs/files/${fileId}/discard`),
  // ShiftWise outbox
  stage: (candidateId: string) =>
    api.post<{ batch_id: string; folder: string; files: ShiftwiseBatch['files']; failures: string[] }>(`/inbound-docs/shiftwise/stage/${candidateId}`),
  outbox: () => api.get<{ batches: ShiftwiseBatch[] }>(`/inbound-docs/shiftwise`),
  markUploaded: (id: string) => api.post<{ success: boolean }>(`/inbound-docs/shiftwise/${id}/mark-uploaded`),
  cancelBatch: (id: string) => api.post<{ success: boolean }>(`/inbound-docs/shiftwise/${id}/cancel`),
};

// ─── Offer Letters (HR) ──────────────────────────────────────────────────────
export interface OfferLetter {
  id: string;
  candidate_id: string | null;
  candidate_name: string | null;
  role_title: string | null;
  employment_type: 'W2' | '1099' | 'per-diem' | 'contract' | null;
  pay_rate: string | null;          // numeric columns arrive as strings
  pay_period: string | null;
  hours_per_week: number | null;
  housing_stipend: string | null;
  mie_stipend: string | null;
  start_date: string | null;        // YYYY-MM-DD
  location: string | null;
  expires_on: string | null;        // YYYY-MM-DD
  extra_terms: string | null;
  body: string;
  status: 'draft' | 'sent' | 'signed' | 'declined' | 'expired';
  esign_document_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface OfferLetterInput {
  candidate_id?: string | null;
  candidate_name?: string;
  role_title?: string;
  employment_type?: 'W2' | '1099' | 'per-diem' | 'contract';
  pay_rate?: number | string | null;
  pay_period?: string;
  hours_per_week?: number | null;
  housing_stipend?: number | string | null;
  mie_stipend?: number | string | null;
  start_date?: string | null;
  location?: string;
  expires_on?: string | null;
  extra_terms?: string;
  body?: string;                    // manual edits to the letter text
  status?: OfferLetter['status'];   // draft-only transitions (declined/expired)
  regenerate?: boolean;             // recompose body from fields on update
  polish?: boolean;                 // false = skip the optional AI polish
}

export const offerLettersApi = {
  create: (data: OfferLetterInput) => api.post<{ letter: OfferLetter }>('/offer-letters', data),
  list: () => api.get<{ letters: OfferLetter[] }>('/offer-letters'),
  get: (id: string) => api.get<{ letter: OfferLetter }>(`/offer-letters/${id}`),
  update: (id: string, data: OfferLetterInput) => api.put<{ letter: OfferLetter }>(`/offer-letters/${id}`, data),
  pdf: (id: string) => api.get<Blob>(`/offer-letters/${id}/pdf`, { responseType: 'blob' }),
  send: (id: string, opts?: { email?: string }) =>
    api.post<{ letter: OfferLetter; esign_document_id: string; signing_email: string }>(`/offer-letters/${id}/send`, opts ?? {}),
  remove: (id: string) => api.delete<{ success: boolean }>(`/offer-letters/${id}`),
};

// ─── Internal team lifecycle (onboarding / training / offboarding) ──────────
export interface LifecycleItem {
  id: string; lifecycle_id: string;
  kind: 'access' | 'training' | 'document' | 'equipment' | 'other';
  title: string; detail: string | null;
  owner: 'cipher' | 'harry' | 'human';
  status: 'pending' | 'done' | 'skipped';
  auto: boolean; done_by: string | null; done_at: string | null; sort: number;
}
export interface TeamLifecycleRow {
  id: string; user_id: string | null; email: string; full_name: string | null;
  phase: 'onboarding' | 'active' | 'offboarding' | 'offboarded';
  target_role: string | null; started_at: string;
  offboard_requested_at: string | null; offboarded_at: string | null;
  notes: string | null; account_email: string | null;
  items: LifecycleItem[];
}
export interface TrainingMaterial {
  id: string; title: string; description: string | null; url: string | null;
  category: string | null; active: boolean; created_at: string;
}
export interface VaWorkspace {
  id: string; email: string; license_assigned: boolean; browser_access_verified: boolean;
  redirection_policy_applied: boolean; outbox_synced: boolean; notes: string | null; updated_at: string;
}
export const teamLifecycleApi = {
  list: () => api.get<{ lifecycles: TeamLifecycleRow[] }>(`/team-lifecycle`),
  onboard: (body: { email: string; full_name?: string; target_role?: string; trainings?: string[] }) =>
    api.post<{ id: string; access_granted: boolean }>(`/team-lifecycle/onboard`, body),
  offboard: (id: string) =>
    api.post<{ success: boolean; access_revoked: boolean; manual_items: number }>(`/team-lifecycle/${id}/offboard`),
  syncAccess: (id: string) =>
    api.post<{ success: boolean; access_granted: boolean; note: string }>(`/team-lifecycle/${id}/sync-access`),
  setItem: (itemId: string, status: LifecycleItem['status']) =>
    api.put<{ success: boolean; phase: string | null }>(`/team-lifecycle/items/${itemId}`, { status }),
  addItem: (id: string, body: { kind?: LifecycleItem['kind']; title: string; detail?: string; owner?: LifecycleItem['owner'] }) =>
    api.post<{ id: string }>(`/team-lifecycle/${id}/items`, body),
  materials: () => api.get<{ materials: TrainingMaterial[] }>(`/team-lifecycle/materials`),
  addMaterial: (body: { title: string; description?: string; url?: string; category?: string }) =>
    api.post<{ id: string }>(`/team-lifecycle/materials`, body),
  removeMaterial: (id: string) => api.delete<{ success: boolean }>(`/team-lifecycle/materials/${id}`),
  vaWorkspaces: () => api.get<{ workspaces: VaWorkspace[]; va_users: Array<{ id: string; email: string; name: string | null }> }>(`/team-lifecycle/va-workspaces`),
  saveVaWorkspace: (body: Partial<VaWorkspace> & { email: string }) =>
    api.put<{ success: boolean; id: string }>(`/team-lifecycle/va-workspaces`, body),
};

// ─── Cipher security agent ──────────────────────────────────────────────────
export interface CipherFinding {
  severity: 'critical' | 'warning' | 'info';
  code: string; title: string; detail: string; data?: unknown;
}
export interface SecurityAgentReport {
  id: string; window_hours: number; findings: CipherFinding[];
  critical_count: number; warning_count: number; info_count: number;
  assessment: string | null; created_at: string;
}
export const securityAgentApi = {
  reports: () => api.get<{ reports: SecurityAgentReport[] }>(`/security-agent/reports`),
  runNow: () => api.post<{ success: boolean; findings: number; critical: number; warning: number; info: number }>(`/security-agent/run-now`),
};

// ─── Client-approval suggestions (confirm-first) ───────────────────────────
export interface ClientApprovalSuggestion {
  id: string; candidate_id: string; client_id: string | null;
  from_address: string | null; subject: string | null; snippet: string | null;
  confidence: 'possible' | 'likely'; status: 'pending' | 'confirmed' | 'dismissed';
  created_at: string; first_name: string; last_name: string; stage: string; client_name: string | null;
}
export const clientApprovalsApi = {
  list: () => api.get<{ suggestions: ClientApprovalSuggestion[] }>(`/client-approvals`),
  scan: () => api.post<{ success: boolean; emails_scanned: number; suggestions_created: number }>(`/client-approvals/scan`),
  confirm: (id: string) => api.post<{ success: boolean; candidate_emailed: boolean }>(`/client-approvals/${id}/confirm`),
  dismiss: (id: string) => api.post<{ success: boolean }>(`/client-approvals/${id}/dismiss`),
};

// ─── Pending Candidate Update Tracker ──────────────────────────────────────
export type PendingReviewOutcome =
  | 'no_update' | 'update_received' | 'needs_follow_up' | 'contact_candidate'
  | 'contact_client' | 'move_to_next_step' | 'remove_from_pending';
export interface PendingReviewCandidate {
  candidate_id: string; first_name: string; last_name: string;
  email: string | null; phone: string | null; stage: string;
  recruiter_id: string | null; recruiter_name: string | null;
  pending_reasons: string[]; job_titles: string | null;
  entered_pending_at: string | null; last_update_at: string | null;
  last_no_update_email_at: string | null;
  days_pending: number; days_since_update: number;
  today_review: null | {
    outcome: PendingReviewOutcome; next_action: string | null; notes: string | null;
    auto_logged: boolean; reviewed_by_name: string | null; updated_at: string;
  };
}
export interface PendingReviewHistoryEntry {
  review_date: string; outcome: PendingReviewOutcome; next_action: string | null;
  notes: string | null; auto_logged: boolean; reviewed_by_name: string | null; updated_at: string;
}
export const pendingTrackerApi = {
  review: () => api.get<{ date: string; candidates: PendingReviewCandidate[] }>('/pending-tracker/review'),
  record: (candidateId: string, body: { outcome: PendingReviewOutcome; next_action?: string; notes?: string }) =>
    api.post<{ success: boolean; review_id: string; task_created: boolean }>(`/pending-tracker/review/${candidateId}`, body),
  history: (candidateId: string) => api.get<{ history: PendingReviewHistoryEntry[] }>(`/pending-tracker/history/${candidateId}`),
};

// ─── App config (demo-mode flag) ───────────────────────────────────────────
export const appConfigApi = {
  get: () => api.get<{ demo_mode: boolean; demo_label: string; demo_site_url: string; demo_site_url_general?: string }>('/app-config'),
};

// ─── Demo data (sales-demo seed) ───────────────────────────────────────────
export interface DemoCounts {
  candidates: number; jobs: number; clients: number; placements: number;
  pending_tracked?: number; portal_accounts?: number; esign_documents?: number;
}
export const demoApi = {
  status: () => api.get<{ counts: DemoCounts }>('/admin/demo/status'),
  seed: () => api.post<{ success: boolean; created: Record<string, number> }>('/admin/demo/seed'),
  wipe: () => api.post<{ success: boolean }>('/admin/demo/wipe'),
};

// ─── Candidate document backup importer (Track M) ──────────────────────────
// [phase6 track-M] Import the DOCUMENTS a CEIPAL data export leaves behind
// (resumes, credentials, signed PDFs): a manifest spreadsheet (filename →
// candidate) + a folder of files. Two steps: preview the manifest (match
// counts, nothing written), then commit the manifest + files (attaches each
// file to its candidate). Commit is idempotent, so large backups can be
// uploaded a few hundred files per call. Self-contained block.
export interface CandidateDocPreview {
  header_row: number;
  mapping: Record<string, string>;
  unmatched_columns: string[];
  counts: { total: number; matched: number; unmatched_candidate: number; ambiguous: number; missing_filename: number };
  sample: Array<{ filename: string; candidate: string; outcome: 'matched' | 'unmatched_candidate' | 'ambiguous' | 'missing_filename'; method: 'email' | 'name' | 'none' }>;
  // [track-doc-zip] Present only for a ZIP preview — how the zip's document
  // entries line up with the manifest (computed from names, nothing written).
  zip?: {
    manifest_source: 'uploaded' | 'in-zip';
    entries: number;
    matched: number;
    unmatched_file: number;
    unmatched_candidate: number;
    ambiguous: number;
    missing_file: number;
  };
}
export interface CandidateDocCommit {
  inserted: number;
  skipped: number;
  unmatched_file: number;
  unmatched_candidate: number;
  failed: number;
  missing_file: number;
  warnings: string[];
}
// [track-doc-zip] preview/commit accept EITHER a manifest + individual files OR a
// single .zip of the document folder (optionally with the manifest inside it). A
// separately-uploaded manifest always wins as the mapping.
export const candidateDocImportApi = {
  preview: (manifest: File | null, zip?: File | null) => {
    const fd = new FormData();
    if (manifest) fd.append('manifest', manifest);
    if (zip) fd.append('zip', zip);
    return api.post<CandidateDocPreview>('/admin/candidate-doc-import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  commit: (manifest: File | null, files: File[], zip?: File | null) => {
    const fd = new FormData();
    if (manifest) fd.append('manifest', manifest);
    for (const f of files) fd.append('file', f);
    if (zip) fd.append('zip', zip);
    return api.post<CandidateDocCommit>('/admin/candidate-doc-import/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const eodApi = {
  today: () => api.get<{ report: EodReport | null; report_date: string }>('/eod/today'),
  byDate: (date: string) => api.get<{ report: EodReport | null; report_date: string }>(`/eod/by-date/${date}`),
  save: (body: { what_got_done?: string | null; blockers?: string | null; tomorrow_focus?: string | null; report_date?: string }) =>
    api.put<{ report: EodReport }>('/eod', body),
  yesterdayTeam: () =>
    api.get<{ report_date: string | null; reports: HuddleEodReport[] }>('/eod/yesterday-team'),
  draftToday: () =>
    api.get<{ what_got_done: string; task_count: number }>('/eod/draft-today'),
};

export const complianceReportsApi = {
  overview: () =>
    api.get<{
      total_records: number;
      by_status: Record<string, number>;
      completion_rate: number;
      by_type: Record<string, Record<string, number>>;
      published_content: { policies: number; documents: number; exams: number; checklists: number };
      expiring_soon_count: number;
      overdue_count: number;
    }>('/compliance/reports/overview'),

  users: () =>
    api.get<{
      users: Array<{
        user_clerk_id: string;
        total: number;
        completed_count: number;
        pending_count: number;
        expired_count: number;
        failed_count: number;
        next_due_date: string | null;
        completion_rate: number;
      }>;
    }>('/compliance/reports/users'),

  content: () =>
    api.get<{
      items: Array<{
        item_type: string;
        item_id: string;
        title: string;
        total_assigned: number;
        completed_count: number;
        expired_count: number;
        failed_count: number;
      }>;
    }>('/compliance/reports/content'),

  expiring: (days?: number) =>
    api.get<{ records: Array<{ id: string; title: string; user_clerk_id: string; expiration_date: string; days_until_expiry: number; item_type: string }> }>(
      '/compliance/reports/expiring',
      { params: days ? { days } : {} }
    ),

  overdue: () =>
    api.get<{ records: Array<{ id: string; title: string; user_clerk_id: string; due_date: string; days_overdue: number; status: string; item_type: string }> }>(
      '/compliance/reports/overdue'
    ),

  notifications: () =>
    api.get<{
      notifications: Array<{
        id: string;
        user_clerk_id: string;
        notification_type: string;
        subject: string;
        status: string;
        created_at: string;
        sent_at: string | null;
        recipient_email: string | null;
      }>;
    }>('/compliance/reports/notifications'),
};

export const complianceJobsApi = {
  status: () =>
    api.get<{
      jobs: Array<{ job_name: string; status: string; records_processed: number; records_affected: number; started_at: string; completed_at: string | null }>;
      notifications: { pending: number; sent: number; failed: number; total: number };
    }>('/compliance/jobs/status'),

  getSettings: () =>
    api.get<{ settings: Record<string, string> }>('/compliance/jobs/settings'),

  updateSetting: (key: string, value: string) =>
    api.patch('/compliance/jobs/settings', { key, value }),

  expire: () => api.post<{ expired_count: number; auto_renewed_count: number }>('/compliance/jobs/expire'),
  notifyDueSoon: () => api.post<{ queued_count: number }>('/compliance/jobs/notify-due-soon'),
  notifyExpiring: () => api.post<{ queued_count: number }>('/compliance/jobs/notify-expiring'),
  processNotifications: () => api.post<{ processed: number; sent: number; failed: number }>('/compliance/jobs/process-notifications'),
  autoAssign: () => api.post<{ users_evaluated: number; assignments_created: number }>('/compliance/jobs/auto-assign'),
  runAll: () => api.post<Record<string, unknown>>('/compliance/jobs/run-all'),
};

// ─── Compliance Phase 4 ──────────────────────────────────────────────────────

export interface CompCertificate {
  id: string;
  competency_record_id: string;
  user_clerk_id: string;
  exam_id: string | null;
  title: string;
  issued_at: string;
  expires_at: string | null;
  certificate_number: string;
  score?: number;
  completed_date?: string;
}

export const complianceCertificatesApi = {
  getMyCertificates: () =>
    api.get<{ certificates: CompCertificate[] }>('/compliance/certificates'),
  getAllCertificates: () =>
    api.get<{ certificates: CompCertificate[]; total: number }>('/compliance/certificates/all'),
  getCertificate: (id: string) =>
    api.get<{ certificate: CompCertificate }>(`/compliance/certificates/${id}`),
  getPrintUrl: (id: string) =>
    `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/api/v1/compliance/certificates/${id}/print`,
};

export const complianceTrendsApi = {
  getTrends: () =>
    api.get<{ days: Array<{ day: string; completions: number; exams: number; policies: number; documents: number; checklists: number }> }>(
      '/compliance/reports/trends'
    ),
  getUserReport: (userId: string) =>
    api.get<{
      user_clerk_id: string;
      records: any[];
      summary: { total: number; completed: number; pending: number; expired: number; completion_rate: number; by_type: Record<string, any> };
    }>(`/compliance/reports/user/${userId}`),
  getExamAnalysis: (examId: string) =>
    api.get<{
      exam_id: string;
      stats: { total_attempts: number; passed: number; failed: number; avg_score: number; max_score: number; min_score: number; unique_takers: number; pass_rate: number };
      score_distribution: Record<string, number>;
    }>(`/compliance/reports/exam/${examId}`),
  exportRecordsUrl: (params?: { status?: string; item_type?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return `${(import.meta.env.VITE_API_URL as string | undefined) ?? ''}/api/v1/compliance/reports/export/records${qs ? '?' + qs : ''}`;
  },
};

// ─── Compliance Phase 5 (Integrations) ───────────────────────────────────────

export const complianceIntegrationApi = {
  // Staff
  getStaffCompliance: (staffId: string) =>
    api.get<{
      linked: boolean;
      staff?: { id: string; first_name: string; last_name: string; clerk_user_id?: string };
      summary?: { total: number; completed: number; pending: number; expired: number; failed: number; completion_rate: number };
      records?: any[];
      expiring_soon?: any[];
    }>(`/compliance/integration/staff/${staffId}/compliance`),

  linkStaffUser: (staffId: string, clerk_user_id: string) =>
    api.post<{ success: boolean; staff: any }>(`/compliance/integration/staff/${staffId}/link-user`, { clerk_user_id }),

  unlinkStaffUser: (staffId: string) =>
    api.post<{ success: boolean }>(`/compliance/integration/staff/${staffId}/unlink-user`),

  // Candidate
  getCandidateCompliance: (candidateId: string) =>
    api.get<{
      summary: { total: number; completed: number; pending: number; expired: number; completion_rate: number };
      records: any[];
      assigned_bundles: any[];
    }>(`/compliance/integration/candidate/${candidateId}/compliance`),

  assignBundleToCandidate: (candidateId: string, bundle_id: string, due_date?: string) =>
    api.post<{ success: boolean; bundle_title: string; created: number; skipped: number }>(
      `/compliance/integration/candidate/${candidateId}/assign-bundle`,
      { bundle_id, due_date }
    ),

  candidateStageHook: (candidateId: string, stage: string) =>
    api.post<{ triggered: boolean; bundles_assigned?: number }>(
      `/compliance/integration/candidate/${candidateId}/stage-hook`,
      { stage }
    ),

  // Questionnaires
  getQuestionnaires: () =>
    api.get<{ checklists: any[] }>('/compliance/integration/questionnaire-checklists'),

  assignQuestionnaire: (incidentId: string, checklist_id: string, user_clerk_id: string) =>
    api.post<{ success: boolean; competency_record_id: string }>(
      `/compliance/integration/incident/${incidentId}/assign-questionnaire`,
      { checklist_id, user_clerk_id }
    ),

  // Overview badge
  getOverviewBadge: () =>
    api.get<{ total: number; completed: number; expired: number; overdue: number; completion_rate: number }>(
      '/compliance/integration/overview-badge'
    ),
};

// ─── Compliance Phase 6 ──────────────────────────────────────────────────────

export const complianceReadinessApi = {
  getAll: () =>
    api.get<{
      records: Array<{
        id: string; staff_id: string | null; candidate_id: string | null;
        staff_name: string | null; staff_role: string | null; candidate_name: string | null;
        candidate_stage: string | null; is_ready: boolean; readiness_score: number;
        blocking_issues: string[]; last_evaluated: string;
      }>;
      summary: { total: number; ready: number; not_ready: number; avg_score: number };
    }>('/compliance/readiness'),
  getStaffReadiness: (staffId: string) =>
    api.get<{ id: string; is_ready: boolean; readiness_score: number; blocking_issues: string[]; last_evaluated: string; notes: string | null } | null>(
      `/compliance/readiness/staff/${staffId}`
    ),
  getCandidateReadiness: (candidateId: string) =>
    api.get<{ id: string; is_ready: boolean; readiness_score: number; blocking_issues: string[]; last_evaluated: string } | null>(
      `/compliance/readiness/candidate/${candidateId}`
    ),
  evaluateStaff: (staffId: string) =>
    api.post<{ staff: any; readiness: { is_ready: boolean; score: number; blocking_issues: string[] } }>(
      `/compliance/readiness/evaluate/staff/${staffId}`
    ),
  evaluateCandidate: (candidateId: string) =>
    api.post<{ candidate: any; readiness: { is_ready: boolean; score: number; blocking_issues: string[] } }>(
      `/compliance/readiness/evaluate/candidate/${candidateId}`
    ),
  evaluateAll: () =>
    api.post<{ staff_evaluated: number; candidates_evaluated: number; ready_count: number }>(
      '/compliance/readiness/evaluate-all'
    ),
  updateStaffReadiness: (staffId: string, data: { is_ready?: boolean; notes?: string }) =>
    api.patch(`/compliance/readiness/staff/${staffId}`, data),
};

export const complianceMessagingApi = {
  getInbox: () => api.get<{ messages: any[] }>('/compliance/messages'),
  getSent: () => api.get<{ messages: any[] }>('/compliance/messages/sent'),
  getUnreadCount: () => api.get<{ count: number }>('/compliance/messages/unread-count'),
  getMessage: (id: string) => api.get<{ message: any; replies: any[] }>(`/compliance/messages/${id}`),
  send: (data: { recipient_clerk_ids: string[]; subject: string; body: string; message_type?: string }) =>
    api.post<{ sent: number; message_ids: string[] }>('/compliance/messages', data),
  reply: (id: string, body: string) =>
    api.post<{ reply: any }>(`/compliance/messages/${id}/reply`, { body }),
  markRead: (id: string) => api.post(`/compliance/messages/${id}/read`),
  archive: (id: string) => api.delete(`/compliance/messages/${id}`),
};

export const complianceBulkAssignApi = {
  bulkAssign: (data: {
    bundle_id: string;
    filter: { user_clerk_ids?: string[]; role?: string; specialty?: string };
    due_date?: string;
  }) => api.post<{ bundle_id: string; total_users: number; created: number; skipped: number }>(
    '/compliance/bundles/bulk-assign', data
  ),
};

// ═══════════════════════════════════════════════════════════════════════════
// ATS Phase 2 — Clients (orgs), Jobs, Submissions, Pipeline Stages, Tasks
// ═══════════════════════════════════════════════════════════════════════════

// ─── Client orgs ─────────────────────────────────────────────────────────────
export interface ClientOrg {
  id: string;
  name: string;
  website?: string | null;
  business_unit?: string | null;
  offerings?: string[];
  submission_format?: string | null;
  submission_format_notes?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  status: 'active' | 'inactive' | 'prospect' | 'churned';
  notes?: string | null;
  facility_count?: number;
  open_jobs?: number;
  // Per-client document requirements (from the FNS Doc Matrix import).
  // Free-form list of doc names required before a submission can ship.
  required_documents?: string[];
  // Free-text narrative from the Client Submission Rules sheet.
  submission_rules_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientContact {
  id: string;
  client_id: string;
  facility_id?: string | null;
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  is_primary: boolean;
  notes?: string | null;
  created_at: string;
}

export interface ClientRequirementTemplate {
  id: string;
  client_id: string;
  kind: 'submission' | 'onboarding';
  bundle_id?: string | null;
  bundle_title?: string | null;
  ad_hoc: Array<{ type?: string; kind?: string; label: string; required?: boolean; notes?: string }>;
  notes?: string | null;
  created_at: string;
}

export const clientsOrgsApi = {
  list: (params?: { status?: string; search?: string }) =>
    api.get<{ clients: ClientOrg[] }>('/clients/orgs', { params }),
  get: (id: string) => api.get<{
    client: ClientOrg;
    facilities: Array<{ id: string; name: string; type?: string; address?: string }>;
    contacts: ClientContact[];
    requirement_templates: ClientRequirementTemplate[];
  }>(`/clients/orgs/${id}`),
  create: (data: Partial<ClientOrg>) => api.post<{ client: ClientOrg }>('/clients/orgs', data),
  update: (id: string, data: Partial<ClientOrg>) => api.put<{ client: ClientOrg }>(`/clients/orgs/${id}`, data),
  delete: (id: string) => api.delete(`/clients/orgs/${id}`),
  duplicate: (id: string, body?: { name?: string }, opts?: { withContacts?: boolean; withTemplates?: boolean }) =>
    api.post<{ client: ClientOrg }>(
      `/clients/orgs/${id}/duplicate`,
      body ?? {},
      { params: { with_contacts: opts?.withContacts ? '1' : undefined, with_templates: opts?.withTemplates ? '1' : undefined } }
    ),
  addContact: (id: string, data: Partial<ClientContact>) =>
    api.post<{ contact: ClientContact }>(`/clients/orgs/${id}/contacts`, data),
  updateContact: (id: string, contactId: string, data: Partial<ClientContact>) =>
    api.put<{ contact: ClientContact }>(`/clients/orgs/${id}/contacts/${contactId}`, data),
  deleteContact: (id: string, contactId: string) =>
    api.delete(`/clients/orgs/${id}/contacts/${contactId}`),
  addRequirementTemplate: (id: string, data: Partial<ClientRequirementTemplate>) =>
    api.post<{ template: ClientRequirementTemplate }>(`/clients/orgs/${id}/requirement-templates`, data),
  deleteRequirementTemplate: (id: string, tplId: string) =>
    api.delete(`/clients/orgs/${id}/requirement-templates/${tplId}`),
};

// ─── Jobs ────────────────────────────────────────────────────────────────────
export interface Job {
  id: string;
  job_code?: string | null;
  title: string;
  client_id?: string | null;
  client_name?: string | null;
  facility_id?: string | null;
  facility_name?: string | null;
  client_job_id?: string | null;
  profession?: string | null;
  specialty?: string | null;
  sub_specialty?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  duration_weeks?: number | null;
  job_type?: string | null;
  shift?: string | null;
  hours_per_week?: number | null;
  remote?: boolean;
  positions?: number;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  // Public careers board. null = auto-detect. true = correctional (kept off
  // the first cards). false = show in normal order even if the name matches.
  is_correctional?: boolean | null;
  // null inherits the facility. true/false is this job's own choice.
  requires_jail_application?: boolean | null;
  facility_requires_jail_application?: boolean | null;
  primary_recruiter_id?: string | null;
  primary_recruiter_name?: string | null;
  account_manager_id?: string | null;
  account_manager_name?: string | null;
  recruitment_manager_id?: string | null;
  recruitment_manager_name?: string | null;
  bill_rate?: number | null;
  pay_rate?: number | null;
  // Phase 1.2A — pay range
  pay_rate_min?: number | null;
  pay_rate_max?: number | null;
  margin?: number | null;
  stipend?: number | null;
  description?: string | null;
  summary?: string | null;
  job_ad?: string | null;
  boolean_search?: string | null;
  status: 'draft' | 'open' | 'on_hold' | 'filled' | 'closed' | 'cancelled';
  submission_count?: number;
  age_days?: number;
  created_at: string;
  updated_at: string;
}

export interface JobRequirement {
  id: string;
  job_id: string;
  kind: 'submission' | 'onboarding';
  bundle_id?: string | null;
  bundle_title?: string | null;
  ad_hoc: Array<{ type?: string; label: string; required?: boolean; notes?: string }>;
  notes?: string | null;
  created_at: string;
}

export interface MatchingCandidate {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  role?: string;
  specialties?: string[];
  city?: string;
  state?: string;
  years_experience?: number;
  match_score: number;
}

// ─── Contact discovery ────────────────────────────────────────────────────
export type ContactField = 'email' | 'phone';
export type ContactSource = 'duplicate_record' | 'conversation' | 'ghl' | 'self_reported' | 'apollo';
export interface ContactSuggestion {
  id: string;
  candidate_id: string;
  field: ContactField;
  value: string;
  source: ContactSource;
  confidence: 'high' | 'medium' | 'low';
  rationale: string | null;
  status: string;
  created_at: string;
}
export interface DiscoverResult {
  candidateId: string;
  name: string;
  found: number;
  stored: number;
  sourcesSearched: string[];
  providerConfigured: boolean;
  suggestions: ContactSuggestion[];
}
export interface ContactSourceStatus {
  sources: Record<string, { enabled: boolean; name?: string | null; note: string }>;
}

// ─── Bulk name lookup ─────────────────────────────────────────────────────
export interface BulkCandidateMatch {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  status: string | null;
  stage: string | null;
  has_contact: boolean;
}
export interface BulkNameLookupResult {
  input: string;
  match_count: number;
  matches: BulkCandidateMatch[];
}
export interface BulkNameLookupResponse {
  count: number;
  truncated: boolean;
  results: BulkNameLookupResult[];
}

export const contactDiscoveryApi = {
  status: () => api.get<ContactSourceStatus>('/contact-discovery/status'),
  discover: (candidateId: string) =>
    api.post<DiscoverResult>(`/contact-discovery/candidates/${candidateId}/discover`, {}, { timeout: 120_000 }),
  suggestions: (candidateId: string) =>
    api.get<{ suggestions: ContactSuggestion[] }>(`/contact-discovery/candidates/${candidateId}/suggestions`),
  accept: (suggestionId: string) =>
    api.post<{ ok: boolean; field?: string; value?: string }>(`/contact-discovery/suggestions/${suggestionId}/accept`),
  reject: (suggestionId: string) =>
    api.post<{ ok: boolean }>(`/contact-discovery/suggestions/${suggestionId}/reject`),
  captureLink: (candidateId: string, ttlDays?: number) =>
    api.post<{ url: string; expiresAt: string }>(`/contact-discovery/candidates/${candidateId}/capture-link`,
      ttlDays ? { ttl_days: ttlDays } : {}),
  bulkDiscover: (candidateIds?: string[]) =>
    api.post<{ processed: number; withFindings: number }>('/contact-discovery/bulk-discover',
      candidateIds?.length ? { candidate_ids: candidateIds } : {}, { timeout: 600_000 }),
  bulkNameLookup: (text: string) =>
    api.post<BulkNameLookupResponse>('/contact-discovery/bulk-name-lookup', { text }, { timeout: 120_000 }),
};

/** Public (no auth) — the candidate-facing "confirm your details" form. */
export const contactCaptureApi = {
  get: (token: string) =>
    publicPortal.get<{ first_name: string; company_name: string }>(`/contact-capture/${token}`),
  submit: (token: string, body: { email?: string; phone?: string }) =>
    publicPortal.post<{ ok: boolean }>(`/contact-capture/${token}`, body),
};

export const jobsApi = {
  list: (params?: {
    status?: string;
    client_id?: string;
    facility_id?: string;
    profession?: string;
    specialty?: string;
    priority?: string;
    recruiter_id?: string;
    search?: string;
  }) => api.get<{ jobs: Job[] }>('/jobs', { params }),
  get: (id: string) => api.get<{ job: Job; requirements: JobRequirement[] }>(`/jobs/${id}`),
  create: (data: Partial<Job>) => api.post<{ job: Job }>('/jobs', data),
  // Structure a pasted job description into job fields; returns follow-up
  // questions for important fields the AI couldn't determine.
  aiDraft: (description: string, answers?: Record<string, string>) =>
    api.post<{
      fields: Partial<Record<'title' | 'profession' | 'specialty' | 'city' | 'state' | 'job_type' | 'shift' | 'duration_weeks' | 'positions' | 'pay_rate_min' | 'pay_rate_max' | 'bill_rate' | 'description', string | number | null>>;
      questions: Array<{ key: string; question: string }>;
    }>('/jobs/ai-draft', { description, answers }),
  update: (id: string, data: Partial<Job>) => api.put<{ job: Job }>(`/jobs/${id}`, data),
  delete: (id: string) => api.delete(`/jobs/${id}`),
  // Incomplete = Open with no location and no pay (stub/test records). Preview
  // returns what a cleanup would archive vs. skip (jobs with applicants).
  incompletePreview: () => api.get<{
    total: number; archivable: number; with_applicants: number;
    jobs: Array<{ id: string; title: string; created_at: string; submission_count: number }>;
  }>('/jobs/incomplete'),
  // Set those incomplete jobs (without applicants) to draft. Reversible.
  cleanupIncomplete: () => api.post<{ archived: number }>('/jobs/cleanup-incomplete'),
  // Find OPEN jobs that repeat another open posting (same title/client/location).
  duplicatesPreview: () => api.get<{
    groups: number; duplicate_records: number; archivable: number; kept_with_applicants: number;
    sample: Array<{ id: string; title: string; city: string | null; state: string | null }>;
  }>('/jobs/duplicates'),
  // Archive the surplus duplicate copies (keeps one per group). Reversible.
  dedupe: () => api.post<{ archived: number }>('/jobs/dedupe'),
  duplicate: (id: string, body?: { title?: string }) =>
    api.post<{ job: Job }>(`/jobs/${id}/duplicate`, body ?? {}),
  addRequirement: (id: string, data: Partial<JobRequirement>) =>
    api.post<{ requirement: JobRequirement }>(`/jobs/${id}/requirements`, data),
  // Edit an existing requirement in place — the previous workflow
  // forced delete + re-add. See backend PUT handler.
  updateRequirement: (id: string, reqId: string, data: Partial<JobRequirement>) =>
    api.put<{ requirement: JobRequirement }>(`/jobs/${id}/requirements/${reqId}`, data),
  deleteRequirement: (id: string, reqId: string) => api.delete(`/jobs/${id}/requirements/${reqId}`),
  generateBoolean: (id: string) => api.post<{ boolean_search: string }>(`/jobs/${id}/ai/boolean`),
  generateJobAd: (id: string) => api.post<{ job_ad: string }>(`/jobs/${id}/ai/job-ad`),
  generateSummary: (id: string) => api.post<{ summary: string }>(`/jobs/${id}/ai/summary`),
  improveDescription: (id: string) =>
    api.post<{ original: string; improved: string; suggestions: string[] }>(`/jobs/${id}/ai/improve-description`),
  matchingCandidates: (id: string) =>
    api.get<{
      candidates: MatchingCandidate[];
      // Phase 1.2B — candidates already pitched to this job, returned
      // separately so the UI shows them in their own section instead
      // of duplicating them in the match list.
      already_submitted?: Array<{
        id: string;
        first_name: string;
        last_name: string;
        role: string | null;
        city: string | null;
        state: string | null;
        stage_key: string | null;
        ai_score: number | null;
        ai_fit_label: string | null;
        updated_at: string;
      }>;
    }>(`/jobs/${id}/matching-candidates`),

  // Auto-eSign triggers — attach an eSign template + a candidate-pipeline
  // stage. When a candidate transitions to that stage on this job, the
  // backend auto-creates and sends the document to the candidate.
  listEsignTriggers: (id: string) =>
    api.get<{ triggers: JobEsignTrigger[] }>(`/jobs/${id}/esign-triggers`),
  addEsignTrigger: (id: string, body: { template_id: string; trigger_stage: string; active?: boolean }) =>
    api.post<{ trigger: JobEsignTrigger }>(`/jobs/${id}/esign-triggers`, body),
  deleteEsignTrigger: (id: string, triggerId: string) =>
    api.delete(`/jobs/${id}/esign-triggers/${triggerId}`),
};

export interface JobEsignTrigger {
  id: string;
  job_id: string;
  template_id: string;
  trigger_stage: string;
  active: boolean;
  created_at: string;
  template_name: string;
  template_category: string | null;
}

// ─── Submissions ─────────────────────────────────────────────────────────────
export type FitLabel = 'excellent' | 'strong' | 'moderate' | 'weak' | 'poor';
export type GateStatus = 'ok' | 'missing' | 'pending' | 'unknown';

export interface SubmissionGap {
  category: string;
  gap: string;
  severity: 'low' | 'medium' | 'high';
}

// ─── Call Playbooks ────────────────────────────────────────────────────────
export interface CallPlaybookListItem {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  is_system: boolean;
  is_ai_generated: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  section_count: number;
  item_count: number;
}

export type CallPlaybookItemType =
  | 'checkbox'
  | 'text'
  | 'header'
  | 'single_select'
  | 'multi_select'
  | 'rating_5'
  | 'rating_10';

export interface CallPlaybookItem {
  id: string;
  section_id: string;
  label: string;
  item_type: CallPlaybookItemType;
  hint: string | null;
  options: string[] | null;
  sort_order: number;
}

// Per-item answer state. `checked` and `notes` are legacy/shared; `value`
// holds type-specific structured answers (single_select string,
// multi_select string[], rating number, text string).
export interface CallPlaybookRunItemState {
  item_id: string;
  checked: boolean;
  notes: string | null;
  value: { selected?: string | string[]; rating?: number; text?: string } | null;
}

export interface CallPlaybookSection {
  id: string;
  title: string;
  description: string | null;
  sort_order: number;
  items: CallPlaybookItem[];
}

export interface CallPlaybookFull {
  playbook: CallPlaybookListItem;
  sections: CallPlaybookSection[];
}

export interface CallPlaybookRun {
  id: string;
  playbook_id: string;
  person_name: string | null;
  status: 'in_progress' | 'completed' | 'archived';
  started_at: string;
  completed_at: string | null;
  // Only present on /runs/mine list responses (joined fields).
  playbook_title?: string;
  playbook_slug?: string | null;
  checked_count?: number;
  total_count?: number;
}

export interface CallPlaybookRunDetail {
  run: CallPlaybookRun;
  item_state: CallPlaybookRunItemState[];
}

export interface CallPlaybookAIChatReply {
  reply: string;
  ready: boolean;
}

export interface CallPlaybookSpec {
  title: string;
  description?: string;
  sections: Array<{
    title: string;
    description?: string;
    items: Array<{ label: string; hint?: string; item_type?: 'checkbox' | 'text' | 'header' }>;
  }>;
}

export const callPlaybooksApi = {
  list: () => api.get<{ playbooks: CallPlaybookListItem[] }>('/call-playbooks'),
  get: (id: string) => api.get<CallPlaybookFull>(`/call-playbooks/${id}`),
  create: (body: { title: string; description?: string; is_ai_generated?: boolean; sections?: CallPlaybookSpec['sections'] }) =>
    api.post<CallPlaybookFull>('/call-playbooks', body),
  update: (id: string, body: { title?: string; description?: string; is_active?: boolean }) =>
    api.put<{ playbook: CallPlaybookListItem }>(`/call-playbooks/${id}`, body),
  remove: (id: string) => api.delete(`/call-playbooks/${id}`),

  myRuns: () => api.get<{ runs: CallPlaybookRun[] }>('/call-playbooks/runs/mine'),
  startRun: (id: string, body?: { person_name?: string }) =>
    api.post<{ run: CallPlaybookRun }>(`/call-playbooks/${id}/runs`, body ?? {}),
  getRun: (runId: string) => api.get<CallPlaybookRunDetail>(`/call-playbooks/runs/${runId}`),
  patchRun: (runId: string, body: { person_name?: string }) =>
    api.patch<{ run: CallPlaybookRun }>(`/call-playbooks/runs/${runId}`, body),
  patchRunItem: (runId: string, itemId: string, body: { checked?: boolean; notes?: string | null; value?: unknown }) =>
    api.patch(`/call-playbooks/runs/${runId}/items/${itemId}`, body),
  completeRun: (runId: string) =>
    api.post<{ run: CallPlaybookRun }>(`/call-playbooks/runs/${runId}/complete`),
  deleteRun: (runId: string) => api.delete(`/call-playbooks/runs/${runId}`),

  aiChat: (messages: Array<{ role: 'user' | 'assistant'; content: string }>) =>
    api.post<CallPlaybookAIChatReply>('/call-playbooks/ai/chat', { messages }),
  aiGenerate: (messages: Array<{ role: 'user' | 'assistant'; content: string }>) =>
    api.post<{ spec: CallPlaybookSpec }>('/call-playbooks/ai/generate', { messages }),
};

export interface SubmissionGateMissing {
  source: 'bundle' | 'ad_hoc';
  kind: string;
  item_id?: string;
  label: string;
  required: boolean;
  status?: string;
}

export interface Submission {
  id: string;
  candidate_id: string;
  candidate_name?: string;
  candidate_role?: string;
  job_id: string;
  job_title?: string;
  job_code?: string;
  client_name?: string;
  facility_name?: string;
  recruiter_id?: string | null;
  recruiter_name?: string;
  stage_key?: string | null;
  stage_label?: string;
  stage_color?: string;
  // Per-job HR interview (lives on the submission)
  hr_interview_status?: 'pending' | 'in_progress' | 'complete';
  hr_interview?: Record<string, string> | null;
  hr_decision?: 'approved' | 'rejected' | null;
  hr_decision_reason?: string | null;
  hr_decision_at?: string | null;
  candidate_summary?: string | null;
  skill_ratings?: Array<{ skill: string; rating: number; notes?: string }>;
  bill_rate?: number | null;
  pay_rate?: number | null;
  stipend?: number | null;
  expenses?: number | null;
  margin?: number | null;
  pdf_url?: string | null;
  ai_score?: number | null;
  ai_score_breakdown?: {
    title: number;
    skills: number;
    certifications: number;
    experience: number;
    education: number;
    location: number;
  } | null;
  ai_fit_label?: FitLabel | null;
  ai_summary?: string | null;
  ai_gaps?: SubmissionGap[];
  gate_status?: GateStatus;
  gate_missing?: SubmissionGateMissing[];
  interview_scheduled_at?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmissionStageHistoryEntry {
  id: string;
  submission_id: string;
  from_stage?: string | null;
  to_stage: string;
  changed_by_name?: string;
  // Phase 1.3A — server-computed display name that falls back through:
  //   changed_by_name (denormalized at write time)
  //   → users.name via the changed_by FK
  //   → "Unknown user"
  // Always populated. Prefer this over changed_by_name directly.
  display_changed_by?: string;
  note?: string | null;
  created_at: string;
}

export const submissionsApi = {
  list: (params?: {
    candidate_id?: string;
    job_id?: string;
    recruiter_id?: string;
    stage_key?: string;
    fit_label?: string;
    gate_status?: string;
    search?: string;
  }) => api.get<{ submissions: Submission[] }>('/submissions', { params }),
  get: (id: string) =>
    api.get<{ submission: Submission; stage_history: SubmissionStageHistoryEntry[] }>(`/submissions/${id}`),
  create: (data: { candidate_id: string; job_id: string } & Partial<Submission>) =>
    api.post<{
      submission: Submission;
      gate: { status: GateStatus; missing: SubmissionGateMissing[]; total_required: number; satisfied: number };
      score: Submission['ai_score_breakdown'] | null;
    }>('/submissions', data),
  update: (id: string, data: Partial<Submission>) =>
    api.put<{ submission: Submission }>(`/submissions/${id}`, data),
  moveStage: (id: string, stage_key: string, note?: string) =>
    api.post<{
      submission: Submission;
      placement_created?: boolean;
      placement_id?: string | null;
      compliance_bundles_assigned?: Array<{ bundle_id: string; bundle_title?: string; created: number; skipped: number }>;
    }>(`/submissions/${id}/move-stage`, { stage_key, note }),
  // Per-job HR interview on a submission.
  hrInterview: (id: string, body: { action: 'start' | 'save' | 'complete'; answers?: Record<string, string>; decision?: 'approved' | 'rejected'; reason?: string; scope?: 'job' | 'company' }) =>
    api.post<{ success: boolean; submission: Submission }>(`/submissions/${id}/hr-interview`, body),
  jobSuggestions: (id: string) =>
    api.get<{ suggestions: Array<{ job_id: string; title: string; location: string; score: number | null; fit: string | null; reason: string | null }> }>(`/submissions/${id}/job-suggestions`),
  rescore: (id: string) => api.post<{ score: NonNullable<Submission['ai_score_breakdown']> }>(`/submissions/${id}/score`),
  recheckGate: (id: string) =>
    api.post<{ gate: { status: GateStatus; missing: SubmissionGateMissing[] } }>(`/submissions/${id}/recheck-gate`),
  generatePdf: (id: string) => api.post<{ pdf_url: string | null; status: string; message: string }>(`/submissions/${id}/pdf`),
  // Opens the generated submission packet in a new tab. Auth comes from
  // the same MSAL token used elsewhere — we forward it via Authorization
  // header on a fetch() rather than navigate(), because window.open
  // can't attach headers. The blob URL is short-lived (revoked when the
  // tab closes) so nothing leaks.
  openPdf: async (id: string, opts?: { internal?: boolean }): Promise<void> => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const url = `${base}/api/v1/submissions/${id}/pdf${opts?.internal ? '?internal=true' : ''}`;
    const headers = await (async () => {
      const h: Record<string, string> = {};
      try {
        const { IS_DEMO_BUILD, demoBearerToken } = await import('./demoMode');
        if (IS_DEMO_BUILD) { h.Authorization = `Bearer ${demoBearerToken()}`; return h; }
        const { msalInstance, apiTokenRequest } = await import('./msal');
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
          const tok = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
          if (tok.idToken) h.Authorization = `Bearer ${tok.idToken}`;
        }
      } catch { /* fall through unauthenticated — will 401 */ }
      return h;
    })();
    const resp = await fetch(url, { headers });
    if (!resp.ok) throw new Error(`PDF download failed (${resp.status})`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    // Revoke after a beat — the new tab has already loaded the blob by then.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
};

// ─── Admin file library / importer ───────────────────────────────────────────
export interface AdminFile {
  id: string;
  filename: string;
  relative_path?: string | null;
  mime: string;
  size_bytes: number;
  category: string | null;
  notes: string | null;
  uploaded_at: string;
  uploaded_by: string | null;
  uploaded_by_name?: string | null;
  has_text: boolean;
}

// Files at/under this size go in a single batched request; larger ones are
// uploaded in chunks so no single request risks the server's time/size/memory
// limits. CHUNK_SIZE is the per-chunk payload; BATCH_LIMIT caps a small-file
// batch's combined size.
const ADMIN_FILE_CHUNK_THRESHOLD = 10 * 1024 * 1024; // 10 MB
const ADMIN_FILE_CHUNK_SIZE = 8 * 1024 * 1024;       // 8 MB
const ADMIN_FILE_BATCH_LIMIT = 10 * 1024 * 1024;     // 10 MB total per batch

function relPathOf(f: File): string {
  return (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
}

export const adminFilesApi = {
  list: (params?: { category?: string; q?: string }) =>
    api.get<{ files: AdminFile[] }>('/admin-files', { params }),

  // Batched single-shot upload of small files (one HTTP request).
  uploadBatch: (files: File[], meta?: { category?: string; notes?: string }) => {
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    // Preserve folder structure for directory uploads. Browsers only put the
    // base filename in the multipart part, so send the per-file relative paths
    // (from webkitRelativePath, in the same order) as a parallel JSON array.
    fd.append('paths', JSON.stringify(files.map(relPathOf)));
    if (meta?.category) fd.append('category', meta.category);
    if (meta?.notes) fd.append('notes', meta.notes);
    return api.post<{ files: AdminFile[] }>('/admin-files', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
  },

  // Chunked upload primitives for a single large file.
  initChunked: (meta: { filename: string; relative_path: string; mime: string; category?: string; notes?: string }) =>
    api.post<{ id: string }>('/admin-files/chunked/init', meta),
  uploadChunk: (id: string, idx: number, blob: Blob) => {
    const fd = new FormData();
    fd.append('idx', String(idx));
    fd.append('chunk', blob);
    return api.post<{ ok: boolean }>(`/admin-files/chunked/${id}/chunk`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
  },
  completeChunked: (id: string, totalChunks: number) =>
    api.post<{ file: AdminFile }>(`/admin-files/chunked/${id}/complete`, { total_chunks: totalChunks }),
  abortChunked: (id: string) => api.post(`/admin-files/chunked/${id}/abort`),

  // High-level orchestrator: routes each file to batched or chunked upload and
  // reports overall progress (0–100) across all bytes.
  uploadFiles: async (
    files: File[],
    meta?: { category?: string; notes?: string },
    onProgress?: (pct: number) => void,
  ): Promise<void> => {
    const totalBytes = files.reduce((s, f) => s + f.size, 0) || 1;
    let uploaded = 0;
    const report = () => onProgress?.(Math.min(100, Math.round((uploaded / totalBytes) * 100)));
    report();

    const small = files.filter((f) => f.size <= ADMIN_FILE_CHUNK_THRESHOLD);
    const large = files.filter((f) => f.size > ADMIN_FILE_CHUNK_THRESHOLD);

    // Small files → batches kept under BATCH_LIMIT.
    let batch: File[] = [];
    let batchSize = 0;
    const flush = async () => {
      if (batch.length === 0) return;
      await adminFilesApi.uploadBatch(batch, meta);
      uploaded += batchSize;
      report();
      batch = [];
      batchSize = 0;
    };
    for (const f of small) {
      if (batch.length > 0 && batchSize + f.size > ADMIN_FILE_BATCH_LIMIT) await flush();
      batch.push(f);
      batchSize += f.size;
    }
    await flush();

    // Large files → chunked, one file at a time.
    for (const f of large) {
      const { data } = await adminFilesApi.initChunked({
        filename: f.name,
        relative_path: relPathOf(f),
        mime: f.type || 'application/octet-stream',
        category: meta?.category,
        notes: meta?.notes,
      });
      const id = data.id;
      const totalChunks = Math.max(1, Math.ceil(f.size / ADMIN_FILE_CHUNK_SIZE));
      try {
        for (let i = 0; i < totalChunks; i++) {
          const start = i * ADMIN_FILE_CHUNK_SIZE;
          const end = Math.min(f.size, start + ADMIN_FILE_CHUNK_SIZE);
          await adminFilesApi.uploadChunk(id, i, f.slice(start, end));
          uploaded += end - start;
          report();
        }
        await adminFilesApi.completeChunked(id, totalChunks);
      } catch (e) {
        try { await adminFilesApi.abortChunked(id); } catch { /* best effort */ }
        throw e;
      }
    }
  },
  preview: (id: string) =>
    api.get<{ filename: string; mime: string; text: string; has_text: boolean }>(`/admin-files/${id}/preview`),
  inventory: () =>
    api.get<{
      files: Array<{ id: string; filename: string; size_bytes: number; chunked: boolean }>;
      totals: { file_count: number; total_file_bytes: string; db_size: string; db_size_bytes: string };
      largest_tables: Array<{ table: string; size: string; size_bytes: string }>;
    }>('/admin-files/inventory'),
  remove: (id: string) => api.delete(`/admin-files/${id}`),
  // Download with the same authenticated-blob pattern as submission PDFs:
  // window.open can't attach the MSAL bearer token, so fetch() it and open
  // a short-lived blob URL.
  download: async (id: string, filename: string): Promise<void> => {
    const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    const url = `${base}/api/v1/admin-files/${id}/download`;
    const headers: Record<string, string> = {};
    try {
      const { IS_DEMO_BUILD, demoBearerToken } = await import('./demoMode');
      if (IS_DEMO_BUILD) {
        headers.Authorization = `Bearer ${demoBearerToken()}`;
      } else {
        const { msalInstance, apiTokenRequest } = await import('./msal');
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
          const tok = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
          if (tok.idToken) headers.Authorization = `Bearer ${tok.idToken}`;
        }
      }
    } catch { /* fall through unauthenticated — will 401 */ }
    const resp = await fetch(url, { headers });
    if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
};

// ─── Rate Cards (Finance) ────────────────────────────────────────────────────
export interface RateCard {
  id: string;
  client_name: string;
  specialty: string;
  bill_rate: string | number | null;
  pay_rate: string | number | null;
  rate_type: string;
  unit: string;
  effective_start: string | null;
  effective_end: string | null;
  notes: string | null;
  taxable_hourly: string | number | null;
  housing_stipend_weekly: string | number | null;
  mie_stipend_weekly: string | number | null;
  hours_per_week: string | number | null;
  stipend_rule_source: string | null;
  stipend_calcs?: {
    hours_per_week: number;
    taxable_weekly: number;
    tax_free_weekly: number;
    weekly_gross: number;
    blended_hourly: number;
    taxable_pct: number;
    tax_free_pct: number;
    weekly_margin_estimate: number | null;
  } | null;
}

export const rateCardsApi = {
  list: (client?: string) => api.get<{ rate_cards: RateCard[] }>('/rate-cards', { params: client ? { client } : undefined }),
  create: (data: Partial<RateCard>) => api.post<{ rate_card: RateCard }>('/rate-cards', data),
  update: (id: string, data: Partial<RateCard>) => api.put<{ rate_card: RateCard }>(`/rate-cards/${id}`, data),
  remove: (id: string) => api.delete(`/rate-cards/${id}`),
};

// ─── Pipeline stages (configurable) ──────────────────────────────────────────
export interface PipelineStage {
  id: string;
  tenant_id: string;
  key: string;
  label: string;
  sort_order: number;
  color?: string | null;
  is_terminal: boolean;
  stale_after_days?: number | null;
  active: boolean;
  description?: string | null;
}

export const pipelineStagesApi = {
  list: () => api.get<{ stages: PipelineStage[]; tracker_stages?: PipelineStage[] }>('/pipeline-stages'),
  create: (data: Partial<PipelineStage>) => api.post<{ stage: PipelineStage }>('/pipeline-stages', data),
  update: (key: string, data: Partial<PipelineStage>) =>
    api.put<{ stage: PipelineStage }>(`/pipeline-stages/${key}`, data),
  disable: (key: string) => api.delete(`/pipeline-stages/${key}`),
};

export interface KanbanColumn extends PipelineStage {
  count: number;
  items: Array<{
    id: string;
    candidate_id: string;
    candidate_name: string;
    candidate_role?: string;
    job_id: string;
    job_title: string;
    job_code?: string;
    client_name?: string;
    facility_name?: string;
    recruiter_name?: string;
    stage_key?: string;
    ai_score?: number | null;
    ai_fit_label?: FitLabel | null;
    gate_status?: GateStatus;
    days_in_stage: number;
    is_stale: boolean;
    updated_at: string;
  }>;
}

export const kanbanApi = {
  get: () => api.get<{ stages: KanbanColumn[]; total: number }>('/pipeline/kanban'),
};

// ─── Recruiter tasks ────────────────────────────────────────────────────────
export interface RecruiterTask {
  id: string;
  title: string;
  description?: string | null;
  task_type?: 'call' | 'meeting' | 'todo' | 'follow_up' | 'email' | 'sms' | 'other' | null;
  due_at?: string | null;
  timezone?: string | null;
  assigned_to?: string | null;
  assigned_to_name?: string;
  escalate_to?: string | null;
  reminder_minutes_before?: number | null;
  recurrence?: string | null;
  notify_email?: boolean;
  notify_sms?: boolean;
  candidate_id?: string | null;
  candidate_name?: string;
  job_id?: string | null;
  job_title?: string;
  submission_id?: string | null;
  client_id?: string | null;
  client_name?: string;
  status: 'open' | 'done' | 'snoozed' | 'cancelled';
  is_overdue?: boolean;
  completed_at?: string | null;
  created_by?: string | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  // When TRUE, only the creator + assignee can see this task. Used for
  // personal/CEO items that shouldn't bleed into team task views.
  is_private?: boolean;
  // Captured during morning huddle review when a commitment is still
  // open — explains the blocker for the next-day check.
  barrier_note?: string | null;
}

// ─── Integration status (Phase 5 QA) ───────────────────────────────────────
export interface IntegrationStatus {
  key: string;
  name: string;
  connected: boolean;
  required_env: string[];
  docs_url?: string;
  description?: string;
}

export const integrationsStatusApi = {
  status: () => api.get<{ integrations: IntegrationStatus[] }>('/integrations/status'),
  emailConfig: () => api.get<{
    microsoft_credentials: boolean;
    sender_mailbox_configured: boolean;
    sender_mailbox: string | null;
    ready: boolean;
  }>('/diagnostics/email-config'),
  sendTestEmail: (to?: string) =>
    api.post<{ ok: boolean; to?: string; message?: string; error?: string }>('/diagnostics/test-email', { to }),
};

// ─── Outbound email log (T2 honesty log) ────────────────────────────────────
export interface OutboundEmailLogEntry {
  id: string;
  to_email: string;
  subject: string | null;
  status: 'sent' | 'blocked' | 'failed' | 'suppressed';
  reason: string | null;
  context: string | null;
  created_at: string;
}

export const outboundEmailLogApi = {
  list: (params?: { status?: string; limit?: number; offset?: number }) => {
    const q = new URLSearchParams();
    if (params?.status) q.set('status', params.status);
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    const qs = q.toString();
    return api.get<{
      log: OutboundEmailLogEntry[];
      total: number;
      limit: number;
      offset: number;
      counts_7d: Record<'sent' | 'blocked' | 'failed' | 'suppressed', number>;
    }>(`/emails/outbound-log${qs ? `?${qs}` : ''}`);
  },
};

// ─── Global search (Phase 9) ────────────────────────────────────────────────
export type SearchResultType = 'candidate' | 'job' | 'submission' | 'client' | 'facility' | 'staff';

export interface SearchResult {
  type: SearchResultType;
  id: string;
  label: string;
  sublabel?: string;
  nav: string;
}

export const searchApi = {
  query: (q: string) => api.get<{ results: SearchResult[] }>('/search', { params: { q } }),
};

// ─── Notification preferences (per-user) ────────────────────────────────────
export interface NotificationPrefs {
  email_enabled: boolean;
  sms_enabled: boolean;
  inapp_enabled: boolean;
  notify_credential_expiry: boolean;
  notify_missing_document: boolean;
  notify_compliance_assign: boolean;
  notify_placement_change: boolean;
  notify_task_reminder: boolean;
  notify_submission_update: boolean;
  notify_sms_approval: boolean;
  notify_system_announcement: boolean;
  digest_schedule: 'off' | 'daily' | 'weekly';
  digest_time_of_day: string;
  quiet_hours_enabled: boolean;
  quiet_start: string;
  quiet_end: string;
  updated_at?: string;
}

export const notificationPrefsApi = {
  get: () => api.get<{ prefs: NotificationPrefs }>('/notification-prefs/me'),
  save: (patch: Partial<NotificationPrefs>) =>
    api.put<{ prefs: NotificationPrefs }>('/notification-prefs/me', patch),
};

// ─── ATS Reports (Phase 4) ──────────────────────────────────────────────────
export interface AtsReportsOverview {
  funnel: Array<{ key: string; label: string; color?: string; sort_order: number; is_terminal: boolean; count: number }>;
  recruiter_leaderboard: Array<{
    id: string; name?: string; email?: string;
    submissions_30d: number; placements: number; open_jobs: number;
  }>;
  jobs_at_risk: Array<{
    id: string; job_code?: string; title: string; profession?: string; specialty?: string;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    city?: string; state?: string; age_days: number; submission_count: number;
    client_name?: string; recruiter_name?: string;
  }>;
  submission_to_placement: {
    total: number; placed: number; client_submitted: number;
    interview: number; offer: number; lost: number; placement_rate: number;
  };
  active_jobs_summary: {
    open_jobs?: number; on_hold_jobs?: number; filled_jobs?: number;
    urgent_open?: number; total_positions_open?: number;
  };
  tasks: {
    open_tasks?: number; overdue?: number; due_today?: number; completed_7d?: number;
  };
  source_of_hire: Array<{
    source: string; applicants: number; submissions: number; placements: number;
    conversion_pct: number; median_days_to_fill: number | null;
  }>;
}

// ─── CEO Performance Analytics ────────────────────────────────────
export interface PerformanceHeadline {
  submissions_30d: number;
  placements_30d: number;
  active_placements: number;
  avg_time_to_fill_30d: string | null;
  conversion_pct_30d: string | null;
}
export interface PerformanceWeek {
  week_start: string;
  submissions: number;
  placements: number;
  avg_days_to_fill: string | null;
}
export interface PerformanceRecruiter {
  recruiter_id: string;
  recruiter_name: string | null;
  recruiter_email: string;
  submissions: number;
  placements: number;
  conversion_pct: string | null;
  avg_days_to_fill: string | null;
  active_candidates: number;
  days_since_hire: number | null;
}
export interface PerformanceFacility {
  facility_id: string;
  facility_name: string;
  client_name: string | null;
  placements: number;
  active_placements: number;
  completed_placements: number;
  cancelled_placements: number;
  retention_pct: string | null;
}
export interface PerformanceAnalytics {
  headline: PerformanceHeadline | null;
  weekly_trend: PerformanceWeek[];
  recruiter_perf: PerformanceRecruiter[];
  facility_perf: PerformanceFacility[];
  generated_at: string;
}
export const analyticsApi = {
  performance: () => api.get<PerformanceAnalytics>('/analytics/performance'),
};

export const atsReportsApi = {
  overview: (params?: { recruiter_id?: string; me?: boolean }) =>
    api.get<AtsReportsOverview>('/ats-reports/overview', {
      params: params?.me
        ? { me: 'true' }
        : params?.recruiter_id
          ? { recruiter_id: params.recruiter_id }
          : undefined,
    }),
};

// ─── Weekly Recruiting Report ───────────────────────────────────────────────
// One Monday–Sunday snapshot of everything that moved: pipeline movements,
// interviews, new candidates/submissions, open jobs and per-recruiter counters.
// `week` is any date inside the wanted week (YYYY-MM-DD); omit it for the
// current week. Backs /weekly-report.
export interface WeeklyReportPeriod {
  start: string;
  end: string;
  label: string;
  is_current_week: boolean;
}
export type WeeklyMovementKind =
  | 'advanced' | 'moved_back' | 'placed' | 'withdrew'
  | 'rejected' | 'not_joined' | 'reopened' | 'entered' | 'other';
export interface MovementRow {
  candidate_id: string;
  candidate_name: string;
  job_title: string | null;
  from_stage: string | null;
  from_label: string | null;
  to_stage: string;
  to_label: string;
  kind: WeeklyMovementKind;
  moved_at: string;
  moved_by_name: string | null;
  note: string | null;
}
export interface InterviewRow {
  id: string;
  candidate_id: string;
  candidate_name: string;
  job_title: string | null;
  round: string | null;
  mode: string | null;
  scheduled_at: string | null;
  status: string | null;
  outcome: string | null;
  booked_at: string;
}
export interface NewCandidateRow {
  id: string;
  name: string;
  role: string | null;
  source: string | null;
  recruiter_name: string | null;
  stage: string | null;
  stage_label: string | null;
  created_at: string;
}
export interface SubmissionRow {
  id: string;
  candidate_id: string;
  candidate_name: string;
  job_id: string;
  job_title: string;
  stage_key: string;
  stage_label: string;
  recruiter_name: string | null;
  created_at: string;
}
export interface JobRow {
  id: string;
  title: string;
  city: string | null;
  state: string | null;
  status: string;
  created_at: string;
  days_open: number;
  submissions_count: number;
  placed_count: number;
}
export interface RecruiterActivityRow {
  user_id: string;
  name: string;
  candidates_added: number;
  submissions: number;
  interviews_booked: number;
  moves: number;
  placed: number;
}
export interface WeeklyReportTotals {
  new_candidates: number;
  website_applications: number;
  submissions: number;
  interviews_booked: number;
  interviews_held: number;
  advanced: number;
  moved_back: number;
  placed: number;
  withdrew: number;
  rejected: number;
  not_joined: number;
  jobs_opened: number;
  jobs_open_now: number;
}
export interface WeeklyReportMovements {
  advanced: MovementRow[];
  moved_back: MovementRow[];
  placed: MovementRow[];
  withdrew: MovementRow[];
  rejected: MovementRow[];
  not_joined: MovementRow[];
  reopened: MovementRow[];
}
export interface WeeklyRecruitingReport {
  period: WeeklyReportPeriod;
  previous_period: { start: string; end: string };
  totals: WeeklyReportTotals;
  previous_totals: WeeklyReportTotals;
  movements: WeeklyReportMovements;
  interviews: InterviewRow[];
  new_candidates: NewCandidateRow[];
  submissions: SubmissionRow[];
  open_jobs: JobRow[];
  jobs_opened: JobRow[];
  recruiter_activity: RecruiterActivityRow[];
  generated_at: string;
}
export interface WeeklyReportEmailResult {
  sent: boolean;
  to: string[];
  message?: string;
}

export const weeklyReportApi = {
  get: (params?: { week?: string; recruiter_id?: string }) =>
    api.get<WeeklyRecruitingReport>('/weekly-report', {
      params: {
        ...(params?.week ? { week: params.week } : {}),
        ...(params?.recruiter_id ? { recruiter_id: params.recruiter_id } : {}),
      },
    }),
  // Returns the .xlsx as a Blob — the caller turns it into a download
  // (Content-Disposition carries the filename).
  exportXlsx: (params?: { week?: string }) =>
    api.get<Blob>('/weekly-report/export.xlsx', {
      params: params?.week ? { week: params.week } : undefined,
      responseType: 'blob',
    }),
  email: (body?: { week?: string; to?: string[] }) =>
    api.post<WeeklyReportEmailResult>('/weekly-report/email', body ?? {}),
};

// ─── Saved candidate views (Phase 4) ────────────────────────────────────────
export interface CandidateSavedView {
  id: string;
  user_id?: string | null;
  name: string;
  filters: Record<string, unknown>;
  is_shared: boolean;
  created_at: string;
}

export const candidateSavedViewsApi = {
  list: () => api.get<{ views: CandidateSavedView[] }>('/candidates/saved-views'),
  create: (name: string, filters: Record<string, unknown>, is_shared = false) =>
    api.post<{ view: CandidateSavedView }>('/candidates/saved-views', { name, filters, is_shared }),
  delete: (id: string) => api.delete(`/candidates/saved-views/${id}`),
};

export interface RecruiterTaskAIDraftResult {
  title: string;
  task_type: 'call' | 'meeting' | 'email' | 'sms' | 'follow_up' | 'todo' | 'other';
  due_at: string | null;
  description: string;
  reminder_minutes_before: number | null;
}

export const tasksApi = {
  list: (params?: {
    assigned_to?: string;
    candidate_id?: string;
    job_id?: string;
    submission_id?: string;
    client_id?: string;
    status?: string;
    overdue?: string;
    due_today?: string;
  }) => api.get<{ tasks: RecruiterTask[] }>('/tasks', { params }),
  create: (data: Partial<RecruiterTask>) => api.post<{ task: RecruiterTask }>('/tasks', data),
  update: (id: string, data: Partial<RecruiterTask>) => api.put<{ task: RecruiterTask }>(`/tasks/${id}`, data),
  complete: (id: string) => api.post<{ task: RecruiterTask }>(`/tasks/${id}/complete`),
  cancel: (id: string) => api.delete(`/tasks/${id}`),

  // AI-assisted task creation — same contract as the Action Plan wizard but
  // tuned for recruiter workflows. See backend/src/routes/recruiterTasks.ts.
  aiNextQuestion: (data: { goal: string; answers: { question: string; answer: string }[] }) =>
    api.post<{ done: boolean; question?: string }>('/tasks/ai-next-question', data),
  aiDraft: (data: { goal: string; answers: { question: string; answer: string }[] }) =>
    api.post<RecruiterTaskAIDraftResult>('/tasks/ai-draft', data),
};

// ─── Recruiting playbooks ────────────────────────────────────────────────
// Structured call scripts a recruiter opens during a candidate phone
// screen. The Harris County Jail playbook is seeded at boot; admins
// can edit copy via PUT (UI for editing is a follow-up).

export interface RecruitingPlaybookSummary {
  id: string;
  slug: string;
  title: string;
  client_name: string | null;
  work_location: string | null;
  address: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PlaybookPositionOverview {
  contract_length?: string;
  schedule_panama?: string;
  schedule_note?: string;
  insurance_note?: string;
  /** Benefit details, e.g. 60-day medical-insurance eligibility + optional 401(k). */
  benefits_note?: string;
}
export interface PlaybookPayRate {
  role: string;
  /** Higher rate, no insurance. Preferred over rate_range when present. */
  non_benefited_rate?: string;
  /** Lower rate that includes medical-insurance eligibility. */
  benefited_rate?: string;
  rate_range: string;
  insurance_rate: string;
  shift_availability: string;
}
export interface PlaybookQuestion {
  number: number;
  question: string;
  hint?: string;
  critical?: boolean;
}
export interface PlaybookQuestionSection {
  section: string;
  items: PlaybookQuestion[];
}
export interface PlaybookSampleScript {
  opening?: string;
  brief_pitch?: string;
  transition?: string;
  closing_interested?: string;
  closing_not_interested?: string;
}
export interface PlaybookCheatSheetEntry {
  label: string;
  value: string;
}

export interface RecruitingPlaybook extends RecruitingPlaybookSummary {
  client_context_note: string | null;
  recruiter_title: string | null;
  position_overview: PlaybookPositionOverview | null;
  pay_rates: PlaybookPayRate[] | null;
  requirements: Record<string, string[]> | null;
  interview_questions: PlaybookQuestionSection[] | null;
  next_steps: string[] | null;
  sms_template: string | null;
  sample_script: PlaybookSampleScript | null;
  cheat_sheet: PlaybookCheatSheetEntry[] | null;
}

// Result shape for materialize-jobs — one entry per role in the
// playbook's pay_rates, plus the parent client/facility that were
// resolved or auto-created.
export interface MaterializeJobsResult {
  client: { id: string; name: string };
  facility: { id: string; name: string; address: string | null };
  jobs: Array<{ role: string; status: 'created' | 'existing'; id: string; title: string }>;
  summary: { created: number; existing: number };
}

export const playbooksApi = {
  list: () =>
    api.get<{ playbooks: RecruitingPlaybookSummary[] }>('/playbooks'),
  // Accepts both UUID and slug, so deep links via slug work too.
  get: (idOrSlug: string) =>
    api.get<{ playbook: RecruitingPlaybook }>(`/playbooks/${idOrSlug}`),
  update: (id: string, data: Partial<RecruitingPlaybook>) =>
    api.put<{ playbook: RecruitingPlaybook }>(`/playbooks/${id}`, data),
  // Create a brand-new call script. JSON sections may be omitted; the
  // backend defaults them to empty and derives a unique slug from the title.
  create: (data: Partial<RecruitingPlaybook> & { title: string }) =>
    api.post<{ playbook: RecruitingPlaybook }>('/playbooks', data),
  // Spawns one draft Job per role in the playbook's pay_rates table.
  // Idempotent on playbook + role + facility, including after a rename.
  // Drafts stay off the public careers board; pay is not copied.
  materializeJobs: (idOrSlug: string) =>
    api.post<MaterializeJobsResult>(`/playbooks/${idOrSlug}/materialize-jobs`),
  duplicate: (idOrSlug: string, body?: { title?: string; slug?: string }) =>
    api.post<{ playbook: RecruitingPlaybook }>(`/playbooks/${idOrSlug}/duplicate`, body ?? {}),
};

// ─── AI Advisors ─────────────────────────────────────────────────────────────
// Chat-with-an-advisor product, sister to the existing AI Team (task
// orchestration). v1 = individual chat only.

export interface AdvisorAgent {
  id: string;
  name: string;
  title: string;
  shortBio: string;
  description: string;
  emoji: string;
  color: string;
  entities: Array<'FNS' | 'NJN' | 'MEMS' | 'ALL'>;
  expertise: string[];
  greeting: string;
}

export interface AdvisorThread {
  id: string;
  type: 'individual' | 'group';
  agent_id: string | null;
  group_name: string | null;
  group_agent_ids: string[];
  title: string;
  created_at: string;
  updated_at: string;
  archived: boolean;
}

export interface AdvisorMessageAttachment {
  blob_id?: string;
  filename: string;
  mime: string;
  size: number;
  preview?: string;
}

export interface AdvisorMessage {
  id: string;
  thread_id: string;
  sender_type: 'user' | 'agent' | 'system';
  sender_id: string | null;
  content: string;
  attachments?: AdvisorMessageAttachment[];
  created_at: string;
  sequence: number;
}

export interface AdvisorMemory {
  id: string;
  user_id: string;
  agent_id: string | null;
  content: string;
  created_at: string;
}

export interface AdvisorSlashCommand {
  command: string;
  description: string;
  usage: string;
  groupOnly?: boolean;
  individualOnly?: boolean;
}

export type AdvisorStreamEvent =
  | { type: 'start'; agentId: string }
  | { type: 'delta'; agentId: string; text: string }
  | { type: 'agentDone'; agentId: string; messageId: string; content: string }
  | { type: 'system'; content: string; messageId: string }
  | { type: 'done'; threadId: string }
  | { type: 'error'; message: string };

export const aiAdvisorsApi = {
  agents: () =>
    api.get<{ agents: AdvisorAgent[] }>('/ai-advisors/agents'),
  commands: () =>
    api.get<{ commands: AdvisorSlashCommand[] }>('/ai-advisors/commands'),
  listThreads: (opts?: { archived?: boolean }) =>
    api.get<{ threads: AdvisorThread[] }>('/ai-advisors/threads', {
      params: opts?.archived ? { archived: '1' } : undefined,
    }),
  getThread: (id: string) =>
    api.get<{ thread: AdvisorThread; messages: AdvisorMessage[] }>(`/ai-advisors/threads/${id}`),
  // Creates the thread + first user message in one shot. The agent's
  // streamed reply comes from the next sendMessage call.
  createThread: (data: { agent_id: string; content: string }) =>
    api.post<{ thread: AdvisorThread }>('/ai-advisors/threads', data),
  createGroupThread: (data: { group_name: string; group_agent_ids: string[]; content: string }) =>
    api.post<{ thread: AdvisorThread }>('/ai-advisors/threads/group', data),
  updateThread: (id: string, data: Partial<Pick<AdvisorThread, 'title' | 'archived'>>) =>
    api.patch<{ thread: AdvisorThread }>(`/ai-advisors/threads/${id}`, data),
  archiveThread: (id: string) =>
    api.delete<{ success: boolean }>(`/ai-advisors/threads/${id}`),
  listMemories: () =>
    api.get<{ memories: AdvisorMemory[] }>('/ai-advisors/memories'),
  deleteMemory: (id: string) =>
    api.delete<{ success: boolean }>(`/ai-advisors/memories/${id}`),
  // Streams the agent's reply via SSE. NOT a regular axios call — we
  // use fetch directly so we can read the response body as a stream.
  // For group threads multiple agents may stream in sequence, each
  // bracketed by 'start' / 'agentDone' events.
  streamMessage: async function* (
    threadId: string,
    content: string,
    getToken: () => Promise<string | null>,
    files: File[] = [],
  ): AsyncGenerator<AdvisorStreamEvent, void, unknown> {
    const token = await getToken();
    // When files are attached, switch to multipart so multer can read them.
    // No Content-Type header — browser sets the multipart boundary itself.
    const useMultipart = files.length > 0;
    const init: RequestInit = useMultipart
      ? (() => {
          const fd = new FormData();
          fd.append('content', content);
          for (const f of files) fd.append('files', f, f.name);
          return {
            method: 'POST',
            headers: {
              Accept: 'text/event-stream',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: fd,
          };
        })()
      : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ content }),
        };
    yield* streamSSE<AdvisorStreamEvent>(`/ai-advisors/threads/${threadId}/messages`, init);
  },
};

// ─── Shared SSE reader ───────────────────────────────────────── [phase5 track-B]
// One fetch-based Server-Sent-Events reader for every streaming AI endpoint
// (advisors + the aiBrain copilot). Yields each parsed `data:` JSON frame;
// heartbeats (`: hb`) and malformed frames are skipped. On a non-OK response
// it yields a single `{ type: 'error', message }` and returns, so callers can
// render the failure in-chat without a throw. `path` is relative to /api/v1.
export async function* streamSSE<T = unknown>(
  path: string,
  init: RequestInit,
): AsyncGenerator<T, void, unknown> {
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
  const resp = await fetch(`${base}/api/v1${path}`, init);
  if (!resp.ok || !resp.body) {
    const errText = await resp.text().catch(() => '');
    yield { type: 'error', message: errText || `HTTP ${resp.status}` } as unknown as T;
    return;
  }
  // Events are separated by a blank line; each event's data lines start with
  // "data:". Accumulate and emit frames as they complete.
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let ix: number;
    while ((ix = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, ix);
      buf = buf.slice(ix + 2);
      for (const line of block.split('\n')) {
        if (!line.startsWith('data:')) continue;
        const payload = line.slice(5).trim();
        if (!payload) continue;
        try {
          yield JSON.parse(payload) as T;
        } catch {
          // Heartbeats and malformed events are ignored — keep streaming.
        }
      }
    }
  }
}

// ─── AI Brain copilot ("Laure") streaming client ─────────────────────────────
// The canonical tool-calling copilot. POST /ai-brain/chat streams SSE:
//   { type: 'delta', text }   — append a token to the reply
//   { type: 'tool_use', name }— a round is running a live database tool
//   { type: 'draft', ... }    — an approve-first draft staged
//   { type: 'done', ... }     — final metadata (context_used, tools_used, …)
//   { type: 'error', message }
export type BrainStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'tool_use'; name: string }
  | { type: 'draft'; draft_id: string; kind: string; preview: string }
  | {
      type: 'done';
      response?: string;
      context_used?: boolean;
      clarification_created?: boolean;
      model?: string;
      tools_used?: Array<{ name: string; summary: string }>;
      drafts?: Array<{ draft_id: string; kind: string; preview: string }>;
      guard?: { denied: boolean };
    }
  | { type: 'error'; message: string };

export interface BrainChatBody {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  sources?: string[];
  pageContext?: { page?: string; entityType?: string; entityId?: string };
}

export const aiBrainApi = {
  streamChat: async function* (
    body: BrainChatBody,
    getToken: () => Promise<string | null>,
  ): AsyncGenerator<BrainStreamEvent, void, unknown> {
    const token = await getToken();
    yield* streamSSE<BrainStreamEvent>('/ai-brain/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
  },
};

// ─── Time Tracking — Entries ─────────────────────────────────────────────────

export type TtEntrySource = 'timer' | 'manual' | 'kiosk' | 'auto' | 'imported';
export type TtEntryStatus = 'draft' | 'submitted' | 'approved' | 'locked';

export interface TtEntry {
  id: string;
  user_id: string;
  workspace_id: string;
  project_id: string | null;
  task_id: string | null;
  start_at: string;
  end_at: string | null;
  duration_seconds: number | null;
  description: string | null;
  billable: boolean;
  source: TtEntrySource;
  status: TtEntryStatus;
  approved_by: string | null;
  approved_at: string | null;
  tag_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface TtEntryListParams {
  user_id?: string;
  project_id?: string;
  from?: string;
  to?: string;
  billable?: '0' | '1';
  status?: TtEntryStatus;
  tag_id?: string;
  limit?: number;
}

export interface TtCreateEntryBody {
  start_at: string;
  end_at?: string | null;
  project_id?: string | null;
  task_id?: string | null;
  description?: string | null;
  billable?: boolean;
  source?: TtEntrySource;
  tag_ids?: string[] | null;
}

export interface TtPatchEntryBody {
  start_at?: string;
  end_at?: string | null;
  project_id?: string | null;
  task_id?: string | null;
  description?: string | null;
  billable?: boolean;
  status?: TtEntryStatus;
  tag_ids?: string[] | null;
  reason?: string;
}

export const ttEntriesApi = {
  /** GET /api/v1/time-tracking/entries — list with optional filters */
  list: (params?: TtEntryListParams) =>
    api.get<{ entries: TtEntry[] }>('/time-tracking/entries', { params }),

  /** GET /api/v1/time-tracking/entries/running — caller's open timer, if any */
  running: () =>
    api.get<{ entry: TtEntry | null }>('/time-tracking/entries/running'),

  /** GET /api/v1/time-tracking/entries/:id */
  get: (id: string) =>
    api.get<{ entry: TtEntry }>(`/time-tracking/entries/${id}`),

  /** POST /api/v1/time-tracking/entries — create manual or timer entry */
  create: (data: TtCreateEntryBody) =>
    api.post<{ entry: TtEntry }>('/time-tracking/entries', data),

  /** PATCH /api/v1/time-tracking/entries/:id */
  update: (id: string, data: TtPatchEntryBody) =>
    api.patch<{ entry: TtEntry }>(`/time-tracking/entries/${id}`, data),

  /** DELETE /api/v1/time-tracking/entries/:id */
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/time-tracking/entries/${id}`),

  /** POST /api/v1/time-tracking/entries/:id/stop */
  stop: (id: string) =>
    api.post<{ entry: TtEntry }>(`/time-tracking/entries/${id}/stop`),

  /** POST /api/v1/time-tracking/entries/:id/duplicate — clones with start_at=NOW, end_at=null */
  duplicate: (id: string) =>
    api.post<{ entry: TtEntry }>(`/time-tracking/entries/${id}/duplicate`),

  /** POST /api/v1/time-tracking/entries/:id/split — splits at `at` ISO timestamp */
  split: (id: string, at: string) =>
    api.post<{ head: TtEntry; tail: TtEntry }>(`/time-tracking/entries/${id}/split`, { at }),
};

// ─── Time Tracking — Projects & Tasks ────────────────────────────────────────

export type TtEntityType = 'FNS' | 'NJN' | 'MES' | 'ALL';

export interface TtProject {
  id: string;
  workspace_id: string;
  client_id: string | null;
  name: string;
  color: string | null;
  billable_default: boolean;
  hourly_rate_cents: number | null;
  budget_hours: number | null;
  budget_dollars_cents: number | null;
  estimate_hours: number | null;
  parent_project_id: string | null;
  entity_type: TtEntityType | null;
  required_entry_fields: string[] | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface TtTask {
  id: string;
  project_id: string;
  name: string;
  estimate_hours: number | null;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface TtCreateProjectBody {
  name: string;
  client_id?: string | null;
  color?: string | null;
  billable_default?: boolean;
  hourly_rate_cents?: number | null;
  budget_hours?: number | null;
  budget_dollars_cents?: number | null;
  estimate_hours?: number | null;
  parent_project_id?: string | null;
  entity_type?: TtEntityType | null;
  required_entry_fields?: string[] | null;
}

export interface TtPatchProjectBody extends Partial<TtCreateProjectBody> {
  archived?: boolean;
}

export interface TtCreateTaskBody {
  name: string;
  estimate_hours?: number | null;
}

export interface TtPatchTaskBody {
  name?: string;
  estimate_hours?: number | null;
  completed?: boolean;
}

export const ttProjectsApi = {
  /** GET /api/v1/time-tracking/projects */
  list: (params?: { archived?: '0' | '1'; client_id?: string }) =>
    api.get<{ projects: TtProject[] }>('/time-tracking/projects', { params }),

  /** GET /api/v1/time-tracking/projects/:id */
  get: (id: string) =>
    api.get<{ project: TtProject }>(`/time-tracking/projects/${id}`),

  /** POST /api/v1/time-tracking/projects */
  create: (data: TtCreateProjectBody) =>
    api.post<{ project: TtProject }>('/time-tracking/projects', data),

  /** PATCH /api/v1/time-tracking/projects/:id */
  update: (id: string, data: TtPatchProjectBody) =>
    api.patch<{ project: TtProject }>(`/time-tracking/projects/${id}`, data),

  /** DELETE /api/v1/time-tracking/projects/:id — soft archive */
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/time-tracking/projects/${id}`),

  /** POST /api/v1/time-tracking/projects/:id/duplicate */
  duplicate: (id: string, body?: { name?: string; with_tasks?: boolean }) =>
    api.post<{ project: TtProject }>(`/time-tracking/projects/${id}/duplicate`, body ?? {}),

  // Tasks (nested under project)

  /** GET /api/v1/time-tracking/projects/:projectId/tasks */
  listTasks: (projectId: string) =>
    api.get<{ tasks: TtTask[] }>(`/time-tracking/projects/${projectId}/tasks`),

  /** POST /api/v1/time-tracking/projects/:projectId/tasks */
  createTask: (projectId: string, data: TtCreateTaskBody) =>
    api.post<{ task: TtTask }>(`/time-tracking/projects/${projectId}/tasks`, data),

  /** PATCH /api/v1/time-tracking/projects/:projectId/tasks/:taskId */
  updateTask: (projectId: string, taskId: string, data: TtPatchTaskBody) =>
    api.patch<{ task: TtTask }>(`/time-tracking/projects/${projectId}/tasks/${taskId}`, data),

  /** DELETE /api/v1/time-tracking/projects/:projectId/tasks/:taskId */
  deleteTask: (projectId: string, taskId: string) =>
    api.delete<{ success: boolean }>(`/time-tracking/projects/${projectId}/tasks/${taskId}`),
};

// ─── Time Tracking — Clients ─────────────────────────────────────────────────

export interface TtClient {
  id: string;
  workspace_id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  hourly_rate_cents: number | null;
  linked_ats_client_id: string | null;
  custom_fields_jsonb: Record<string, unknown>;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface TtCreateClientBody {
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  hourly_rate_cents?: number | null;
  linked_ats_client_id?: string | null;
  custom_fields_jsonb?: Record<string, unknown>;
}

export interface TtPatchClientBody extends Partial<TtCreateClientBody> {
  archived?: boolean;
}

export const ttClientsApi = {
  /** GET /api/v1/time-tracking/clients */
  list: (params?: { archived?: '0' | '1' }) =>
    api.get<{ clients: TtClient[] }>('/time-tracking/clients', { params }),

  /** GET /api/v1/time-tracking/clients/:id */
  get: (id: string) =>
    api.get<{ client: TtClient }>(`/time-tracking/clients/${id}`),

  /** POST /api/v1/time-tracking/clients */
  create: (data: TtCreateClientBody) =>
    api.post<{ client: TtClient }>('/time-tracking/clients', data),

  /** PATCH /api/v1/time-tracking/clients/:id */
  update: (id: string, data: TtPatchClientBody) =>
    api.patch<{ client: TtClient }>(`/time-tracking/clients/${id}`, data),

  /** DELETE /api/v1/time-tracking/clients/:id — soft archive */
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/time-tracking/clients/${id}`),
};

// ─── Time Tracking — Tags ─────────────────────────────────────────────────────

export interface TtTag {
  id: string;
  workspace_id: string;
  name: string;
  color: string | null;
  created_at: string;
  updated_at: string;
}

export interface TtCreateTagBody {
  name: string;
  color?: string | null;
}

export const ttTagsApi = {
  /** GET /api/v1/time-tracking/tags */
  list: () =>
    api.get<{ tags: TtTag[] }>('/time-tracking/tags'),

  /** POST /api/v1/time-tracking/tags */
  create: (data: TtCreateTagBody) =>
    api.post<{ tag: TtTag; duplicate?: boolean }>('/time-tracking/tags', data),

  /** PATCH /api/v1/time-tracking/tags/:id */
  update: (id: string, data: Partial<TtCreateTagBody>) =>
    api.patch<{ tag: TtTag }>(`/time-tracking/tags/${id}`, data),

  /** DELETE /api/v1/time-tracking/tags/:id */
  delete: (id: string) =>
    api.delete<{ success: boolean }>(`/time-tracking/tags/${id}`),
};

// ─── Assignment renewals ────────────────────────────────────────────────────
export interface PlacementRenewal {
  id: string;
  placement_id: string;
  end_date: string | null;
  status: 'pending' | 'notice_drafted' | 'notice_sent' | 'renewed' | 'declined' | 'dismissed';
  draft_subject: string | null;
  draft_body: string | null;
  client_response: string | null;
  notes: string | null;
  notified_at: string | null;
  staff_name: string | null;
  role: string | null;
  facility_name: string | null;
  facility_contact: string | null;
  facility_email: string | null;
  days_remaining: number | null;
  urgency: 'overdue' | 'urgent' | 'soon' | 'upcoming' | null;
}

export const placementRenewalsApi = {
  list: (status?: string) =>
    api.get<{ renewals: PlacementRenewal[] }>('/placement-renewals', { params: status ? { status } : undefined }),
  get: (id: string) => api.get<{ renewal: PlacementRenewal }>(`/placement-renewals/${id}`),
  draft: (placementId: string) =>
    api.post<{ renewal: PlacementRenewal; draft: { subject: string; body: string } }>(`/placement-renewals/${placementId}/draft`),
  update: (id: string, body: Partial<Pick<PlacementRenewal, 'status' | 'draft_subject' | 'draft_body' | 'client_response' | 'notes'>>) =>
    api.patch<{ renewal: PlacementRenewal }>(`/placement-renewals/${id}`, body),
};

// ─── CEU transcript ─────────────────────────────────────────────────────────
export interface CeuRecord {
  id: string;
  title: string;
  item_type: string;
  completed_date: string | null;
  ceus: number;
  expiration_date: string | null;
  certificate_number: string | null;
}
export interface CeuSummary {
  total_ceus: number;
  record_count: number;
  by_year: Array<{ year: string; ceus: number; count: number }>;
  records: CeuRecord[];
}
export const ceusApi = {
  me: (params?: { year?: string; from?: string; to?: string }) =>
    api.get<CeuSummary>('/ceus/me', { params }),
  forUser: (clerkId: string, params?: { year?: string }) =>
    api.get<CeuSummary>(`/ceus/users/${clerkId}`, { params }),
};

// ─── Candidate/Employee Portal ──────────────────────────────────────────────
export interface PortalMessage {
  id: string;
  sender: 'candidate' | 'staff';
  staff_name: string | null;
  body: string;
  created_at: string;
}
export interface PortalDoc {
  id: string; label: string; document_type: string; status: string; required: boolean;
  issue_date?: string | null;
  expiry_date?: string | null;
  candidate_note?: string | null;
  guidance?: string | null;
  expiry_flag?: 'expired' | 'expiring_soon' | null;
}
export interface PortalTask {
  id: string;
  title: string;
  details: string | null;
  status: 'open' | 'done';
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
}
export interface PortalInfo {
  candidate: { first_name: string; last_name: string; stage: string; status: string };
  documents: PortalDoc[];
  unread_messages: number;
  tasks: PortalTask[];
}
export interface PortalLinkStatus {
  expires_at: string | null;
  last_accessed_at: string | null;
  created_at: string;
}

// [phase6 track-E] Voluntary EEO/OFCCP self-identification (tokenized self-serve).
// Note: tracks D and F also touch this file — keep additions tagged + additive.
export interface EeoOption { value: string; label: string }
export interface EeoDimension { key: string; label: string; options: EeoOption[] }
export interface EeoSelfId {
  race_ethnicity: string;
  gender: string;
  veteran_status: string;
  disability_status: string;
}
export interface EeoFormResponse {
  dimensions: EeoDimension[];
  self_id: EeoSelfId;
  submitted_at: string | null;
  voluntary: boolean;
}

// Public (token-in-URL, no login) — used by the /portal/:token page.
export const candidatePortalPublicApi = {
  info: (token: string) => api.get<PortalInfo>(`/portal/${token}`),
  messages: (token: string) => api.get<{ messages: PortalMessage[] }>(`/portal/${token}/messages`),
  sendMessage: (token: string, body: string) => api.post<{ message: PortalMessage }>(`/portal/${token}/messages`, { body }),
  // Phase 2: upload a document from the portal (multipart/form-data).
  uploadDocument: (token: string, file: File, opts?: { document_id?: string; document_type?: string; label?: string }) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.document_id)   fd.append('document_id',   opts.document_id);
    if (opts?.document_type) fd.append('document_type', opts.document_type);
    if (opts?.label)         fd.append('label',         opts.label);
    return api.post<{ ok: boolean; document_id: string; filename: string; size: number }>(
      `/portal/${token}/upload`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  // Phase 2: list tasks.
  tasks: (token: string) => api.get<{ tasks: PortalTask[] }>(`/portal/${token}/tasks`),
  // Phase 2: mark a task complete.
  completeTask: (token: string, taskId: string) =>
    api.post<{ task: PortalTask }>(`/portal/${token}/tasks/${taskId}/complete`),
  // [phase6 track-E] Voluntary EEO/OFCCP self-identification (self-serve).
  eeo: (token: string) => api.get<EeoFormResponse>(`/portal/${token}/eeo`),
  submitEeo: (token: string, selfId: EeoSelfId) =>
    api.post<{ ok: boolean; self_id: EeoSelfId; voluntary: boolean }>(`/portal/${token}/eeo`, selfId),
};

// [phase4 track-E] Nurse Hub — one branded token page unifying profile, tasks,
// documents, messages, and e-sign forms. The hub token still resolves through
// the same portal endpoints, so message/task actions reuse the APIs above; only
// the unified payload, email-OTP, and gated upload are hub-specific.
export interface HubScopes { profile: boolean; tasks: boolean; messages: boolean; upload: boolean; esign: boolean }
export interface HubEsignDoc {
  title: string;
  status: 'signed' | 'awaiting_signature';
  signed_at: string | null;
  sign_token: string | null;
}
export interface HubPayload {
  candidate: { first_name: string; last_name: string; stage: string; status: string; email: string | null };
  scopes: HubScopes;
  email_verified: boolean;
  doc_section_unlocked: boolean;
  documents: PortalDoc[];
  tasks: PortalTask[];
  messages: PortalMessage[];
  unread_messages: number;
  esign_docs: HubEsignDoc[];
}
export const nurseHubApi = {
  hub: (token: string) => api.get<HubPayload>(`/portal/${token}/hub`),
  sendCode: (token: string) =>
    api.post<{ status: 'sent' | 'blocked' | 'suppressed'; reason?: string | null; already_verified?: boolean }>(
      `/portal/${token}/hub/send-code`),
  verifyCode: (token: string, code: string) =>
    api.post<{ verified: boolean }>(`/portal/${token}/hub/verify-code`, { code }),
  uploadDocument: (
    token: string, file: File,
    opts?: { document_id?: string; document_type?: string; label?: string;
             issue_date?: string; expiry_date?: string; candidate_note?: string },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.document_id)    fd.append('document_id',    opts.document_id);
    if (opts?.document_type)  fd.append('document_type',  opts.document_type);
    if (opts?.label)          fd.append('label',          opts.label);
    if (opts?.issue_date)     fd.append('issue_date',     opts.issue_date);
    if (opts?.expiry_date)    fd.append('expiry_date',    opts.expiry_date);
    if (opts?.candidate_note) fd.append('candidate_note', opts.candidate_note);
    return api.post<{ ok: boolean; document_id: string; filename: string; size: number }>(
      `/portal/${token}/hub/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Staff-facing (authed) — mint/revoke links + read/reply to candidate messages.
export const candidatePortalAdminApi = {
  getLink: (candidateId: string) => api.get<{ link: PortalLinkStatus | null }>(`/candidate-portal/${candidateId}/link`),
  mintLink: (candidateId: string) => api.post<{ url: string; expires_at: string }>(`/candidate-portal/${candidateId}/link`),
  revokeLink: (candidateId: string) => api.post(`/candidate-portal/${candidateId}/link/revoke`),
  messages: (candidateId: string) => api.get<{ messages: (PortalMessage & { read_by_candidate?: boolean })[] }>(`/candidate-portal/${candidateId}/messages`),
  sendMessage: (candidateId: string, body: string) => api.post<{ message: PortalMessage }>(`/candidate-portal/${candidateId}/messages`, { body }),
  // Phase 2: task management.
  listTasks: (candidateId: string) => api.get<{ tasks: PortalTask[] }>(`/candidate-portal/${candidateId}/tasks`),
  createTask: (candidateId: string, payload: { title: string; details?: string; due_date?: string }) =>
    api.post<{ task: PortalTask }>(`/candidate-portal/${candidateId}/tasks`, payload),
  deleteTask: (candidateId: string, taskId: string) =>
    api.delete<{ success: boolean }>(`/candidate-portal/${candidateId}/tasks/${taskId}`),
};

// ─── Company Certifications (FNS's own licenses/insurance/registrations) ─────
export interface CompanyCertification {
  id: string;
  name: string;
  category: 'license' | 'certification' | 'insurance' | 'registration' | 'permit' | 'tax' | 'other';
  issuing_authority: string | null;
  identifier: string | null;
  status: 'active' | 'pending' | 'in_review' | 'expired' | 'lapsed' | 'na';
  issue_date: string | null;
  expiration_date: string | null;
  renewal_url: string | null;
  contact_name: string | null;
  contact_email: string | null;
  responsible_user_id: string | null;
  reminder_days: number[];
  notes: string | null;
  days_until: number | null;
  urgency: 'overdue' | 'urgent' | 'soon' | 'upcoming' | 'ok';
}
export const companyCertsApi = {
  list: (params?: { status?: string; category?: string }) =>
    api.get<{ certifications: CompanyCertification[] }>('/company-certifications', { params }),
  get: (id: string) => api.get<{ certification: CompanyCertification }>(`/company-certifications/${id}`),
  create: (data: Partial<CompanyCertification>) =>
    api.post<{ certification: CompanyCertification }>('/company-certifications', data),
  update: (id: string, data: Partial<CompanyCertification>) =>
    api.patch<{ certification: CompanyCertification }>(`/company-certifications/${id}`, data),
  remove: (id: string) => api.delete(`/company-certifications/${id}`),
};

// ─── Daily CEO Briefing ─────────────────────────────────────────────────────
export interface CeoBriefing {
  generated_at: string;
  deadlines: Array<{ type: string; label: string; date: string; days_until: number }>;
  pipeline: {
    candidates_by_stage: Record<string, number>;
    open_jobs: number;
    sourced_new: number;
    callable_candidates: number;
    active_placements: number;
    placed_this_week: number;
  };
  needs_attention: {
    pending_assignment_renewals: number;
    unread_candidate_messages: number;
    overdue_company_certs: number;
    strong_fit_leads_to_review: number;
  };
  team: { eod_reports_yesterday: number; running_timers: number };
}
export const ceoBriefingApi = {
  get: () => api.get<CeoBriefing>('/ceo-briefing'),
};

// ─── Team Productivity Scorecard ────────────────────────────────────────────
export interface ScorecardMember {
  id: string;
  name: string;
  role: string;
  hours: number;
  candidates: number;
  tasksDone: number;
  eodDays: number;
  status: 'active' | 'low' | 'inactive';
  flags: string[];
  kpis: Record<string, number>;
}
export interface KpiTarget { metric_key: string; label: string; weekly_target: number; sort_order: number }
export const teamScorecardApi = {
  get: () => api.get<{ week_start: string; members: ScorecardMember[] }>('/team-scorecard'),
  targets: () => api.get<{ targets: KpiTarget[] }>('/team-scorecard/targets'),
  updateTarget: (key: string, weeklyTarget: number) =>
    api.put<{ target: KpiTarget }>(`/team-scorecard/targets/${key}`, { weekly_target: weeklyTarget }),
  suggestions: () =>
    api.get<{ source: 'ai' | 'rules'; suggestions: Array<{ name: string; suggestions: string[] }>; targets: KpiTarget[] }>('/team-scorecard/suggestions'),
};

// ─── Client Health & Renewal Radar ──────────────────────────────────────────
export interface ClientHealth {
  id: string;
  name: string;
  status: string;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  total_orders: number;
  open_orders: number;
  last_order_at: string | null;
  days_since_last_order: number | null;
  orders_90d: number;
  orders_prior_90d: number;
  active_placements: number;
  contract_renewal_date: string | null;
  renewal_days: number | null;
  next_qbr_date: string | null;
  qbr_days: number | null;
  risk: 'healthy' | 'watch' | 'at_risk' | 'dormant' | 'prospect' | 'churned';
  reasons: string[];
  order_trend_pct: number | null;
}
export const clientHealthApi = {
  list: () => api.get<{ clients: ClientHealth[] }>('/client-health'),
  updateDates: (id: string, body: { contract_renewal_date?: string | null; next_qbr_date?: string | null }) =>
    api.patch<{ client: { id: string; contract_renewal_date: string | null; next_qbr_date: string | null } }>(`/client-health/${id}`, body),
};

// ─── Security services (passive website check + readiness assessment) ───────
export interface SecurityFinding {
  key: string; title: string;
  status: 'pass' | 'warn' | 'fail' | 'skipped';
  severity: 'critical' | 'serious' | 'warning' | 'info' | 'ok';
  penalty: number; what: string; why: string; how: string;
}
export interface WebsiteReport {
  score: number; grade: string; summary: string; checkedUrl: string;
  findings: SecurityFinding[];
}
export interface ReadinessQuestion {
  id: string; area: string; weight: number; text: string; help?: string; critical?: boolean;
}
export interface ReadinessAreaScore { area: string; score: number; answered: number; maxWeight: number; }
export interface ReadinessActionItem { questionId: string; area: string; priority: number; critical: boolean; headline: string; }
export interface ReadinessResult {
  score: number; grade: string; answeredCount: number; naCount: number;
  areas: ReadinessAreaScore[]; actionPlan: ReadinessActionItem[];
}
export type ReadinessAnswer = 'yes' | 'partly' | 'no' | 'na';

export const securityApi = {
  websiteCheck: (url: string, opts?: { client_label?: string | null; save?: boolean }) =>
    api.post<{ report: WebsiteReport; saved_id: string | null }>('/security-tools/website-check', { url, ...opts }),
  websiteHistory: (client?: string) =>
    api.get<{ checks: Array<{ id: string; client_label: string | null; target_url: string; score: number; grade: string; created_at: string }> }>(`/security-tools/website-check/history${client ? `?client=${encodeURIComponent(client)}` : ''}`),
  readinessQuestions: () => api.get<{ questions: ReadinessQuestion[] }>('/security-tools/readiness/questions'),
  readinessScore: (answers: Record<string, ReadinessAnswer>) =>
    api.post<{ result: ReadinessResult }>('/security-tools/readiness/score', { answers }),
  saveReadiness: (body: { client_label: string; answers: Record<string, ReadinessAnswer>; assessment_date?: string; notes?: string | null }) =>
    api.post<{ id: string; result: ReadinessResult; delta: { previous_score: number; previous_date: string; change: number } | null }>('/security-tools/readiness', body),
  readinessHistory: (client?: string) =>
    api.get<{ assessments: Array<{ id: string; client_label: string; assessment_date: string; score: number; grade: string; created_at: string }> }>(`/security-tools/readiness/history${client ? `?client=${encodeURIComponent(client)}` : ''}`),
};

// ─── Reference checks ───────────────────────────────────────────────────────
export interface ReferenceRequest {
  id: string; referee_name: string; referee_email: string | null; relationship: string | null;
  company: string | null; status: string; reminder_count: number; last_reminder_at: string | null;
  overall_rating: number | null; would_rehire: string | null; ai_summary: string | null;
  ai_flags: string[] | null; submitted_at: string | null; created_at: string;
}
// ─── Employee renewals / time-off outreach ──────────────────────────────────
export interface EmployeeRenewal {
  id: string; status: string; contract_end_date: string | null;
  wants_renew: boolean | null; wants_time_off: boolean | null;
  time_off_dates: string | null; time_off_month: string | null;
  manager_email: string | null; manager_notified_at: string | null;
  reminder_count: number; sent_at: string | null; responded_at: string | null; created_at: string;
  staff_name: string; staff_email: string | null; facility_name: string | null;
}
export interface AttendancePolicy {
  title: string; intro: string;
  sections: Array<{ heading: string; points: string[] }>;
}
export const attendancePolicyApi = {
  get: () => api.get<{ policy: AttendancePolicy; time_and_half_holidays: string[]; video_url: string }>('/attendance-policy'),
};

export const employeeRenewalsApi = {
  list: () => api.get<{ renewals: EmployeeRenewal[] }>('/employee-renewals'),
  remind: (id: string) => api.post<{ emailed: boolean }>(`/employee-renewals/${id}/remind`),
  cancel: (id: string) => api.post<{ success: boolean }>(`/employee-renewals/${id}/cancel`),
};

export const referencesApi = {
  list: (candidateId: string) => api.get<{ references: ReferenceRequest[] }>(`/references/candidate/${candidateId}`),
  create: (body: { candidate_id: string; referee_name: string; referee_email?: string | null; referee_phone?: string | null; relationship?: string | null; company?: string | null }) =>
    api.post<{ id: string; emailed: boolean }>('/references', body),
  remind: (id: string) => api.post<{ emailed: boolean }>(`/references/${id}/remind`),
  cancel: (id: string) => api.post<{ success: boolean }>(`/references/${id}/cancel`),
};

// ─── Continuous compliance monitoring ───────────────────────────────────────
export type ComplianceStatus = 'green' | 'amber' | 'red' | 'unknown';
export interface CompliancePerson {
  candidate_id: string; name: string; role: string | null; recruiter: string | null;
  status: ComplianceStatus; expired: number; expiringSoon: number; valid: number;
  pending: number; total: number; nextExpiry: string | null; daysToNextExpiry: number | null;
  needsReattestation: boolean; reasons: string[];
}
export interface FleetSummary {
  people: number; green: number; amber: number; red: number; unknown: number;
  compliancePct: number; totalExpired: number; totalExpiringSoon: number; reattestationDue: number;
}
export interface CredentialEvent {
  event_type: string; detail: Record<string, unknown> | null; actor: string | null; created_at: string;
}
export const complianceMonitorApi = {
  monitor: () => api.get<{ summary: FleetSummary; people: CompliancePerson[] }>('/credentialing/monitor'),
  history: (candidateId: string) =>
    api.get<{ events: CredentialEvent[] }>(`/credentialing/monitor/${candidateId}/history`),
  reattest: (candidateId: string) =>
    api.post<{ success: boolean; credentials: number }>(`/credentialing/monitor/${candidateId}/reattest`),
};

// ─── Background checks (FCRA workflow) ──────────────────────────────────────
export type BgCheckStatus =
  | 'pending_consent' | 'consented' | 'ordered' | 'in_progress'
  | 'completed' | 'pre_adverse' | 'adverse' | 'cleared' | 'cancelled';
export type BgCheckResult = 'clear' | 'consider' | 'flagged' | null;
export interface BgNextAction {
  step: string; label: string; blocked: boolean; detail?: string;
}
export interface BackgroundCheck {
  id: string; candidate_id: string; package: string; status: BgCheckStatus;
  result: BgCheckResult; provider: string | null; provider_ref: string | null;
  report_url: string | null; consent_obtained_at: string | null;
  disclosure_version: string | null; authorization_method: string | null;
  adjudication: string | null; adjudication_notes: string | null;
  pre_adverse_sent_at: string | null; pre_adverse_wait_days: number;
  adverse_sent_at: string | null; ordered_at: string | null;
  completed_at: string | null; created_at: string;
  candidate_name?: string; next: BgNextAction;
}
export interface BgCheckEvent {
  event_type: string; detail: Record<string, unknown> | null; actor: string | null; created_at: string;
}
export const backgroundChecksApi = {
  listForCandidate: (candidateId: string) =>
    api.get<{ checks: BackgroundCheck[] }>(`/background-checks/candidate/${candidateId}`),
  fleet: (status?: string) =>
    api.get<{ checks: BackgroundCheck[] }>(`/background-checks${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  create: (body: { candidate_id: string; package?: string }) =>
    api.post<{ check: BackgroundCheck }>('/background-checks', body),
  consent: (id: string, body: { disclosure_version?: string; authorization_method?: string }) =>
    api.post<{ check: BackgroundCheck }>(`/background-checks/${id}/consent`, body),
  order: (id: string) =>
    api.post<{ check: BackgroundCheck; provider_mode: string }>(`/background-checks/${id}/order`),
  recordResult: (id: string, body: { result: 'clear' | 'consider' | 'flagged'; report_url?: string }) =>
    api.post<{ check: BackgroundCheck }>(`/background-checks/${id}/result`, body),
  preAdverse: (id: string, body?: { individualized_assessment?: Record<string, unknown>; wait_days?: number }) =>
    api.post<{ check: BackgroundCheck; emailed: boolean }>(`/background-checks/${id}/pre-adverse`, body ?? {}),
  adverse: (id: string) =>
    api.post<{ check: BackgroundCheck; emailed: boolean }>(`/background-checks/${id}/adverse`),
  clear: (id: string, body?: { notes?: string }) =>
    api.post<{ check: BackgroundCheck }>(`/background-checks/${id}/clear`, body ?? {}),
  cancel: (id: string) => api.post<{ check: BackgroundCheck }>(`/background-checks/${id}/cancel`),
  events: (id: string) => api.get<{ events: BgCheckEvent[] }>(`/background-checks/${id}/events`),
};

// ─── Primary-Source Verification (PSV) ──────────────────────────────────────
export type PsvResult = 'verified' | 'not_found' | 'discrepancy' | 'pending';
export type PsvStatus = 'verified' | 'discrepancy' | 'due' | 'unverified';
export interface PrimarySource {
  key: string; label: string; description: string;
  category: string; url?: string; automatable: boolean;
}
export interface CredentialVerification {
  id: string; credential_id: string; candidate_id: string; source: string;
  method: string | null; result: PsvResult; source_reference: string | null;
  evidence_url: string | null; verified_by: string | null; verified_at: string | null;
  next_recheck_at: string | null; automated: boolean; notes: string | null; created_at: string;
}
export interface CredentialWithPsv {
  id: string; type: string; issuer: string | null; state_or_jurisdiction: string | null;
  identifier: string | null; expiry_date: string | null; status: string; verified_at: string | null;
  psv: { status: PsvStatus; currentSources: string[]; hasDiscrepancy: boolean; dueSources: string[]; lastVerifiedAt: string | null };
  verifications: CredentialVerification[];
}
export interface PsvDueItem {
  verification_id: string; credential_id: string; candidate_id: string;
  candidate_name: string; credential_type: string; source: string;
  verified_at: string | null; next_recheck_at: string | null;
}
export const psvApi = {
  sources: () => api.get<{ sources: PrimarySource[]; automated: string[] }>('/psv/sources'),
  forCandidate: (candidateId: string) =>
    api.get<{ credentials: CredentialWithPsv[] }>(`/psv/candidate/${candidateId}`),
  verify: (credentialId: string, body: {
    source: string; method?: string; result?: PsvResult;
    source_reference?: string; evidence_url?: string; notes?: string;
  }) => api.post<{ verification: CredentialVerification }>(`/psv/credential/${credentialId}/verify`, body),
  due: () => api.get<{ due: PsvDueItem[] }>('/psv/due'),
};

// ─── E-Verify case tracker ──────────────────────────────────────────────────
export type EverifyStatus =
  | 'pending' | 'submitted' | 'authorized' | 'tnc' | 'tnc_referred'
  | 'final_nonconfirmation' | 'closed';
export interface EverifyCase {
  id: string; candidate_id: string; status: EverifyStatus; case_number: string | null;
  start_date: string | null; submit_deadline: string | null; submitted_at: string | null;
  tnc_notice_given_at: string | null; referred_at: string | null; closed_at: string | null;
  notes: string | null; created_at: string;
  urgency: 'ok' | 'due_soon' | 'overdue' | 'none';
  next: { step: string; label: string; blocked: boolean };
  everify_url: string;
  candidate_name?: string;
}
export const everifyApi = {
  fleet: () => api.get<{ cases: EverifyCase[] }>('/everify'),
  forCandidate: (candidateId: string) =>
    api.get<{ cases: EverifyCase[] }>(`/everify/candidate/${candidateId}`),
  create: (body: { candidate_id: string; start_date?: string; notes?: string }) =>
    api.post<{ case: EverifyCase }>('/everify', body),
  transition: (id: string, body: { to: EverifyStatus; case_number?: string; notes?: string }) =>
    api.post<{ case: EverifyCase }>(`/everify/${id}/transition`, body),
};

// ─── Onboarding packets ─────────────────────────────────────────────────────
export interface OnboardingPacket {
  id: string; name: string; facility_id: string | null; facility_match: string | null;
  days_before_start: number; email_subject: string; email_body: string; active: boolean;
  facility_name?: string | null; doc_count?: number; send_count?: number; created_at?: string;
}
export interface PacketDoc { id: string; admin_file_id: string; filename: string; size_bytes: number; sort_order: number }
export interface PacketSend {
  recipient_name: string | null; recipient_email: string; start_date: string | null;
  status: string; detail: string | null; sent_by: string | null; sent_at: string;
}
export interface PacketDueRecipient { candidateId?: string | null; staffId?: string | null; name: string; email: string; startDate: string | null }
export const onboardingPacketsApi = {
  list: () => api.get<{ packets: OnboardingPacket[] }>('/onboarding-packets'),
  detail: (id: string) =>
    api.get<{ packet: OnboardingPacket; docs: PacketDoc[]; sends: PacketSend[]; due: PacketDueRecipient[] }>(`/onboarding-packets/${id}`),
  create: (body: Partial<OnboardingPacket> & { name: string; email_subject: string; email_body: string }) =>
    api.post<{ packet: OnboardingPacket }>('/onboarding-packets', body),
  update: (id: string, body: Partial<OnboardingPacket>) =>
    api.put<{ packet: OnboardingPacket }>(`/onboarding-packets/${id}`, body),
  uploadDoc: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ success: boolean }>(`/onboarding-packets/${id}/docs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeDoc: (id: string, docId: string) => api.delete<{ success: boolean }>(`/onboarding-packets/${id}/docs/${docId}`),
  send: (id: string, body: { candidate_id?: string; staff_id?: string; email?: string; name?: string; start_date?: string }) =>
    api.post<{ success: boolean }>(`/onboarding-packets/${id}/send`, body),
};

// ─── Onboarding e-sign bundle (Bundle of Five + Housing Packet) ─────────────
export interface BundleDocTemplate {
  id: string; name: string; source_hash: string | null; uploaded_override?: boolean; uploaded_by?: string | null; updated_at: string;
}
export interface BundleDocStatus {
  key: string; template_name: string; description: string; federal: boolean; seeded: boolean;
  template: BundleDocTemplate | null;
}
export interface BundleSend {
  id: string; candidate_id: string | null; staff_id: string | null;
  recipient_email: string; recipient_name: string;
  kind: 'esign' | 'federal_attachment' | 'housing_packet' | 'training_videos'; doc_key: string;
  esign_document_id: string | null; start_date: string | null;
  status: 'sent' | 'failed' | 'returned' | 'cancelled'; detail: string | null;
  reminder_count: number; last_reminder_at: string | null; returned_at: string | null;
  sent_by: string | null; created_at: string;
}
export interface BundleSendResult {
  esign_sent: string[]; attachments_sent: string[]; housing_sent: boolean; training_videos_sent: boolean;
  skipped: string[]; errors: string[];
}
export interface JobDescriptionStatus {
  key: string; template_name: string; description: string; seeded: boolean;
  template: BundleDocTemplate | null;
}
export interface TrainingVideo { title: string; url: string }
export const onboardingBundleApi = {
  status: () => api.get<{
    docs: BundleDocStatus[]; housing_doc: BundleDocStatus; job_descriptions: JobDescriptionStatus[];
    training_videos: { key: string; videos: TrainingVideo[] };
    federal_esign_enabled: boolean; sends: BundleSend[]; housing_outstanding: BundleSend[];
  }>('/onboarding-bundle'),
  due: () => api.get<{ due: PacketDueRecipient[] }>('/onboarding-bundle/due'),
  send: (body: { candidate_id?: string; staff_id?: string; email?: string; name?: string; role?: string; include_housing?: boolean; include_training_videos?: boolean }) =>
    api.post<{ result: BundleSendResult }>('/onboarding-bundle/send', body),
  markReturned: (sendId: string) => api.post<{ send: BundleSend }>(`/onboarding-bundle/sends/${sendId}/mark-returned`, {}),
  remind: (sendId: string) => api.post<{ success: boolean }>(`/onboarding-bundle/sends/${sendId}/remind`, {}),
  uploadDoc: (key: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ success: boolean; template_id: string; source_hash: string; updated_at: string }>(
      `/onboarding-bundle/docs/${key}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  removeDoc: (key: string) => api.delete<{ success: boolean }>(`/onboarding-bundle/docs/${key}/upload`),
};

// ─── Laure (AI Chief of Staff) ──────────────────────────────────────────────
export interface LaureBriefing { data: unknown; html: string }
export const laureApi = {
  briefing: () => api.get<LaureBriefing>('/nancy/briefing'),
  briefingAudio: () => api.get<Blob>('/nancy/briefing/audio', { responseType: 'blob', timeout: 120000 }),
  sendReminders: () => api.post<{ success: boolean; note: string }>('/nancy/actions/send-reminders', {}),
  draftEmail: (body: { to?: string; recipient_name?: string; purpose: string }) =>
    api.post<{ draft: { to: string; subject: string; body: string }; source: 'ai' | 'template' }>('/nancy/actions/draft-email', body),
  sendEmail: (body: { to: string; subject: string; body: string }) =>
    api.post<{ success: boolean }>('/nancy/actions/send-email', body),
};

// ─── Board meeting room ─────────────────────────────────────────────────────
export interface BoardTake { agent_id: string; name: string; title: string; emoji: string; take: string }
export const boardRoomApi = {
  meet: (question: string, agentIds?: string[]) =>
    api.post<{ question: string; takes: BoardTake[]; synthesis: string }>('/ai-advisors/board-meeting', { question, agent_ids: agentIds }, { timeout: 180000 }),
  agents: () => api.get<{ agents: Array<{ id: string; name: string; title: string; emoji: string; shortBio: string }> }>('/ai-advisors/agents'),
};

// ─── G.I. Jane (SAM.gov) + Grace (grants pipeline) ──────────────────────────
export interface SamOpportunity {
  notice_id: string | null; title: string; solicitation_number: string | null; agency: string | null;
  type: string | null; naics: string | null; posted: string | null; deadline: string | null;
  place: string | null; url: string | null;
}
export interface GrantRow {
  id: string; name: string; funder: string | null; amount: string | null; deadline: string | null;
  status: 'researching' | 'preparing' | 'submitted' | 'awarded' | 'declined' | 'abandoned';
  url: string | null; notes: string | null; created_at: string; updated_at: string;
}
export interface GovBid {
  id: string; subject: string; from_name: string | null; from_email: string | null;
  received_at: string | null; body_preview: string | null; ai_summary: string | null;
  deadline: string | null;
  status: 'new' | 'reviewing' | 'preparing' | 'submitted' | 'won' | 'lost' | 'passed';
  notes: string | null; created_at: string;
}
export interface AgentDraft {
  id: string; agent: 'grace' | 'gi-jane'; kind: 'grant_application' | 'bid_response';
  related_type: string | null; related_id: string | null;
  title: string; content: string;
  status: 'draft_ready' | 'approved' | 'submitted' | 'rejected';
  review_notes: string | null; reviewed_at: string | null; submitted_at: string | null; created_at: string;
  grant_deadline: string | null; grant_url: string | null; bid_deadline: string | null;
}
export const govApi = {
  samSearch: (q: string, limit = 15) =>
    api.get<{ total: number; opportunities: SamOpportunity[] }>('/gov/sam/opportunities', { params: { q, limit } }),
  drafts: (all = false) =>
    api.get<{ drafts: AgentDraft[]; statuses: string[] }>('/gov/drafts', { params: all ? { all: 1 } : undefined }),
  updateDraft: (id: string, body: { status?: AgentDraft['status']; content?: string; review_notes?: string | null }) =>
    api.put<{ draft: AgentDraft }>(`/gov/drafts/${id}`, body),
  runAgentSweep: () =>
    api.post<{ sourcing: { searched: number; evaluated: number; added: number; skipped_low_fit: number; errors: string[] };
               drafting: { grant_drafts: number; bid_drafts: number; errors: string[] } }>('/gov/agent-sweep/run-now', {}, { timeout: 300000 }),
  bids: () => api.get<{ bids: GovBid[]; statuses: string[]; mailbox: string | null; folder: string }>('/gov/bids'),
  updateBid: (id: string, body: { status?: GovBid['status']; notes?: string | null; deadline?: string | null }) =>
    api.put<{ bid: GovBid }>(`/gov/bids/${id}`, body),
  scanBidsNow: () => api.post<{ mailbox: string | null; seen: number; imported: number; error?: string }>('/gov/bids/scan-now', {}, { timeout: 120000 }),
  grants: () => api.get<{ grants: GrantRow[]; statuses: string[] }>('/gov/grants'),
  addGrant: (body: Partial<GrantRow> & { name: string }) => api.post<{ grant: GrantRow }>('/gov/grants', body),
  updateGrant: (id: string, body: Partial<GrantRow>) => api.put<{ grant: GrantRow }>(`/gov/grants/${id}`, body),
  deleteGrant: (id: string) => api.delete(`/gov/grants/${id}`),
};

// ─── Agent knowledge base (Grace / G.I. Jane grounding docs) ────────────────
export interface AgentKnowledgeDoc {
  id: string; title: string; filename: string | null; mime: string | null;
  size_bytes: number | null; active: boolean; content_chars: number;
  preview: string; uploaded_by: string | null; created_at: string;
}
export const agentKnowledgeApi = {
  list: () => api.get<{ docs: AgentKnowledgeDoc[] }>('/agent-knowledge'),
  upload: (file: File, title?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    return api.post<{ doc: AgentKnowledgeDoc }>('/agent-knowledge', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  paste: (title: string, content: string) =>
    api.post<{ doc: AgentKnowledgeDoc }>('/agent-knowledge/paste', { title, content }),
  update: (id: string, body: { title?: string; active?: boolean }) =>
    api.put<{ doc: AgentKnowledgeDoc }>(`/agent-knowledge/${id}`, body),
  remove: (id: string) => api.delete(`/agent-knowledge/${id}`),
};

// ─── Phase 4 track-A — Unified conversations inbox (two-way SMS + email) ──
export interface ConversationSummary {
  id: string;
  candidate_id: string | null;
  channel: 'sms' | 'email';
  counterparty_phone: string | null;
  counterparty_email: string | null;
  subject: string | null;
  status: 'open' | 'snoozed' | 'closed';
  match_status: 'matched' | 'unmatched' | 'ambiguous';
  assigned_user_id: string | null;
  assigned_user_name: string | null;
  unread_count: number;
  last_message_at: string | null;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  last_body: string | null;
  last_direction: 'in' | 'out' | null;
}
export interface ConversationDetail extends ConversationSummary {
  candidate_email: string | null;
  candidate_phone: string | null;
  graph_conversation_id: string | null;
  updated_at: string;
}
export interface ConversationMessage {
  id: string;
  direction: 'in' | 'out';
  channel: 'sms' | 'email';
  body: string;
  from_addr: string | null;
  to_addr: string | null;
  status: string;
  scheduled_job_id: string | null;
  sent_by_user_id: string | null;
  sent_by_name: string | null;
  job_status: string | null;
  job_error: string | null;
  job_result?: { blocked?: boolean; status?: string; delivery?: string; reason?: string; skipped?: string } | null;
  created_at: string;
}
export interface ConversationCounts { total: string; unread: string; unmatched: string; mine: string }

export const conversationsApi = {
  list: (params?: { tab?: string; channel?: string; status?: string; q?: string; limit?: number; candidate_id?: string }) =>
    api.get<{ conversations: ConversationSummary[]; counts: ConversationCounts }>('/conversations', { params }),
  get: (id: string) => api.get<{ conversation: ConversationDetail }>(`/conversations/${id}`),
  messages: (id: string) => api.get<{ messages: ConversationMessage[] }>(`/conversations/${id}/messages`),
  reply: (id: string, body: string, subject?: string) =>
    api.post<{ message_id: string; scheduled_job_id: string; status: string }>(`/conversations/${id}/reply`, { body, subject }),
  assign: (id: string, assigned_user_id: string | null) =>
    api.post(`/conversations/${id}/assign`, { assigned_user_id }),
  close: (id: string, status?: 'open' | 'snoozed' | 'closed') =>
    api.post(`/conversations/${id}/close`, { status }),
  linkCandidate: (id: string, candidate_id?: string | null) =>
    api.post(`/conversations/${id}/link-candidate`, { candidate_id }),
};

// [phase6 track-F] Migration concierge — new-tenant import wizard.
// Self-contained block (own types + api object) so it merges cleanly alongside
// Track D's additions to this file. Backend routes are gated on tenant.import.
export type TenantImportEntity = 'candidate' | 'client' | 'placement';
export interface TenantImportParse {
  entity: TenantImportEntity;
  headers: string[];
  rows: Record<string, string>[];
  mapping: Record<string, string>;
  unmatched: string[];
  missing_required: string[];
  total_rows: number;
  filename: string;
}
export type TenantImportOutcome = 'insert' | 'duplicate' | 'invalid';
export interface TenantImportPreview {
  entity: TenantImportEntity;
  mapping: Record<string, string>;
  unmatched_columns: string[];
  missing_required: string[];
  total_rows: number;
  to_insert: number;
  to_skip_duplicate: number;
  invalid: number;
  invalid_rows: Array<{ row_number: number; reasons: string[] }>;
  sample: Array<{ row_number: number; outcome: TenantImportOutcome; reasons: string[]; data: Record<string, string> }>;
}
export interface TenantImportCommit {
  run_id: string;
  entity: TenantImportEntity;
  total_rows: number;
  inserted: number;
  skipped_duplicate: number;
  invalid: number;
  errors: Array<{ row_number: number; reason: string }>;
}
export interface TenantImportRun {
  id: string;
  entity: TenantImportEntity;
  filename: string | null;
  status: string;
  total_rows: number;
  inserted: number;
  skipped_duplicate: number;
  invalid: number;
  created_at: string;
  committed_at: string | null;
}
export const tenantImportApi = {
  entities: () => api.get<{ entities: TenantImportEntity[] }>('/tenant-import/entities'),
  parse: (entity: TenantImportEntity, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<TenantImportParse>(`/tenant-import/parse?entity=${entity}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  preview: (entity: TenantImportEntity, rows: Record<string, string>[], mapping: Record<string, string>, unmatched?: string[]) =>
    api.post<TenantImportPreview>(`/tenant-import/preview?entity=${entity}`, { rows, mapping, unmatched }),
  commit: (entity: TenantImportEntity, rows: Record<string, string>[], mapping: Record<string, string>, filename?: string) =>
    api.post<TenantImportCommit>(`/tenant-import/commit?entity=${entity}`, { rows, mapping, filename }),
  runs: () => api.get<{ runs: TenantImportRun[] }>('/tenant-import/runs'),
};

// [phase6 track-H] Access Transparency — a tenant's own admins/leadership read
// the log of every time platform staff (the operator/founder) touched THEIR
// data, plus a tamper-evident chain-verify. The backend enforces
// tenant.transparency.view and RLS-scopes every read to the caller's tenant, so
// a tenant can only ever see its own entries. Self-contained block; touches
// nothing above it (parallel tracks D/F append their own blocks here too).
export interface AccessTransparencyEntry {
  id: string;
  seq: number | string;
  tenant_id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_is_founder: boolean;
  action: string;
  route: string | null;
  method: string | null;
  reason: string | null;
  prev_hash: string;
  row_hash: string;
  created_at: string;
}
export interface AccessTransparencyVerify {
  intact: boolean;
  entries_verified: number;
  broken_at_index: number | null;
  broken_entry_id: string | null;
  detail: string | null;
}
export const accessTransparencyApi = {
  list: (params?: { limit?: number; offset?: number; action?: string }) =>
    api.get<{ entries: AccessTransparencyEntry[]; total: number; limit: number; offset: number }>(
      '/access-transparency',
      { params }
    ),
  verify: () => api.get<AccessTransparencyVerify>('/access-transparency/verify'),
};

export default api;

// ─── [phase6 track-D] Tenant-admin self-serve cockpit ───────────────────────
// Self-contained block (Track F also appends to this file — keep tags clear so
// both merge). Backs the /tenant-admin page: branding, seats, billing, flags.
// Every call is tenant-scoped server-side (a tenant admin only sees THEIR tenant).
export type EmailLogoAlign = 'left' | 'center';
export type EmailFontKey = 'system' | 'classic' | 'modern' | 'rounded';
export type EmailHeaderStyle = 'tint' | 'white';
export interface TenantBrandingFields {
  app_name?: string;
  company_name?: string;
  logo_url?: string;
  primary_color?: string;
  email_logo_width?: number;
  email_logo_align?: EmailLogoAlign;
  email_font?: EmailFontKey;
  email_header_style?: EmailHeaderStyle;
}
export interface TenantEffectiveBranding {
  app_name: string;
  company_name: string;
  tagline: string;
  logo_url: string | null;
  primary_color: string | null;
  email_display_name: string;
  support_email: string | null;
  terminology: Record<string, string>;
  email_logo_width?: number;
  email_logo_align?: EmailLogoAlign;
  email_font?: EmailFontKey;
  email_header_style?: EmailHeaderStyle;
}
export interface TenantBrandingResponse {
  tenant_id: string;
  tenant_name: string;
  branding: Record<string, unknown>;
  effective_branding: TenantEffectiveBranding;
}
export interface TenantSeatUser {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  mfa_enabled: boolean;
  created_at: string;
}
export interface TenantSeatsResponse {
  total: number;
  active_seats: number;
  pending: number;
  users: TenantSeatUser[];
}
export interface TenantBillingRecord {
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  subscription_status: string | null;
  current_period_end: string | null;
  updated_at: string | null;
}
export interface TenantBillingResponse {
  plan: string;
  tenant_status: string;
  tenant_since: string;
  billing: TenantBillingRecord | null;
}
export interface TenantFeatureFlagDef {
  key: string;
  label: string;
  description: string;
}
export interface TenantFeatureFlagsResponse {
  catalog: TenantFeatureFlagDef[];
  values: Record<string, boolean>;
}

export const tenantAdminApi = {
  getBranding: () => api.get<TenantBrandingResponse>('/tenant-admin/branding'),
  updateBranding: (data: TenantBrandingFields) =>
    api.put<{ branding: Record<string, unknown>; effective_branding: TenantEffectiveBranding }>(
      '/tenant-admin/branding', data
    ),
  getSeats: () => api.get<TenantSeatsResponse>('/tenant-admin/seats'),
  getBilling: () => api.get<TenantBillingResponse>('/tenant-admin/billing'),
  getFeatureFlags: () => api.get<TenantFeatureFlagsResponse>('/tenant-admin/feature-flags'),
  updateFeatureFlags: (flags: Record<string, boolean>) =>
    api.put<TenantFeatureFlagsResponse>('/tenant-admin/feature-flags', { flags }),
};

// ─── Outbound email (per-user Microsoft mailbox) ───────────────────────────

export interface MailConnectionStatus {
  configured: boolean;
  config_problem: string | null;
  connected: boolean;
  reconnect_required?: boolean;
  connection: {
    mailbox: string;
    display_name: string | null;
    status: 'connected' | 'expired' | 'revoked';
    scopes: string[];
    last_error: string | null;
    connected_at: string;
    last_used_at: string | null;
  } | null;
  allowed_senders: string[];
  shared_mailboxes: Array<{ id: string; mailbox: string; label: string | null }>;
  requested_scopes?: string[];
}

export interface MailRecipientResult {
  address: string;
  candidateId: string | null;
  status: 'sent' | 'failed' | 'blocked' | 'suppressed';
  errorCode?: string;
  errorMessage?: string;
  graphMessageId?: string | null;
  internetMessageId?: string | null;
  communicationId: string;
}

export interface MailSendResult {
  batchId: string;
  from: string;
  status: 'sent' | 'partial' | 'failed';
  sent: number;
  failed: number;
  results: MailRecipientResult[];
}

export interface CandidateCommunication {
  id: string;
  candidate_id?: string | null;
  batch_id: string | null;
  from_address: string;
  to_address: string;
  cc_addresses: string[];
  bcc_addresses: string[];
  subject: string | null;
  body_preview: string | null;
  status: 'pending' | 'sent' | 'failed' | 'blocked' | 'suppressed';
  error_code: string | null;
  error_message: string | null;
  graph_message_id: string | null;
  internet_message_id: string | null;
  has_attachments: boolean;
  attachment_names: string[];
  sent_by_name: string | null;
  created_at: string;
  sent_at: string | null;
}

export interface EmailTemplate {
  id: number;
  name: string;
  category: string | null;
  subject: string | null;
  content: string;
  variables: unknown;
}

export interface MailboxGrant {
  id: string;
  mailbox: string;
  label: string | null;
  created_at: string;
  user_id: string;
  user_name: string | null;
  user_email: string;
  granted_by_name: string | null;
}

export interface SendEmailPayload {
  from?: string | null;
  to: Array<{ address: string; candidate_id?: string | null; name?: string | null }>;
  cc?: string[];
  bcc?: string[];
  subject: string;
  body_html: string;
  attach_resume?: boolean;
  individual?: boolean;
  template_id?: number | null;
}

export const mailApi = {
  status: () => api.get<MailConnectionStatus>('/microsoft/status'),
  connect: (returnTo?: string) =>
    api.post<{ authorize_url: string }>('/microsoft/connect', { return_to: returnTo }),
  disconnect: () => api.delete<{ disconnected: boolean }>('/microsoft/connection'),

  listGrants: () => api.get<{ grants: MailboxGrant[] }>('/microsoft/grants'),
  grant: (body: { user_id: string; mailbox: string; label?: string }) =>
    api.post<{ granted: boolean; mailbox: string; note: string }>('/microsoft/grants', body),
  revokeGrant: (id: string) => api.delete<{ revoked: boolean }>(`/microsoft/grants/${id}`),

  templates: () => api.get<{ templates: EmailTemplate[] }>('/mail/templates'),

  // Render a subject+body through the branded email shell (no send) so the
  // composer can show recipients' exact view.
  preview: (body: { subject: string; body: string; show_unsubscribe?: boolean; branding?: Partial<TenantBrandingFields> }) =>
    api.post<{ html: string }>('/mail/preview', body),

  // Attachments ride along as multipart; everything else is one JSON field so
  // nested arrays survive the form encoding intact.
  send: (payload: SendEmailPayload, attachments: File[] = []) => {
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payload));
    for (const f of attachments) fd.append('attachments', f);
    return api.post<MailSendResult>('/mail/send', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // A partial batch returns 207 and a fully failed one 502; both carry a
      // usable body, so don't let axios throw them away.
      validateStatus: (s) => (s >= 200 && s < 300) || s === 207 || s === 502,
    });
  },

  candidateCommunications: (candidateId: string) =>
    api.get<{ communications: CandidateCommunication[] }>(`/candidates/${candidateId}/communications`),
  communications: (params?: { candidate_id?: string; status?: string; batch_id?: string; limit?: number }) =>
    api.get<{ communications: CandidateCommunication[]; total: number }>('/mail/communications', { params }),
};

// ─── Duplicate candidates + resume merge ───────────────────────────────────

export type MatchConfidence = 'high' | 'medium' | 'low';

export interface DuplicateMatch {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  alternate_email: string | null;
  phone: string | null;
  role: string | null;
  specialties: string[];
  stage: string | null;
  status: string | null;
  assigned_recruiter_name: string | null;
  resume_count: number;
  created_at: string;
  updated_at: string;
  confidence: MatchConfidence;
  reasons: string[];
}

export interface DuplicateCheckResult {
  duplicate_found: boolean;
  blocks_create: boolean;
  warning: string | null;
  matches: DuplicateMatch[];
  actions: string[];
}

export interface FieldDiff {
  field: string;
  label: string;
  existing: unknown;
  incoming: unknown;
  kind: 'new' | 'different';
}

export interface MergePreview {
  candidate: { id: string; first_name: string; last_name: string };
  parsed: Record<string, unknown> | null;
  /** Set when the resume could not be read. The file can still be attached. */
  parse_error?: string | null;
  field_changes: FieldDiff[];
  default_selection: string[];
  existing_resumes: Array<{ id: string; filename: string; uploaded_at: string; is_primary: boolean }>;
  new_resume: { filename: string; size: number };
}

export interface MergeResult {
  candidateId: string;
  action: string;
  resumeFileId: string | null;
  appliedFields: Array<{ field: string; from: unknown; to: unknown }>;
  primaryResumeId: string | null;
  resumeCount: number;
  mergeLogId: string;
  preserved: string;
}

export type MergeAction = 'merge_resume' | 'replace_resume' | 'keep_both' | 'update_missing';

export const duplicatesApi = {
  check: (body: {
    first_name?: string; last_name?: string; email?: string;
    alternate_email?: string; phone?: string; exclude_id?: string;
  }) => api.post<DuplicateCheckResult>('/candidate-duplicates/check', body),

  previewMerge: (candidateId: string, resume: File, action: MergeAction) => {
    const fd = new FormData();
    fd.append('resume', resume);
    fd.append('action', action);
    return api.post<MergePreview>(`/candidate-duplicates/${candidateId}/preview-merge`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  merge: (candidateId: string, body: {
    action: MergeAction;
    resume?: File | null;
    apply_fields?: string[];
    make_primary?: boolean;
    match_confidence?: MatchConfidence | null;
    match_reasons?: string[];
    notes?: string;
  }) => {
    const fd = new FormData();
    fd.append('action', body.action);
    if (body.resume) fd.append('resume', body.resume);
    fd.append('apply_fields', JSON.stringify(body.apply_fields ?? []));
    if (body.make_primary !== undefined) fd.append('make_primary', String(body.make_primary));
    if (body.match_confidence) fd.append('match_confidence', body.match_confidence);
    fd.append('match_reasons', JSON.stringify(body.match_reasons ?? []));
    if (body.notes) fd.append('notes', body.notes);
    return api.post<MergeResult>(`/candidate-duplicates/${candidateId}/merge`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  resolve: (candidateId: string, body: {
    action: 'created_separate' | 'cancelled';
    match_confidence?: MatchConfidence | null;
    match_reasons?: string[];
    notes?: string;
  }) => api.post<{ recorded: boolean }>(`/candidate-duplicates/${candidateId}/resolve`, body),

  resumes: (candidateId: string) =>
    api.get<{ resumes: Array<{ id: string; filename: string; mime: string; size_bytes: number; uploaded_at: string; is_primary: boolean; source_note: string | null }> }>(
      `/candidate-duplicates/${candidateId}/resumes`
    ),

  setPrimaryResume: (candidateId: string, resumeId: string) =>
    api.put<{ primary_resume_id: string }>(`/candidate-duplicates/${candidateId}/resumes/${resumeId}/primary`),

  mergeHistory: (candidateId: string) =>
    api.get<{ merges: Array<{ id: string; action: string; match_confidence: string | null; match_reasons: string[]; field_changes: Record<string, unknown>; performed_by_name: string | null; notes: string | null; created_at: string }> }>(
      `/candidate-duplicates/${candidateId}/merge-history`
    ),
};
